import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { API_BASE_URL, WS_BASE_URL } from './config';
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  applyEdgeChanges,
  applyNodeChanges,
  useReactFlow,
  // §4.79 Multi-Agent Orchestration Studio — custom edge primitives
  BaseEdge,
  getBezierPath,
  EdgeLabelRenderer,
} from 'reactflow';
import 'reactflow/dist/style.css';
import axios from 'axios';
import Sidebar from './Sidebar';
import NodeConfigPanel from './NodeConfigPanel';
import WorkflowHistory from './WorkflowHistory';
import WorkflowMetrics from './WorkflowMetrics';
import WorkflowVersions from './WorkflowVersions';
import VariableInspector from './VariableInspector';
import WorkflowEnvVars from './WorkflowEnvVars';
import AdminPanel from './AdminPanel';
import Profile from './Profile';
import CredentialManager from './CredentialManager';
import WorkspaceManager from './WorkspaceManager';
import Login from './Login';
import LandingPage, { PrivacyPolicyPage, TermsOfServicePage } from './LandingPage';
import FormsLandingPage from './FormsLandingPage';
import Docs from './Docs';
import AgentChat from './AgentChat';
import AuditLogViewer from './AuditLogViewer';
import MonitoringDashboard from './MonitoringDashboard';
import GlobalVariables from './GlobalVariables';
import DataStore from './DataStore';
import Tables from './Tables';
import logo from './assets/logo.svg';
import JsonEditor from './components/JsonEditor';
import CopilotModal from './components/CopilotModal';
import WorkflowTemplatesModal from './WorkflowTemplatesModal';
import ZapierImportModal from './ZapierImportModal';
import NotificationCenter from './NotificationCenter';
import SchedulerCalendar from './SchedulerCalendar';
import PluginGovernance from './PluginGovernance';
import WebhookDashboard from './WebhookDashboard';
import CustomRouteManager from './CustomRouteManager';
import MarketplaceModal from './MarketplaceModal';
import StepView from './components/StepView';
import FlowCopilotPanel from './components/FlowCopilotPanel';
// §4.79 Multi-Agent Orchestration Studio
import ReasoningStreamPanel from './components/ReasoningStreamPanel';
import AgentGraphMemoryPanel from './components/AgentGraphMemoryPanel';
import MobileRunMonitor from './MobileRunMonitor';
import XavvyForms from './XavvyForms';
import XavvyFormDesigner from './XavvyFormDesigner';
import XavvyFormRenderer from './XavvyFormRenderer';
import LicenseManager from './LicenseManager';
import CaseManager from './CaseManager';
import CaseTypeAdmin from './CaseTypeAdmin';
import CaseClientBootstrap from './CaseClient';
import Reports from './Reports';
import Docuforge from './Docuforge';
import ProcessIntel from './ProcessIntel';
import Rules from './Rules';
import useOAuthConnect from './hooks/useOAuthConnect';
import { formatLocalDate, formatLocalDateTime } from './timezone';


const initialNodes = [];

let id = 1;
const getId = () => `${id++}`;
const syncNextId = (nodeList) => {
  const maxId = nodeList.reduce((max, node) => {
    const parsed = parseInt(node.id, 10);
    if (Number.isNaN(parsed)) {
      return max;
    }
    return Math.max(max, parsed);
  }, 0);
  id = Math.max(id, maxId + 1);
};

const normalizeWorkflowDefinitionForCanvas = (definition) => {
  const rawNodes = Array.isArray(definition?.nodes) ? definition.nodes : [];
  const normalizedNodes = rawNodes
    .filter((node) => node && typeof node === 'object')
    .map((node, index) => {
      const nodeId = node.id != null ? String(node.id) : `node_${index + 1}`;
      const nodeType = typeof node?.data?.type === 'string' ? node.data.type : (typeof node.type === 'string' ? node.type : 'debug');
      const hasValidPosition = Number.isFinite(node?.position?.x) && Number.isFinite(node?.position?.y);
      const fallbackX = 180 + ((index % 4) * 260);
      const fallbackY = 80 + (Math.floor(index / 4) * 140);
      // Canvas-only node types preserve their ReactFlow type; all others use 'icon'
      const CANVAS_TYPES = ['sticky_note', 'group'];
      const rfType = CANVAS_TYPES.includes(nodeType) ? nodeType : 'icon';

      return {
        ...node,
        id: nodeId,
        type: rfType,
        position: hasValidPosition ? node.position : { x: fallbackX, y: fallbackY },
        data: {
          ...(node.data && typeof node.data === 'object' ? node.data : {}),
          type: nodeType,
          label: node?.data?.label || node.label || nodeType || nodeId,
          params: node?.data?.params && typeof node.data.params === 'object' ? node.data.params : {}
        }
      };
    });

  // Restore collapse state: hide children of collapsed groups
  const collapsedGroupIds = new Set(
    normalizedNodes.filter(n => n.type === 'group' && n.data?.collapsed).map(n => n.id)
  );
  if (collapsedGroupIds.size > 0) {
    for (const node of normalizedNodes) {
      if (node.parentId && collapsedGroupIds.has(node.parentId)) {
        node.hidden = true;
      }
    }
  }

  const nodeIds = new Set(normalizedNodes.map((node) => node.id));
  const rawEdges = Array.isArray(definition?.edges) ? definition.edges : [];
  const normalizedEdges = rawEdges
    .filter((edge) => edge && typeof edge === 'object')
    .map((edge, index) => {
      const source = edge.source != null ? String(edge.source) : '';
      const target = edge.target != null ? String(edge.target) : '';
      const edge_ = {
        ...edge,
        id: edge.id != null ? String(edge.id) : `edge_${source}_${target}_${index + 1}`,
        source,
        target
      };
      // Migrate legacy edges that have condition data / labels but no explicit type
      if (!edge_.type && (edge_.data?.condition || edge_.data?.edge_type === 'tool' || edge_.label)) {
        edge_.type = 'condition';
      }
      // Hide edges connected to nodes inside collapsed groups
      if (
        collapsedGroupIds.size > 0 &&
        normalizedNodes.some(n => n.id === source && n.hidden) ||
        normalizedNodes.some(n => n.id === target && n.hidden)
      ) {
        edge_.hidden = true;
      }
      return edge_;
    })
    .filter((edge) => nodeIds.has(edge.source) && nodeIds.has(edge.target));

  return { nodes: normalizedNodes, edges: normalizedEdges };
};

const buildWorkflowSnapshot = (name, nodes, edges, cronSchedule, isPaused, maxConcurrentRuns) => {
  const normalizeNode = (node) => {
    if (!node || typeof node !== 'object') return node;
    const {
      selected,
      dragging,
      positionAbsolute,
      measured,
      width,
      height,
      ...rest
    } = node;
    return rest;
  };
  const normalizeEdge = (edge) => {
    if (!edge || typeof edge !== 'object') return edge;
    const { selected, ...rest } = edge;
    return rest;
  };

  return JSON.stringify({
    name: name || '',
    cron_schedule: cronSchedule || '',
    is_paused: Boolean(isPaused),
    max_concurrent_runs: Number(maxConcurrentRuns) || 3,
    nodes: (nodes || []).map(normalizeNode),
    edges: (edges || []).map(normalizeEdge)
  });
};

const normalizeHumanApprovalOptions = (rawOptions) => {
  let options = [];
  if (Array.isArray(rawOptions)) {
    options = rawOptions;
  } else if (typeof rawOptions === 'string') {
    const trimmed = rawOptions.trim();
    if (!trimmed) {
      options = [];
    } else {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          options = parsed;
        } else {
          options = trimmed.split(',');
        }
      } catch {
        options = trimmed.split(',');
      }
    }
  }

  const normalized = [];
  const seen = new Set();
  options.forEach((option) => {
    const key = String(option || '').trim().toLowerCase();
    if (!key || seen.has(key)) {
      return;
    }
    seen.add(key);
    normalized.push(key);
  });

  if (normalized.length === 0) {
    return ['approve', 'reject', 'cancel'];
  }
  return normalized;
};

const humanApprovalOptionColor = (option) => {
  if (option === 'approve') {
    return 'var(--success-color)';
  }
  if (option === 'reject') {
    return 'var(--error-color)';
  }
  if (option === 'cancel') {
    return 'var(--text-secondary)';
  }
  return 'var(--accent-color)';
};

const iconByType = {
  debug: '🐞',
  openai_agent: '🤖',
  gemini_agent: '✨',
  http_request: '🌐',
  edit_fields: '✏️',
  webhook: '🔗',
  file_to_base64: '📄',
  webhook_response: '📤',
  if: '❓',
  switch: '🔀',
  file_type_detector: '🔍',
  qr_code_generator: '🔳',
  currency_converter: '💹',
  holiday_checker: '🎉',
  weather: '🌤️',
  ip_geolocation: '📍',
  url_shortener: '🔗',
  postgres_query: '🐘',
  mysql_query: '🐬',
  sql_server_query: '🗄️',
  aws_s3: '🪣',
  azure_blob_storage: '🫙',
  google_cloud_storage: '☁️',
  google_sheets: '📊',
  google_docs: '📝',
  excel: '📗',
  send_mail: '📧',
  loop: '🔄',
  try_catch: '🛡',
  wait: '⏳',
  merge: '⛙',
  vector_store: '📚',
  embeddings: '🧠',
  jira: '🎫',
  gmail: '📨',
  outlook: '📧',
  teams: '💬',
  slack: '#️⃣',
  code: '💻',
  chat_trigger: '💬',
  gmail_trigger: '📨',
  outlook_trigger: '📧',
  human_approval: '✅',
  form_input: '📝',
  form_trigger: '📋',
  google_sheets_trigger: '📊',
  airtable_trigger: '🗂',
  github_trigger: '🐙',
  slack_trigger: '💬',
  jira_trigger: '🎯',
  notion_trigger: '📓',
  rss_feed_trigger: '📡',
};

const triggerNodeIconMap = {
  cron: '⏰',
  webhook: '🔗',
  chat_trigger: '💬',
  gmail_trigger: '📨',
  outlook_trigger: '📧',
  form_trigger: '📋',
};

const nodeContainerStyle = {
  background: 'var(--panel)',
  border: '1px solid var(--border)',
  borderRadius: '8px',
  padding: '9px 12px',
  color: 'var(--text)',
  minWidth: '160px',
  textAlign: 'center',
  boxShadow: 'var(--elev-1)',
  fontSize: '12px',
};

const iconStyle = {
  fontSize: '20px',
  lineHeight: '1',
  marginBottom: '4px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '20px',
  width: '100%',
  background: 'transparent'
};

function IconNode({ data }) {
  // console.log('IconNode data:', data);
  const icon = data?.icon || iconByType[data?.type];
  
  if (!icon) {
      console.warn("Missing icon for type:", data?.type);
  }

  const displayIcon = icon || '❓';

  const status = data?.status;
  let borderColor = 'var(--border)';
  let boxShadow = 'var(--elev-1)';
  let animation = 'none';

  if (status === 'running') {
      borderColor = 'var(--warning)';
      animation = 'xavvyNodeRunningPulse 1.1s ease-in-out infinite';
  } else if (status === 'completed') {
      borderColor = 'var(--success)';
  } else if (status === 'failed') {
      borderColor = 'var(--error)';
  }
  const borderWidth = '1px';

  const style = {
      ...nodeContainerStyle,
      border: `${borderWidth} solid ${borderColor}`,
      boxShadow: boxShadow,
      position: 'relative',
      animation
  };

  const compactNodeStyle = {
      ...style,
      minWidth: '140px',
      textAlign: 'left',
      padding: '8px 10px'
  };

  const compactTitle = {
      marginBottom: '6px',
      fontWeight: 'bold',
      fontSize: '12px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
  };

  const compactList = {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      fontSize: '11px'
  };

  if (data?.type === 'switch') {
      return (
        <div style={{ ...compactNodeStyle }}>
             {data?.pinnedData && <div style={{ position: 'absolute', top: -7, right: -7, fontSize: '13px', lineHeight: 1, zIndex: 10, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))' }} title="Test data pinned — node skipped during execution">📌</div>}
             <Handle type="target" position={Position.Top} style={{ left: '50%' }} />
             <div style={{ ...iconStyle, justifyContent: 'flex-start' }} title={data?.type}>{displayIcon}</div>
             <div style={compactTitle}>{data?.label || 'Switch'}</div>
             <Handle type="source" position={Position.Right} />
        </div>
      );
  }

  if (data?.type === 'loop') {
      return (
        <div style={{ ...compactNodeStyle }}>
             {data?.pinnedData && <div style={{ position: 'absolute', top: -7, right: -7, fontSize: '13px', lineHeight: 1, zIndex: 10, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))' }} title="Test data pinned — node skipped during execution">📌</div>}
             <Handle type="target" position={Position.Top} style={{ left: '50%' }} />
             <div style={{ ...iconStyle, justifyContent: 'flex-start' }} title={data?.type}>{displayIcon}</div>
             <div style={compactTitle}>{data?.label || 'Loop'}</div>
             <Handle type="source" position={Position.Right} />
        </div>
      );
  }

  if (data?.type === 'try_catch') {
      return (
        <div style={{ ...compactNodeStyle }}>
             {data?.pinnedData && <div style={{ position: 'absolute', top: -7, right: -7, fontSize: '13px', lineHeight: 1, zIndex: 10, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))' }} title="Test data pinned — node skipped during execution">📌</div>}
             <Handle type="target" position={Position.Top} style={{ left: '50%' }} />
             <div style={{ ...iconStyle, justifyContent: 'flex-start' }} title={data?.type}>{displayIcon}</div>
             <div style={compactTitle}>{data?.label || 'Try / Catch'}</div>
             <Handle type="source" position={Position.Right} />
        </div>
      );
  }

  if (data?.type === 'if') {
      return (
        <div style={{ ...compactNodeStyle }}>
             {data?.pinnedData && <div style={{ position: 'absolute', top: -7, right: -7, fontSize: '13px', lineHeight: 1, zIndex: 10, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))' }} title="Test data pinned — node skipped during execution">📌</div>}
             <Handle type="target" position={Position.Top} style={{ left: '50%' }} />
             <div style={{ ...iconStyle, justifyContent: 'flex-start' }} title={data?.type}>{displayIcon}</div>
             <div style={compactTitle}>{data?.label || 'If / Else'}</div>
             <Handle type="source" position={Position.Right} />
        </div>
      );
  }

  if (data?.type === 'human_approval') {
      return (
        <div style={{ ...compactNodeStyle }}>
             {data?.pinnedData && <div style={{ position: 'absolute', top: -7, right: -7, fontSize: '13px', lineHeight: 1, zIndex: 10, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))' }} title="Test data pinned — node skipped during execution">📌</div>}
             <Handle type="target" position={Position.Top} style={{ left: '50%' }} />
             <div style={{ ...iconStyle, justifyContent: 'flex-start' }} title={data?.type}>{displayIcon}</div>
             <div style={compactTitle}>{data?.label || 'Human Approval'}</div>
             <Handle type="source" position={Position.Right} />
        </div>
      );
  }

  if (data?.type === 'ai_agent') {
    const toolConns = data?.params?.tool_connections || [];
    const visibleToolConns = toolConns.slice(0, 3);
    const hiddenToolCount = Math.max(0, toolConns.length - visibleToolConns.length);
    return (
      <div style={{ ...style, width: '240px', minWidth: '240px', maxWidth: '240px' }}>
        {data?.pinnedData && <div style={{ position: 'absolute', top: -7, right: -7, fontSize: '13px', lineHeight: 1, zIndex: 10, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))' }} title="Test data pinned — node skipped during execution">📌</div>}
        <Handle type="target" position={Position.Top} />
        <div style={{ ...iconStyle }} title="AI Agent">{displayIcon}</div>
        <div style={{ fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }} title={data?.label || 'AI Agent'}>{data?.label || 'AI Agent'}</div>
        {toolConns.length > 0 && (
          <div style={{ marginTop: '5px', display: 'flex', flexWrap: 'wrap', gap: '3px', justifyContent: 'center' }}>
            {visibleToolConns.map((tc, i) => (
              <div key={i} style={{ fontSize: '9px', background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.35)', color: '#c084fc', borderRadius: '10px', padding: '1px 6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100px' }} title={tc.description || tc.tool_name}>
                ⚙ {tc.tool_name}
              </div>
            ))}
            {hiddenToolCount > 0 && (
              <div style={{ fontSize: '9px', background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.35)', color: '#c084fc', borderRadius: '10px', padding: '1px 6px', whiteSpace: 'nowrap' }} title={`${hiddenToolCount} more connected tools`}>
                +{hiddenToolCount} more
              </div>
            )}
          </div>
        )}
        <Handle type="source" position={Position.Bottom} />
        {/* Tools handle — drag FROM here to any node to connect it as a tool */}
        <Handle
          type="source"
          id="tools"
          position={Position.Right}
          style={{ background: '#a855f7', width: 10, height: 10, border: '2px solid #7c3aed', right: -5 }}
          title="Drag to a node to connect it as an AI tool"
        />
        <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: '8px', color: '#a855f7', pointerEvents: 'none', userSelect: 'none', fontWeight: 700, letterSpacing: '0.03em', writingMode: 'vertical-rl' }}>TOOLS</div>
      </div>
    );
  }

  return (
    <div style={style}>
      {data?.pinnedData && <div style={{ position: 'absolute', top: -7, right: -7, fontSize: '13px', lineHeight: 1, zIndex: 10, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))' }} title="Test data pinned — node skipped during execution">📌</div>}
      {['google_sheets_trigger','airtable_trigger','github_trigger','slack_trigger','jira_trigger','notion_trigger','rss_feed_trigger'].includes(data?.type) && data?.params?.polling_interval_minutes && (
        <div style={{ position: 'absolute', top: -9, left: '50%', transform: 'translateX(-50%)', fontSize: '10px', background: 'var(--bg-secondary)', color: 'var(--primary-color)', border: '1px solid var(--primary-color)', borderRadius: '8px', padding: '1px 6px', whiteSpace: 'nowrap', zIndex: 10, lineHeight: '14px' }}>⏱ {data.params.polling_interval_minutes} min</div>
      )}
      {/* §4.83 Credential Re-auth: show reconnect badge on failed nodes with expired credentials */}
      {data?.credential_reauth && status === 'failed' && (
        <div style={{ position: 'absolute', bottom: -22, left: '50%', transform: 'translateX(-50%)', fontSize: '10px', background: 'var(--error-color)', color: 'white', borderRadius: '8px', padding: '2px 8px', whiteSpace: 'nowrap', zIndex: 10, lineHeight: '14px', fontWeight: 600, cursor: 'default' }} title={`Credential "${data.credential_reauth.credential_name || data.credential_reauth.service_type}" needs re-auth. Click the run error panel to reconnect.`}>
          Reconnect
        </div>
      )}
      {data?.type !== 'webhook' && !(data?.type || '').endsWith('_trigger') && <Handle type="target" position={Position.Top} />}
      <div style={iconStyle} title={data?.type}>{displayIcon}</div>
      <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>
        {data?.label || data?.type || 'Node'}
      </div>
      {(() => {
        const paramCount = Object.values(data?.params || {}).filter(v => v !== null && v !== undefined && v !== '').length;
        if (paramCount === 0) return null;
        return (
          <div style={{ fontSize: '10px', color: 'var(--dim)', marginTop: '4px', lineHeight: 1 }}>
            {paramCount} param{paramCount !== 1 ? 's' : ''} set
          </div>
        );
      })()}
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
}

const STICKY_COLORS = {
  yellow: { bg: '#fef9c3', border: '#fbbf24', text: '#78350f' },
  blue:   { bg: '#dbeafe', border: '#60a5fa', text: '#1e3a5f' },
  green:  { bg: '#dcfce7', border: '#4ade80', text: '#14532d' },
  pink:   { bg: '#fce7f3', border: '#f472b6', text: '#831843' },
};

function StickyNoteNode({ id, data }) {
  const { setNodes } = useReactFlow();
  const updateData = (patch) =>
    setNodes(nds => nds.map(n => n.id === id ? { ...n, data: { ...n.data, ...patch } } : n));
  const deleteNode = () => setNodes(nds => nds.filter(n => n.id !== id));
  const c = STICKY_COLORS[data?.color || 'yellow'];

  return (
    <div style={{
      background: c.bg,
      border: `2px solid ${c.border}`,
      borderRadius: '6px',
      padding: '8px',
      width: '190px',
      minHeight: '120px',
      boxShadow: '3px 3px 10px rgba(0,0,0,0.18)',
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    }}>
      {/* Color picker row */}
      <div style={{ display: 'flex', gap: '5px', flexShrink: 0, alignItems: 'center' }}>
        {Object.entries(STICKY_COLORS).map(([key, s]) => (
          <div
            key={key}
            title={key}
            onClick={() => updateData({ color: key })}
            style={{
              width: '11px', height: '11px', borderRadius: '50%',
              background: s.border, cursor: 'pointer', flexShrink: 0,
              border: (data?.color || 'yellow') === key ? '2px solid rgba(0,0,0,0.4)' : '1px solid transparent',
            }}
          />
        ))}
        <div
          title="Delete"
          onClick={deleteNode}
          className="nodrag"
          style={{
            marginLeft: 'auto', cursor: 'pointer', fontSize: '12px',
            color: c.text, opacity: 0.5, lineHeight: 1,
            padding: '0 2px',
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = '1'}
          onMouseLeave={e => e.currentTarget.style.opacity = '0.5'}
        >✕</div>
      </div>
      {/* Editable text */}
      <textarea
        className="nodrag"
        value={data?.text || ''}
        onChange={e => updateData({ text: e.target.value })}
        placeholder="Add a note…"
        style={{
          flex: 1,
          background: 'transparent',
          border: 'none',
          resize: 'none',
          fontSize: '13px',
          color: c.text,
          fontFamily: 'inherit',
          outline: 'none',
          lineHeight: '1.5',
          minHeight: '80px',
          cursor: 'text',
        }}
      />
    </div>
  );
}

function GroupNode({ id, data }) {
  const { setNodes, setEdges, getNodes } = useReactFlow();
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelDraft, setLabelDraft] = useState(data?.label || 'Group');
  const collapsed = data?.collapsed || false;

  const toggleCollapse = () => {
    const next = !collapsed;
    const childIds = getNodes().filter(n => n.parentId === id).map(n => n.id);
    setNodes(nds => nds.map(n => {
      if (n.id === id) {
        const h = next ? 36 : (data?.expandedHeight || 160);
        const w = data?.expandedWidth || (n.style?.width || 300);
        return { ...n, data: { ...n.data, collapsed: next }, style: { ...n.style, height: h, width: w } };
      }
      if (n.parentId === id) return { ...n, hidden: next };
      return n;
    }));
    setEdges(eds => eds.map(e => {
      const inside = childIds.includes(e.source) || childIds.includes(e.target);
      return inside ? { ...e, hidden: next } : e;
    }));
  };

  const finishLabel = () => {
    setEditingLabel(false);
    setNodes(nds => nds.map(n => n.id === id ? { ...n, data: { ...n.data, label: labelDraft } } : n));
  };

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      {/* Header */}
      <div
        className="nodrag"
        style={{
          position: 'absolute', top: 0, left: 0, right: 0,
          height: '34px',
          background: 'rgba(108,99,255,0.14)',
          borderRadius: collapsed ? '6px' : '6px 6px 0 0',
          display: 'flex', alignItems: 'center',
          padding: '0 8px', gap: '6px',
          borderBottom: collapsed ? 'none' : '1px dashed rgba(108,99,255,0.4)',
        }}
      >
        <button
          onClick={toggleCollapse}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-secondary)', fontSize: '10px', padding: '2px 4px', flexShrink: 0,
          }}
          title={collapsed ? 'Expand group' : 'Collapse group'}
        >
          {collapsed ? '▶' : '▼'}
        </button>
        {editingLabel ? (
          <input
            className="nodrag"
            autoFocus
            value={labelDraft}
            onChange={e => setLabelDraft(e.target.value)}
            onBlur={finishLabel}
            onKeyDown={e => { if (e.key === 'Enter') finishLabel(); }}
            style={{
              flex: 1, background: 'transparent', border: 'none',
              borderBottom: '1px solid var(--accent-color)',
              color: 'var(--text-primary)', fontSize: '12px',
              fontWeight: 600, outline: 'none', minWidth: 0,
            }}
          />
        ) : (
          <span
            onDoubleClick={() => { setLabelDraft(data?.label || 'Group'); setEditingLabel(true); }}
            style={{
              flex: 1, fontSize: '12px', fontWeight: 600,
              color: 'var(--text-secondary)', whiteSpace: 'nowrap',
              overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'default',
            }}
            title="Double-click to rename"
          >
            {data?.label || 'Group'}
          </span>
        )}
        <span style={{ fontSize: '10px', color: 'var(--text-tertiary)', flexShrink: 0 }}>
          {collapsed ? '(collapsed)' : ''}
        </span>
      </div>
      {/* Body overlay (expanded only) */}
      {!collapsed && (
        <div style={{
          position: 'absolute', top: '34px', left: 0, right: 0, bottom: 0,
          borderRadius: '0 0 6px 6px',
          border: '1px dashed rgba(108,99,255,0.35)',
          borderTop: 'none',
          pointerEvents: 'none',
        }} />
      )}
    </div>
  );
}

// §4.79 Multi-Agent Orchestration Studio — custom ReactFlow node + edge
const AGENT_ROLE_COLORS = {
  supervisor: '#D4A017', // gold
  worker: '#A0AEC0',     // silver
  specialist: '#38B2AC', // teal
};
const AGENT_ROLE_ICONS = {
  supervisor: '👑',
  worker: '⚙️',
  specialist: '🔧',
};
const PROVIDER_ICONS = {
  openai: '🟢',
  anthropic: '🟣',
  groq: '⚡',
  mistral: '🌬️',
  azure_openai: '☁️',
  gemini: '✨',
  ollama: '🦙',
};

function AgentGraphNode({ data }) {
  const role = (data?.params?.agent_role || 'worker').toLowerCase();
  const provider = (data?.params?.provider || 'openai').toLowerCase();
  const status = data?.status;
  const roleColor = AGENT_ROLE_COLORS[role] || AGENT_ROLE_COLORS.worker;
  const providerIcon = PROVIDER_ICONS[provider] || '🤖';
  const roleIcon = AGENT_ROLE_ICONS[role] || '⚙️';

  let borderColor = roleColor;
  let boxShadow = '0 1px 2px rgba(0,0,0,0.25)';
  let animation = 'none';
  if (status === 'running') {
    borderColor = 'var(--warning-color)';
    boxShadow = '0 0 10px var(--warning-color)';
    animation = 'xavvyNodeRunningPulse 1.1s ease-in-out infinite';
  } else if (status === 'completed') {
    borderColor = 'var(--success-color)';
    boxShadow = '0 0 5px var(--success-color)';
  } else if (status === 'failed') {
    borderColor = 'var(--error-color)';
    boxShadow = '0 0 5px var(--error-color)';
  }

  const rootStyle = {
    minWidth: '200px',
    background: 'var(--bg-node, #2d2d2d)',
    color: 'var(--text-primary)',
    borderLeft: `5px solid ${roleColor}`,
    border: `2px solid ${borderColor}`,
    borderLeftWidth: '5px',
    borderRadius: '8px',
    padding: '10px 12px',
    boxShadow,
    position: 'relative',
    animation,
    fontSize: '12px',
  };

  return (
    <div style={rootStyle}>
      {data?.pinnedData && (
        <div style={{ position: 'absolute', top: -7, right: -7, fontSize: '13px', lineHeight: 1, zIndex: 10, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))' }} title="Test data pinned — node skipped during execution">📌</div>
      )}
      <Handle type="target" position={Position.Top} style={{ background: 'var(--accent-color)' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
        <span style={{ fontSize: '16px' }} title={`provider: ${provider}`}>{providerIcon}</span>
        <span style={{ fontWeight: 600, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{data?.label || 'Agent'}</span>
        <span title={`role: ${role}`} style={{ fontSize: '14px' }}>{roleIcon}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '10px', color: 'var(--text-tertiary)' }}>
        <span style={{ background: roleColor, color: '#000', padding: '1px 6px', borderRadius: '3px', fontWeight: 600, textTransform: 'uppercase' }}>{role}</span>
        <span title="Drag from this handle to delegate to another agent">delegate →</span>
      </div>
      <Handle type="source" position={Position.Bottom} style={{ background: 'var(--accent-color)' }} />
      <Handle
        id="delegate"
        type="source"
        position={Position.Right}
        style={{ background: '#8B5CF6', border: '2px dashed #fff', width: 12, height: 12, top: '50%' }}
      />
    </div>
  );
}

// Condition-aware edge: colors line + pill label by branch type
const EDGE_CONDITION_COLORS = {
  true:      { stroke: '#22c55e', bg: 'rgba(34,197,94,0.12)',   border: 'rgba(34,197,94,0.4)',   text: '#86efac' },
  success:   { stroke: '#22c55e', bg: 'rgba(34,197,94,0.12)',   border: 'rgba(34,197,94,0.4)',   text: '#86efac' },
  approve:   { stroke: '#22c55e', bg: 'rgba(34,197,94,0.12)',   border: 'rgba(34,197,94,0.4)',   text: '#86efac' },
  false:     { stroke: '#ef4444', bg: 'rgba(239,68,68,0.12)',   border: 'rgba(239,68,68,0.4)',   text: '#fca5a5' },
  error:     { stroke: '#ef4444', bg: 'rgba(239,68,68,0.12)',   border: 'rgba(239,68,68,0.4)',   text: '#fca5a5' },
  reject:    { stroke: '#ef4444', bg: 'rgba(239,68,68,0.12)',   border: 'rgba(239,68,68,0.4)',   text: '#fca5a5' },
  try:       { stroke: '#06b6d4', bg: 'rgba(6,182,212,0.12)',   border: 'rgba(6,182,212,0.4)',   text: '#67e8f9' },
  loopBody:  { stroke: '#7c7cff', bg: 'rgba(124,124,255,0.12)', border: 'rgba(124,124,255,0.4)', text: '#a5b4fc' },
  completed: { stroke: '#71717a', bg: 'rgba(113,113,122,0.12)', border: 'rgba(113,113,122,0.4)', text: '#a1a1aa' },
  cancel:    { stroke: '#71717a', bg: 'rgba(113,113,122,0.12)', border: 'rgba(113,113,122,0.4)', text: '#a1a1aa' },
  _delegate: { stroke: '#8B5CF6', bg: 'rgba(139,92,246,0.12)',  border: 'rgba(139,92,246,0.4)',  text: '#c4b5fd' },
  _tool:     { stroke: '#a855f7', bg: 'rgba(168,85,247,0.12)',  border: 'rgba(168,85,247,0.4)',  text: '#d8b4fe' },
  _default:  { stroke: '#71717a', bg: 'rgba(113,113,122,0.12)', border: 'rgba(113,113,122,0.4)', text: '#a1a1aa' },
};

function getAvailableConditions(nodeType, nodeData) {
  if (nodeType === 'if') return ['true', 'false'];
  if (nodeType === 'loop') return ['loopBody', 'completed'];
  if (nodeType === 'try_catch') return ['try', 'success', 'error'];
  if (nodeType === 'switch') {
    const cases = nodeData?.params?.cases || [];
    const def = nodeData?.params?.default_case || 'Default';
    return [...new Set([...cases, def])].map(c => String(c || '').trim()).filter(Boolean);
  }
  if (nodeType === 'human_approval') return normalizeHumanApprovalOptions(nodeData?.params?.options);
  return [];
}

function getEdgeConditionColor(data, label) {
  if (data?.edge_type === 'delegate_to') return EDGE_CONDITION_COLORS._delegate;
  if (data?.edge_type === 'tool')        return EDGE_CONDITION_COLORS._tool;
  const key = data?.condition || label;
  return EDGE_CONDITION_COLORS[key] || EDGE_CONDITION_COLORS._default;
}

function ConditionEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data, label, markerEnd, selected }) {
  const [edgePath, labelX, labelY] = getBezierPath({ sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition });
  const c = getEdgeConditionColor(data, label);
  const isDashed = data?.edge_type === 'tool' || data?.edge_type === 'delegate_to';
  const displayLabel = label || data?.condition;
  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          stroke: c.stroke,
          strokeWidth: selected ? 2 : 1.5,
          strokeDasharray: isDashed ? '5 3' : undefined,
          transition: 'stroke-width 120ms ease',
        }}
      />
      {displayLabel && (
        <EdgeLabelRenderer>
          <div style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            background: c.bg,
            border: `1px solid ${c.border}`,
            color: c.text,
            padding: '1px 6px',
            borderRadius: '999px',
            fontSize: '10px',
            fontWeight: 600,
            pointerEvents: 'none',
            userSelect: 'none',
          }}>
            {displayLabel}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

const nodeTypes = { icon: IconNode, sticky_note: StickyNoteNode, group: GroupNode, agent_graph_node: AgentGraphNode };
const edgeTypes = { delegate_to: ConditionEdge, condition: ConditionEdge };

function ExecutionPanel({ result, onClose, workflowId }) {
  const [retrying, setRetrying] = useState(false);
  const [retryResult, setRetryResult] = useState(null);

  const { connect: oauthConnect, connecting: oauthConnecting } = useOAuthConnect({
    onSuccess: async (credentialId) => {
      // After successful re-auth, auto-retry from checkpoint
      if (!result?.run_id) return;
      setRetrying(true);
      setRetryResult(null);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/runs/${result.run_id}/reauth-resume`, {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        if (res.ok) {
          setRetryResult({ success: true, run_id: data.run_id });
        } else {
          setRetryResult({ success: false, detail: data.detail || 'Resume failed' });
        }
      } catch (err) {
        setRetryResult({ success: false, detail: err.message });
      } finally {
        setRetrying(false);
      }
    },
  });

  if (!result) return null;

  const reauth = result.credential_reauth;
  const serviceName = reauth?.service_type
    ? reauth.service_type.charAt(0).toUpperCase() + reauth.service_type.slice(1)
    : 'Service';

  return (
    <div style={{
      height: '300px',
      background: 'var(--bg-secondary)',
      borderTop: '1px solid var(--border-color)',
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 0,
      zIndex: 10
    }}>
      <div style={{
        padding: '10px 20px',
        background: 'var(--bg-tertiary)',
        borderBottom: '1px solid var(--border-color)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span style={{ fontWeight: 'bold', color: 'var(--text-primary)', fontSize: '14px' }}>Execution Result</span>
        <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: '16px' }}>✕</button>
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
        {/* §4.83 Credential Re-auth: show Reconnect banner when auth failure detected */}
        {reauth && (
          <div style={{
            background: 'rgba(239,68,68,0.08)',
            border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: '8px',
            padding: '14px 16px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
          }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text-primary)', marginBottom: '4px' }}>
                Authentication Failed
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                The <strong>{reauth.credential_name || serviceName}</strong> credential has expired or been revoked.
                Reconnect to refresh the token{result.run_id ? ' and auto-retry this run from checkpoint.' : '.'}
              </div>
              {retryResult && (
                <div style={{ marginTop: '6px', fontSize: '12px', color: retryResult.success ? '#10b981' : '#ef4444' }}>
                  {retryResult.success
                    ? `Resumed successfully (new run: ${retryResult.run_id})`
                    : `Retry failed: ${retryResult.detail}`}
                </div>
              )}
            </div>
            <button
              disabled={oauthConnecting || retrying}
              onClick={() => oauthConnect(reauth.service_type, reauth.credential_name || `My ${serviceName} Account`, reauth.credential_id)}
              style={{
                background: 'var(--accent-color)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                fontWeight: 600,
                fontSize: '13px',
                cursor: oauthConnecting || retrying ? 'not-allowed' : 'pointer',
                opacity: oauthConnecting || retrying ? 0.6 : 1,
                whiteSpace: 'nowrap',
              }}
            >
              {oauthConnecting ? 'Connecting...' : retrying ? 'Retrying...' : `Reconnect ${serviceName}`}
            </button>
          </div>
        )}
        <pre
          tabIndex={0}
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && (e.key === 'a' || e.key === 'A')) {
              e.preventDefault();
              e.stopPropagation();
              const range = document.createRange();
              range.selectNodeContents(e.currentTarget);
              const sel = window.getSelection();
              sel.removeAllRanges();
              sel.addRange(range);
            }
          }}
          style={{ margin: 0, fontFamily: 'monospace', color: 'var(--text-secondary)', fontSize: '12px', whiteSpace: 'pre-wrap', outline: 'none' }}
        >
          {JSON.stringify(result, null, 2)}
        </pre>
      </div>
    </div>
  );
}

const SCHEDULE_PRESETS = [
  { value: 'interval', label: 'Every few minutes' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'custom', label: 'Custom cron' }
];

const SCHEDULE_INTERVALS = [5, 10, 15, 30];

const WEEKDAY_OPTIONS = [
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' }
];

const clampInteger = (rawValue, fallback, min, max) => {
  const parsed = Number.parseInt(rawValue, 10);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }
  return Math.min(max, Math.max(min, parsed));
};

const padTimePart = (value) => String(value).padStart(2, '0');

const parseHourMinute = (timeValue, fallbackHour, fallbackMinute) => {
  const [hourPart, minutePart] = String(timeValue || '').split(':');
  return {
    hour: clampInteger(hourPart, fallbackHour, 0, 23),
    minute: clampInteger(minutePart, fallbackMinute, 0, 59)
  };
};

const normalizeWeekday = (rawValue) => {
  const value = String(rawValue || '').trim().toLowerCase();
  const weekdayMap = {
    sun: '0',
    sunday: '0',
    mon: '1',
    monday: '1',
    tue: '2',
    tuesday: '2',
    wed: '3',
    wednesday: '3',
    thu: '4',
    thursday: '4',
    fri: '5',
    friday: '5',
    sat: '6',
    saturday: '6',
  };
  if (weekdayMap[value]) {
    return weekdayMap[value];
  }
  return WEEKDAY_OPTIONS.some((option) => option.value === value) ? value : '1';
};

const wrapDayIndex = (value) => ((value % 7) + 7) % 7;

const convertTimeByMinutes = (hour, minute, offsetMinutes) => {
  const baseMinutes = clampInteger(hour, 0, 0, 23) * 60 + clampInteger(minute, 0, 0, 59);
  const shiftedTotal = baseMinutes + offsetMinutes;
  const minutesInDay = 24 * 60;
  const normalizedMinutes = ((shiftedTotal % minutesInDay) + minutesInDay) % minutesInDay;
  const dayShift = Math.floor((shiftedTotal - normalizedMinutes) / minutesInDay);
  return {
    hour: Math.floor(normalizedMinutes / 60),
    minute: normalizedMinutes % 60,
    dayShift
  };
};

const shiftScheduledFields = (preset, hour, minute, weekday, monthDay, offsetMinutes) => {
  if (!offsetMinutes || !Number.isFinite(offsetMinutes)) {
    return {
      hour: clampInteger(hour, 9, 0, 23),
      minute: clampInteger(minute, 0, 0, 59),
      weekday: normalizeWeekday(weekday),
      monthDay: clampInteger(monthDay, 1, 1, 31)
    };
  }

  const shifted = convertTimeByMinutes(hour, minute, offsetMinutes);
  let shiftedWeekday = normalizeWeekday(weekday);
  let shiftedMonthDay = clampInteger(monthDay, 1, 1, 31);

  if (preset === 'weekly') {
    const weekdayNumber = Number.parseInt(shiftedWeekday, 10);
    shiftedWeekday = String(wrapDayIndex((Number.isFinite(weekdayNumber) ? weekdayNumber : 1) + shifted.dayShift));
  } else if (preset === 'monthly') {
    shiftedMonthDay = clampInteger(shiftedMonthDay + shifted.dayShift, shiftedMonthDay, 1, 31);
  }

  return {
    hour: shifted.hour,
    minute: shifted.minute,
    weekday: shiftedWeekday,
    monthDay: shiftedMonthDay
  };
};

const parseScheduleFromCron = (cronExpression, timezoneOffsetMinutes = 0) => {
  const trimmedCron = String(cronExpression || '').trim();
  const defaultState = {
    enabled: false,
    preset: 'daily',
    intervalMinutes: 15,
    hour: 9,
    minute: 0,
    weekday: '1',
    monthDay: 1,
    customCron: trimmedCron
  };

  if (!trimmedCron) {
    return defaultState;
  }

  const parts = trimmedCron.split(/\s+/);
  if (parts.length !== 5) {
    return { ...defaultState, enabled: true, preset: 'custom' };
  }

  const [minutePart, hourPart, dayPart, monthPart, weekPart] = parts;
  const numericMinute = /^\d+$/.test(minutePart);
  const numericHour = /^\d+$/.test(hourPart);
  const numericDay = /^\d+$/.test(dayPart);

  if (minutePart.startsWith('*/') && hourPart === '*' && dayPart === '*' && monthPart === '*' && weekPart === '*') {
    const interval = clampInteger(minutePart.replace('*/', ''), 15, 1, 59);
    return {
      ...defaultState,
      enabled: true,
      preset: 'interval',
      intervalMinutes: interval,
      customCron: trimmedCron
    };
  }

  if (numericMinute && hourPart === '*' && dayPart === '*' && monthPart === '*' && weekPart === '*') {
    return {
      ...defaultState,
      enabled: true,
      preset: 'hourly',
      minute: clampInteger(minutePart, 0, 0, 59),
      customCron: trimmedCron
    };
  }

  if (numericMinute && numericHour && dayPart === '*' && monthPart === '*' && weekPart === '*') {
    const shifted = shiftScheduledFields(
      'daily',
      clampInteger(hourPart, 9, 0, 23),
      clampInteger(minutePart, 0, 0, 59),
      '1',
      1,
      timezoneOffsetMinutes
    );
    return {
      ...defaultState,
      enabled: true,
      preset: 'daily',
      hour: shifted.hour,
      minute: shifted.minute,
      customCron: trimmedCron
    };
  }

  if (numericMinute && numericHour && dayPart === '*' && monthPart === '*') {
    const shifted = shiftScheduledFields(
      'weekly',
      clampInteger(hourPart, 9, 0, 23),
      clampInteger(minutePart, 0, 0, 59),
      normalizeWeekday(weekPart),
      1,
      timezoneOffsetMinutes
    );
    return {
      ...defaultState,
      enabled: true,
      preset: 'weekly',
      hour: shifted.hour,
      minute: shifted.minute,
      weekday: shifted.weekday,
      customCron: trimmedCron
    };
  }

  if (numericMinute && numericHour && numericDay && monthPart === '*' && weekPart === '*') {
    const shifted = shiftScheduledFields(
      'monthly',
      clampInteger(hourPart, 9, 0, 23),
      clampInteger(minutePart, 0, 0, 59),
      '1',
      clampInteger(dayPart, 1, 1, 31),
      timezoneOffsetMinutes
    );
    return {
      ...defaultState,
      enabled: true,
      preset: 'monthly',
      hour: shifted.hour,
      minute: shifted.minute,
      monthDay: shifted.monthDay,
      customCron: trimmedCron
    };
  }

  return { ...defaultState, enabled: true, preset: 'custom' };
};

const buildCronFromSchedule = ({ enabled, preset, intervalMinutes, hour, minute, weekday, monthDay, customCron, timezoneOffsetMinutes = 0 }) => {
  if (!enabled) {
    return '';
  }

  const safeTime = shiftScheduledFields(
    preset,
    hour,
    minute,
    weekday,
    monthDay,
    timezoneOffsetMinutes
  );
  const safeMinute = safeTime.minute;
  const safeHour = safeTime.hour;
  const safeMonthDay = clampInteger(monthDay, 1, 1, 31);
  const safeWeekday = safeTime.weekday;
  const adjustedMonthDay = safeTime.monthDay;
  const safeInterval = clampInteger(intervalMinutes, 15, 1, 59);

  if (preset === 'interval') {
    return `*/${safeInterval} * * * *`;
  }
  if (preset === 'hourly') {
    return `${safeMinute} * * * *`;
  }
  if (preset === 'daily') {
    return `${safeMinute} ${safeHour} * * *`;
  }
  if (preset === 'weekly') {
    return `${safeMinute} ${safeHour} * * ${safeWeekday}`;
  }
  if (preset === 'monthly') {
    return `${safeMinute} ${safeHour} ${adjustedMonthDay || safeMonthDay} * *`;
  }
  return String(customCron || '').trim();
};

function ScheduleModal({ cronSchedule, setCronSchedule, isPaused, setIsPaused, maxConcurrentRuns, setMaxConcurrentRuns, schedulerTimezoneMeta, onClose, advancedScheduler, setAdvancedScheduler, slaMaxSeconds, setSlaMaxSeconds, runPriority, setRunPriority }) {
  const clientTimezoneName = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'Local',
    []
  );
  const serverTimezoneName = schedulerTimezoneMeta?.timezone || 'UTC';
  const serverOffsetMinutes = Number.isFinite(schedulerTimezoneMeta?.utc_offset_minutes)
    ? schedulerTimezoneMeta.utc_offset_minutes
    : 0;
  const clientOffsetMinutes = -new Date().getTimezoneOffset();
  const conversionOffsetMinutes = serverOffsetMinutes - clientOffsetMinutes;
  const parsedSchedule = parseScheduleFromCron(cronSchedule, -conversionOffsetMinutes);
  const [scheduleEnabled, setScheduleEnabled] = useState(parsedSchedule.enabled);
  const [schedulePreset, setSchedulePreset] = useState(parsedSchedule.preset);
  const [scheduleIntervalMinutes, setScheduleIntervalMinutes] = useState(parsedSchedule.intervalMinutes);
  const [scheduleHour, setScheduleHour] = useState(parsedSchedule.hour);
  const [scheduleMinute, setScheduleMinute] = useState(parsedSchedule.minute);
  const [scheduleWeekday, setScheduleWeekday] = useState(parsedSchedule.weekday);
  const [scheduleMonthDay, setScheduleMonthDay] = useState(parsedSchedule.monthDay);
  const [customCron, setCustomCron] = useState(parsedSchedule.customCron);
  const [localPaused, setLocalPaused] = useState(isPaused);
  const [localMaxConcurrentRuns, setLocalMaxConcurrentRuns] = useState(maxConcurrentRuns);
  // Advanced Scheduler state
  const [localSkipIfRunning, setLocalSkipIfRunning] = useState(advancedScheduler?.skip_if_running ?? false);
  const [localRunWindowStart, setLocalRunWindowStart] = useState(advancedScheduler?.run_window_start ?? '');
  const [localRunWindowEnd, setLocalRunWindowEnd] = useState(advancedScheduler?.run_window_end ?? '');
  const [localMaxRunsPerHour, setLocalMaxRunsPerHour] = useState(advancedScheduler?.max_runs_per_hour ?? '');
  const [localBlackoutWindows, setLocalBlackoutWindows] = useState(
    advancedScheduler?.blackout_windows ? JSON.parse(advancedScheduler.blackout_windows) : []
  );
  // SLA Alerts (§4.70)
  const [localSlaMaxSeconds, setLocalSlaMaxSeconds] = useState(slaMaxSeconds ?? '');
  // Run Priority Classes
  const [localRunPriority, setLocalRunPriority] = useState(runPriority || 'normal');
  const computedCron = buildCronFromSchedule({
    enabled: scheduleEnabled,
    preset: schedulePreset,
    intervalMinutes: scheduleIntervalMinutes,
    hour: scheduleHour,
    minute: scheduleMinute,
    weekday: scheduleWeekday,
    monthDay: scheduleMonthDay,
    customCron,
    timezoneOffsetMinutes: conversionOffsetMinutes
  });

  const handleApply = () => {
    const parsedMaxConcurrentRuns = Number.parseInt(localMaxConcurrentRuns, 10);
    const cronToSave = buildCronFromSchedule({
      enabled: scheduleEnabled,
      preset: schedulePreset,
      intervalMinutes: scheduleIntervalMinutes,
      hour: scheduleHour,
      minute: scheduleMinute,
      weekday: scheduleWeekday,
      monthDay: scheduleMonthDay,
      customCron,
      timezoneOffsetMinutes: conversionOffsetMinutes
    });
    setCronSchedule(cronToSave);
    setIsPaused(localPaused);
    setMaxConcurrentRuns(Number.isFinite(parsedMaxConcurrentRuns) && parsedMaxConcurrentRuns > 0 ? parsedMaxConcurrentRuns : 3);
    const parsedMaxRunsPerHour = Number.parseInt(localMaxRunsPerHour, 10);
    setAdvancedScheduler({
      skip_if_running: localSkipIfRunning,
      run_window_start: localRunWindowStart || null,
      run_window_end: localRunWindowEnd || null,
      max_runs_per_hour: Number.isFinite(parsedMaxRunsPerHour) && parsedMaxRunsPerHour > 0 ? parsedMaxRunsPerHour : null,
      blackout_windows: localBlackoutWindows.length > 0 ? JSON.stringify(localBlackoutWindows) : null,
    });
    const parsedSla = Number.parseInt(localSlaMaxSeconds, 10);
    setSlaMaxSeconds(Number.isFinite(parsedSla) && parsedSla > 0 ? parsedSla : null);
    setRunPriority(['high', 'normal', 'low'].includes(localRunPriority) ? localRunPriority : 'normal');
    onClose();
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'var(--bg-modal-overlay)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'var(--bg-modal)', padding: '20px', borderRadius: '8px', width: '460px',
        color: 'var(--text-primary)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-strong)'
      }}>
        <h3 style={{ marginTop: 0 }}>Schedule Workflow</h3>
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={scheduleEnabled}
              onChange={(e) => setScheduleEnabled(e.target.checked)}
              style={{ marginRight: '10px' }}
            />
            Run on a schedule
          </label>
        </div>
        {scheduleEnabled && (
          <>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Frequency</label>
              <select
                value={schedulePreset}
                onChange={(e) => setSchedulePreset(e.target.value)}
                style={{ width: '100%', padding: '8px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px' }}
              >
                {SCHEDULE_PRESETS.map((preset) => (
                  <option key={preset.value} value={preset.value}>
                    {preset.label}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: '15px', fontSize: '12px', color: 'var(--text-secondary)' }}>
              Schedule time is set in <strong>{clientTimezoneName}</strong> and converted to server timezone <strong>{serverTimezoneName}</strong> before saving.
            </div>

            {schedulePreset === 'interval' && (
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Run every</label>
                <select
                  value={String(scheduleIntervalMinutes)}
                  onChange={(e) => setScheduleIntervalMinutes(clampInteger(e.target.value, 15, 1, 59))}
                  style={{ width: '100%', padding: '8px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px' }}
                >
                  {SCHEDULE_INTERVALS.map((interval) => (
                    <option key={interval} value={interval}>
                      {interval} minutes
                    </option>
                  ))}
                </select>
              </div>
            )}

            {schedulePreset === 'hourly' && (
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Minute of each hour</label>
                <input
                  type="number"
                  min="0"
                  max="59"
                  value={scheduleMinute}
                  onChange={(e) => setScheduleMinute(clampInteger(e.target.value, 0, 0, 59))}
                  style={{ width: '100%', padding: '8px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px' }}
                />
              </div>
            )}

            {(schedulePreset === 'daily' || schedulePreset === 'weekly' || schedulePreset === 'monthly') && (
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Run at</label>
                <input
                  type="time"
                  value={`${padTimePart(scheduleHour)}:${padTimePart(scheduleMinute)}`}
                  onChange={(e) => {
                    const parsedTime = parseHourMinute(e.target.value, scheduleHour, scheduleMinute);
                    setScheduleHour(parsedTime.hour);
                    setScheduleMinute(parsedTime.minute);
                  }}
                  style={{ width: '100%', padding: '8px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px' }}
                />
              </div>
            )}

            {schedulePreset === 'weekly' && (
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Day of week</label>
                <select
                  value={scheduleWeekday}
                  onChange={(e) => setScheduleWeekday(normalizeWeekday(e.target.value))}
                  style={{ width: '100%', padding: '8px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px' }}
                >
                  {WEEKDAY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {schedulePreset === 'monthly' && (
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Day of month</label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={scheduleMonthDay}
                  onChange={(e) => setScheduleMonthDay(clampInteger(e.target.value, 1, 1, 31))}
                  style={{ width: '100%', padding: '8px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px' }}
                />
              </div>
            )}

            {schedulePreset === 'custom' && (
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Cron expression</label>
                <input
                  type="text"
                  value={customCron}
                  onChange={(e) => setCustomCron(e.target.value)}
                  placeholder="* * * * *"
                  style={{ width: '100%', padding: '8px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px' }}
                />
              </div>
            )}

            {schedulePreset !== 'custom' && (
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Generated cron</label>
                <input
                  type="text"
                  value={computedCron}
                  readOnly
                  style={{ width: '100%', padding: '8px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)', borderRadius: '4px' }}
                />
                <div style={{ marginTop: '6px', fontSize: '11px', color: 'var(--text-tertiary)' }}>
                  Stored in server timezone: {serverTimezoneName}
                </div>
              </div>
            )}
          </>
        )}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={localPaused}
              onChange={(e) => setLocalPaused(e.target.checked)}
              style={{ marginRight: '10px' }}
            />
            Pause Schedule
          </label>
        </div>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '5px' }}>Max Concurrent Runs</label>
          <input
            type="number"
            min="1"
            value={localMaxConcurrentRuns}
            onChange={(e) => setLocalMaxConcurrentRuns(e.target.value)}
            style={{ width: '100%', padding: '8px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px' }}
          />
        </div>

        {/* Advanced Scheduler options */}
        <details style={{ marginBottom: '20px' }}>
          <summary style={{ cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '10px' }}>
            Advanced Scheduler options
          </summary>
          <div style={{ paddingTop: '10px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
              <input type="checkbox" checked={localSkipIfRunning} onChange={(e) => setLocalSkipIfRunning(e.target.checked)} />
              Skip if a run is already active
            </label>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>Run window (UTC)</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input type="time" value={localRunWindowStart} onChange={(e) => setLocalRunWindowStart(e.target.value)}
                  style={{ flex: 1, padding: '6px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px', fontSize: '13px' }} />
                <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>to</span>
                <input type="time" value={localRunWindowEnd} onChange={(e) => setLocalRunWindowEnd(e.target.value)}
                  style={{ flex: 1, padding: '6px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px', fontSize: '13px' }} />
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '3px' }}>Only run within this time range. Leave blank to allow any time.</div>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>Max runs per hour</label>
              <input type="number" min="1" placeholder="Unlimited"
                value={localMaxRunsPerHour}
                onChange={(e) => setLocalMaxRunsPerHour(e.target.value)}
                style={{ width: '100%', padding: '6px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px', fontSize: '13px' }} />
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{ fontSize: '13px' }}>Blackout windows (UTC)</label>
                <button onClick={() => setLocalBlackoutWindows(w => [...w, { days: [0,1,2,3,4,5,6], start: '22:00', end: '06:00' }])}
                  style={{ fontSize: '11px', padding: '2px 8px', background: 'var(--accent-color)', border: 'none', color: 'var(--text-inverse)', borderRadius: '4px', cursor: 'pointer' }}>+ Add</button>
              </div>
              {localBlackoutWindows.map((bw, idx) => (
                <div key={idx} style={{ border: '1px solid var(--border-color)', borderRadius: '4px', padding: '8px', marginBottom: '6px', fontSize: '12px' }}>
                  <div style={{ display: 'flex', gap: '6px', marginBottom: '6px', alignItems: 'center' }}>
                    <input type="time" value={bw.start}
                      onChange={(e) => setLocalBlackoutWindows(w => w.map((x, i) => i === idx ? { ...x, start: e.target.value } : x))}
                      style={{ flex: 1, padding: '4px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px' }} />
                    <span style={{ color: 'var(--text-secondary)' }}>–</span>
                    <input type="time" value={bw.end}
                      onChange={(e) => setLocalBlackoutWindows(w => w.map((x, i) => i === idx ? { ...x, end: e.target.value } : x))}
                      style={{ flex: 1, padding: '4px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px' }} />
                    <button onClick={() => setLocalBlackoutWindows(w => w.filter((_, i) => i !== idx))}
                      style={{ background: 'transparent', border: 'none', color: 'var(--text-danger, #e57373)', cursor: 'pointer', fontSize: '14px' }}>✕</button>
                  </div>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((day, di) => (
                      <label key={day} style={{ display: 'flex', alignItems: 'center', gap: '2px', cursor: 'pointer' }}>
                        <input type="checkbox"
                          checked={(bw.days ?? [0,1,2,3,4,5,6]).includes(di)}
                          onChange={(e) => {
                            const curr = bw.days ?? [0,1,2,3,4,5,6];
                            const next = e.target.checked ? [...curr, di].sort() : curr.filter(d => d !== di);
                            setLocalBlackoutWindows(w => w.map((x, i) => i === idx ? { ...x, days: next } : x));
                          }} />
                        {day}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              {localBlackoutWindows.length === 0 && <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>No blackout windows configured.</div>}
            </div>
          </div>
        </details>

        {/* Run Priority Classes */}
        <details style={{ marginBottom: '16px', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '0' }}>
          <summary style={{ padding: '8px 12px', cursor: 'pointer', fontWeight: 600, fontSize: '13px', userSelect: 'none', listStyle: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}>
            🚦 Run Priority
          </summary>
          <div style={{ padding: '12px', borderTop: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
              High-priority runs jump the queue ahead of normal and low; low yields to others. Use High for alert workflows that can't wait behind bulk jobs.
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontSize: '12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Priority</label>
              <select
                value={localRunPriority}
                onChange={(e) => setLocalRunPriority(e.target.value)}
                style={{ flex: 1, padding: '5px 8px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px', fontSize: '13px' }}
              >
                <option value="high">🔴 High — jumps the queue</option>
                <option value="normal">⚪ Normal (default)</option>
                <option value="low">🔵 Low — yields to others</option>
              </select>
            </div>
          </div>
        </details>

        {/* SLA Alerts (§4.70) */}
        <details style={{ marginBottom: '16px', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '0' }}>
          <summary style={{ padding: '8px 12px', cursor: 'pointer', fontWeight: 600, fontSize: '13px', userSelect: 'none', listStyle: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}>
            ⏱ SLA Alerts
          </summary>
          <div style={{ padding: '12px', borderTop: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
              Alert when a run exceeds the maximum allowed duration. Leave blank to disable.
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontSize: '12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Max duration</label>
              <input
                type="number"
                min="1"
                placeholder="e.g. 300"
                value={localSlaMaxSeconds}
                onChange={(e) => setLocalSlaMaxSeconds(e.target.value)}
                style={{ flex: 1, padding: '5px 8px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px', fontSize: '13px' }}
              />
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>seconds</span>
            </div>
            {localSlaMaxSeconds > 0 && (
              <div style={{ fontSize: '11px', color: 'var(--accent-color)' }}>
                Alert fires if a run is still in-progress after {localSlaMaxSeconds}s ({Math.floor(localSlaMaxSeconds / 60)}m {localSlaMaxSeconds % 60}s)
              </div>
            )}
          </div>
        </details>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-secondary)', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleApply} style={{ padding: '8px 16px', background: 'var(--accent-color)', border: 'none', color: 'var(--text-inverse)', borderRadius: '4px', cursor: 'pointer' }}>Apply</button>
        </div>
      </div>
    </div>
  );
}

function MoveWorkflowModal({ workflow, selectedCount = 0, onClose, onMove }) {
    const [workspaces, setWorkspaces] = useState([]);
    const [selectedWorkspaceId, setSelectedWorkspaceId] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchWorkspaces = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/workspaces/`, { params: { member_only: true } });
                setWorkspaces(res.data);
            } catch (error) {
                console.error("Failed to fetch workspaces", error);
            } finally {
                setLoading(false);
            }
        };
        fetchWorkspaces();
    }, []);

    // Initialize with current workspace if exists
    useEffect(() => {
        if (workflow?.workspace_id) {
            setSelectedWorkspaceId(workflow.workspace_id);
        } else {
            setSelectedWorkspaceId("");
        }
    }, [workflow]);

    const handleMove = () => {
        const targetId = selectedWorkspaceId === "" ? null : selectedWorkspaceId;
        if (workflow && targetId === workflow.workspace_id) {
            onClose();
            return;
        }
        onMove(targetId);
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'var(--bg-modal-overlay)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
            <div style={{
                backgroundColor: 'var(--bg-modal)', padding: '20px', borderRadius: '8px', width: '400px',
                color: 'var(--text-primary)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-strong)'
            }}>
                <h3 style={{ marginTop: 0 }}>{selectedCount > 0 ? 'Move Workflows' : 'Move Workflow'}</h3>
                <p>
                    {selectedCount > 0 ? (
                        <>Move <strong>{selectedCount}</strong> workflows to:</>
                    ) : (
                        <>Move <strong>{workflow?.name}</strong> to:</>
                    )}
                </p>
                
                <div style={{ marginBottom: '20px' }}>
                    <select 
                        value={selectedWorkspaceId || ""} 
                        onChange={(e) => setSelectedWorkspaceId(e.target.value)}
                        style={{ width: '100%', padding: '8px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px' }}
                        disabled={loading}
                    >
                        <option value="" disabled>Select workspace…</option>
                        {workspaces.map(ws => (
                            <option key={ws.id} value={ws.id}>{ws.name}{ws.is_default ? ' (default)' : ''}</option>
                        ))}
                    </select>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    <button onClick={onClose} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-secondary)', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                    <button onClick={handleMove} style={{ padding: '8px 16px', background: 'var(--accent-color)', border: 'none', color: 'var(--text-inverse)', borderRadius: '4px', cursor: 'pointer' }}>Move</button>
                </div>
            </div>
        </div>
    );
}

// ─── Move to Folder Modal (§12.26) ─────────────────────────────────────────

function MoveToFolderModal({ folders, isBulk, workflowName, onClose, onMove }) {
  const [selectedFolderId, setSelectedFolderId] = React.useState('__none__');
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--bg-modal-overlay)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
      <div style={{ backgroundColor: 'var(--bg-modal)', padding: '20px', borderRadius: '8px', width: '360px', color: 'var(--text-primary)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-strong)' }}>
        <h3 style={{ marginTop: 0 }}>Move to Folder</h3>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
          {isBulk ? 'Move selected workflows to:' : <>Move <strong>{workflowName}</strong> to:</>}
        </p>
        <select
          value={selectedFolderId}
          onChange={e => setSelectedFolderId(e.target.value)}
          style={{ width: '100%', padding: '8px', marginBottom: '16px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px' }}
        >
          <option value="__none__">— No folder (root) —</option>
          {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: 'transparent', border: '1px solid var(--border-color)', color: 'var(--text-secondary)', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
          <button onClick={() => onMove(selectedFolderId === '__none__' ? null : selectedFolderId)} style={{ padding: '8px 16px', background: 'var(--accent-color)', border: 'none', color: '#fff', borderRadius: '4px', cursor: 'pointer' }}>Move</button>
        </div>
      </div>
    </div>
  );
}

// ─── Git Sync Modal (§12.21) ────────────────────────────────────────────────

function GitSyncModal({ workspace, syncBaseUrl, config: initialConfig, onClose, onSaved }) {
  const [tab, setTab] = useState('config'); // 'config' | 'diff'
  const [form, setForm] = useState({
    provider: initialConfig?.provider || 'github',
    repo: initialConfig?.repo || '',
    branch: initialConfig?.branch || 'main',
    directory: initialConfig?.directory || 'workflows',
    access_token: '',
    auto_sync: initialConfig?.auto_sync || false,
  });
  const [config, setConfig] = useState(initialConfig);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [loadingDiff, setLoadingDiff] = useState(false);
  const [diffs, setDiffs] = useState(null);
  const [diffDetail, setDiffDetail] = useState(null); // selected diff entry for viewer
  const [syncResult, setSyncResult] = useState(null);

  const token = localStorage.getItem('token');
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const save = async () => {
    if (!form.repo.trim()) { alert('Repository is required (e.g. owner/repo)'); return; }
    setSaving(true);
    try {
      const res = await axios.put(
        syncBaseUrl,
        { provider: form.provider, repo: form.repo.trim(), branch: form.branch.trim() || 'main', directory: form.directory.trim() || 'workflows', access_token: form.access_token || undefined, auto_sync: form.auto_sync },
        { headers: authHeaders }
      );
      setConfig(res.data);
      setForm(f => ({ ...f, access_token: '' }));
      if (onSaved) await onSaved();
      setSyncResult({ type: 'success', message: 'Git sync configuration saved.' });
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save git sync config');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!(await window.confirm(`Remove git sync configuration for ${workspace.name}?`))) return;
    setRemoving(true);
    try {
      await axios.delete(syncBaseUrl, { headers: authHeaders });
      setConfig(null);
      if (onSaved) await onSaved();
      setSyncResult({ type: 'info', message: 'Git sync configuration removed.' });
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to remove config');
    } finally { setRemoving(false); }
  };

  const push = async () => {
    if (!config) { alert('Save the configuration first.'); return; }
    if (!(await window.confirm('Push all workflows to the git repository? This will create or update workflow files in the repo.'))) return;
    setPushing(true); setSyncResult(null);
    try {
      const res = await axios.post(`${syncBaseUrl}/push`, {}, { headers: authHeaders });
      setSyncResult({ type: res.data.errors?.length ? 'warn' : 'success', message: `Pushed ${res.data.pushed} workflow(s).${res.data.errors?.length ? ` ${res.data.errors.length} error(s).` : ''}`, errors: res.data.errors });
      if (onSaved) await onSaved();
    } catch (e) {
      setSyncResult({ type: 'error', message: e.response?.data?.detail || 'Push failed' });
    } finally { setPushing(false); }
  };

  const pull = async () => {
    if (!config) { alert('Save the configuration first.'); return; }
    if (!(await window.confirm('Pull workflows from the git repository? New workflows will be created; existing workflows with matching names will be updated.'))) return;
    setPulling(true); setSyncResult(null);
    try {
      const res = await axios.post(`${syncBaseUrl}/pull`, {}, { headers: authHeaders });
      setSyncResult({ type: 'success', message: `Created ${res.data.created}, updated ${res.data.updated} workflow(s).${res.data.errors?.length ? ` ${res.data.errors.length} error(s).` : ''}`, errors: res.data.errors });
      if (onSaved) await onSaved();
    } catch (e) {
      setSyncResult({ type: 'error', message: e.response?.data?.detail || 'Pull failed' });
    } finally { setPulling(false); }
  };

  const loadDiff = async () => {
    if (!config) return;
    setLoadingDiff(true); setDiffs(null);
    try {
      const res = await axios.get(`${syncBaseUrl}/diff`, { headers: authHeaders });
      setDiffs(res.data);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to load diff');
    } finally { setLoadingDiff(false); }
  };

  useEffect(() => {
    if (tab === 'diff' && config && !diffs && !loadingDiff) loadDiff();
  }, [tab, config]);

  const statusColor = { in_sync: 'var(--success-color)', modified: 'var(--warning-color)', local_only: 'var(--info-color)', repo_only: 'var(--text-tertiary)' };
  const statusLabel = { in_sync: 'In sync', modified: 'Modified', local_only: 'Local only', repo_only: 'Repo only' };

  const fieldStyle = { width: '100%', padding: '7px 10px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '5px', fontSize: '13px', boxSizing: 'border-box' };
  const labelStyle = { fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' };
  const tabBtnStyle = (active) => ({ padding: '6px 14px', background: active ? 'var(--accent-color)' : 'transparent', color: active ? 'white' : 'var(--text-secondary)', border: '1px solid ' + (active ? 'var(--accent-color)' : 'var(--border-color)'), borderRadius: '5px', cursor: 'pointer', fontSize: '13px', fontWeight: active ? 600 : 400 });

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9100 }}>
      <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '24px', width: '620px', maxHeight: '85vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>⎇ Git Sync — {workspace.name}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '18px' }}>✕</button>
        </div>
        <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>
          Store and deploy workflows via a version-controlled git repository. Push exports all workspace workflows as JSON files; Pull imports them back.
        </p>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button style={tabBtnStyle(tab === 'config')} onClick={() => setTab('config')}>Configuration</button>
          <button style={tabBtnStyle(tab === 'diff')} onClick={() => setTab('diff')} disabled={!config}>
            Diff Viewer {diffs ? `(${diffs.filter(d => d.status !== 'in_sync').length} change${diffs.filter(d => d.status !== 'in_sync').length !== 1 ? 's' : ''})` : ''}
          </button>
        </div>

        {/* ── Config tab ── */}
        {tab === 'config' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Provider</label>
                <select value={form.provider} onChange={e => setForm(f => ({ ...f, provider: e.target.value }))} style={fieldStyle}>
                  <option value="github">GitHub</option>
                  <option value="gitlab">GitLab</option>
                </select>
              </div>
              <div style={{ flex: 2 }}>
                <label style={labelStyle}>Repository (owner/repo)</label>
                <input value={form.repo} onChange={e => setForm(f => ({ ...f, repo: e.target.value }))} placeholder="e.g. acme/workflows" style={fieldStyle} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Branch</label>
                <input value={form.branch} onChange={e => setForm(f => ({ ...f, branch: e.target.value }))} placeholder="main" style={fieldStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Directory in repo</label>
                <input value={form.directory} onChange={e => setForm(f => ({ ...f, directory: e.target.value }))} placeholder="workflows" style={fieldStyle} />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Personal Access Token {config?.has_token ? '(leave blank to keep existing)' : ''}</label>
              <input type="password" value={form.access_token} onChange={e => setForm(f => ({ ...f, access_token: e.target.value }))} placeholder={config?.has_token ? '••••••••' : 'ghp_... or glpat-...'} style={fieldStyle} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input type="checkbox" id="autoSync" checked={form.auto_sync} onChange={e => setForm(f => ({ ...f, auto_sync: e.target.checked }))} />
              <label htmlFor="autoSync" style={{ fontSize: '13px', color: 'var(--text-primary)', cursor: 'pointer' }}>Auto-sync on push (requires webhook)</label>
            </div>

            {/* Webhook URL */}
            {config?.webhook_url && (
              <div style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '10px 12px' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '4px' }}>Webhook URL (add to your {config.provider === 'github' ? 'GitHub repo → Settings → Webhooks' : 'GitLab project → Settings → Webhooks'})</div>
                <code style={{ fontSize: '11px', color: 'var(--text-primary)', wordBreak: 'break-all' }}>{config.webhook_url}</code>
              </div>
            )}

            {/* Sync result */}
            {syncResult && (
              <div style={{ padding: '8px 12px', borderRadius: '6px', fontSize: '13px', background: syncResult.type === 'success' ? 'rgba(76,175,80,0.1)' : syncResult.type === 'warn' ? 'rgba(255,165,0,0.1)' : syncResult.type === 'error' ? 'rgba(244,67,54,0.1)' : 'rgba(33,150,243,0.1)', color: syncResult.type === 'success' ? 'var(--success-color)' : syncResult.type === 'warn' ? 'var(--warning-color)' : syncResult.type === 'error' ? 'var(--error-color)' : 'var(--info-color)' }}>
                {syncResult.message}
                {syncResult.errors?.length > 0 && (
                  <ul style={{ margin: '6px 0 0', paddingLeft: '16px', fontSize: '12px' }}>
                    {syncResult.errors.map((e, i) => <li key={i}>{e.workflow || e.file}: {e.error}</li>)}
                  </ul>
                )}
              </div>
            )}

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
              <button onClick={save} disabled={saving} style={{ background: 'var(--accent-color)', color: 'white', border: 'none', borderRadius: '5px', padding: '7px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                {saving ? 'Saving…' : config ? 'Update Config' : 'Save Config'}
              </button>
              {config && (
                <>
                  <button onClick={push} disabled={pushing} style={{ background: 'var(--info-color)', color: 'var(--text-inverse)', border: 'none', borderRadius: '5px', padding: '7px 16px', cursor: 'pointer', fontSize: '13px' }}>
                    {pushing ? 'Pushing…' : '↑ Push to Repo'}
                  </button>
                  <button onClick={pull} disabled={pulling} style={{ background: 'var(--success-color)', color: 'var(--text-inverse)', border: 'none', borderRadius: '5px', padding: '7px 16px', cursor: 'pointer', fontSize: '13px' }}>
                    {pulling ? 'Pulling…' : '↓ Pull from Repo'}
                  </button>
                  <button onClick={remove} disabled={removing} style={{ background: 'transparent', color: 'var(--error-color)', border: '1px solid var(--error-color)', borderRadius: '5px', padding: '7px 16px', cursor: 'pointer', fontSize: '13px' }}>
                    {removing ? 'Removing…' : 'Remove Config'}
                  </button>
                </>
              )}
            </div>
            {config?.last_synced_at && (
              <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>Last synced: {formatLocalDateTime(config.last_synced_at)}</div>
            )}
          </div>
        )}

        {/* ── Diff Viewer tab ── */}
        {tab === 'diff' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button onClick={loadDiff} disabled={loadingDiff} style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '5px', padding: '5px 12px', cursor: 'pointer', fontSize: '12px' }}>
                {loadingDiff ? 'Loading…' : '↺ Refresh'}
              </button>
              {diffs && <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{diffs.length} workflow(s) total</span>}
            </div>

            {/* Diff list */}
            {diffs && !diffDetail && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {diffs.length === 0 && <div style={{ color: 'var(--text-tertiary)', fontSize: '13px', textAlign: 'center', padding: '16px' }}>No workflows found in repo directory.</div>}
                {diffs.map((d, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--bg-node)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '10px 14px' }}>
                    <div>
                      <span style={{ fontWeight: 500, fontSize: '14px' }}>{d.name}</span>
                      <span style={{ marginLeft: '10px', fontSize: '11px', color: statusColor[d.status] || 'var(--text-tertiary)', fontWeight: 600 }}>{statusLabel[d.status] || d.status}</span>
                    </div>
                    {d.status !== 'in_sync' && (
                      <button onClick={() => setDiffDetail(d)} style={{ background: 'var(--warning-color)', color: 'var(--text-inverse)', border: 'none', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px' }}>
                        View Diff
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Diff detail viewer */}
            {diffDetail && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button onClick={() => setDiffDetail(null)} style={{ background: 'none', border: 'none', color: 'var(--accent-color)', cursor: 'pointer', fontSize: '13px', padding: 0 }}>← Back to list</button>
                  <span style={{ fontWeight: 600, fontSize: '14px' }}>{diffDetail.name}</span>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '4px', fontWeight: 600 }}>DIFF (live → repo/{config?.branch})</div>
                  <pre style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '5px', padding: '10px', fontSize: '11px', overflow: 'auto', maxHeight: '420px', color: 'var(--text-primary)', margin: 0, whiteSpace: 'pre-wrap' }}>
                    {diffDetail.diff_text || 'No textual diff available.'}
                  </pre>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────

function EnvironmentManagerModal({ workspace, environments: initialEnvs, onClose, onSaved }) {
  // workspace is null when managing personal (user-scoped) environments
  const isPersonal = !workspace;
  const envBaseUrl = isPersonal ? `${API_BASE_URL}/users/me/environments` : `${API_BASE_URL}/workspaces/${workspace?.id}/environments`;
  const [envs, setEnvs] = useState(initialEnvs || []);
  const [credentials, setCredentials] = useState([]);
  const [editingEnv, setEditingEnv] = useState(null); // null | {id?, name, credential_mappings: {}}
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    setEnvs(initialEnvs || []);
  }, [initialEnvs]);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/credentials/`).then(r => setCredentials(r.data || [])).catch(() => {});
  }, []);

  const startNew = () => setEditingEnv({ name: '', credential_mappings: {} });
  const startEdit = (env) => setEditingEnv({ id: env.id, name: env.name, credential_mappings: { ...env.credential_mappings } });

  const saveEnv = async () => {
    if (!editingEnv?.name?.trim()) { alert('Environment name is required'); return; }
    setSaving(true);
    try {
      if (editingEnv.id) {
        await axios.put(`${envBaseUrl}/${editingEnv.id}`, {
          name: editingEnv.name, credential_mappings: editingEnv.credential_mappings
        });
      } else {
        await axios.post(envBaseUrl, {
          name: editingEnv.name, credential_mappings: editingEnv.credential_mappings
        });
      }
      setEditingEnv(null);
      if (onSaved) await onSaved();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save environment');
    } finally { setSaving(false); }
  };

  const deleteEnv = async (envId) => {
    if (!(await window.confirm('Delete this environment?'))) return;
    setDeleting(envId);
    try {
      await axios.delete(`${envBaseUrl}/${envId}`);
      if (onSaved) await onSaved();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to delete');
    } finally { setDeleting(null); }
  };

  const updateMapping = (srcId, tgtId) => {
    setEditingEnv(prev => ({ ...prev, credential_mappings: { ...prev.credential_mappings, [srcId]: tgtId } }));
  };

  const removeMapping = (srcId) => {
    setEditingEnv(prev => {
      const m = { ...prev.credential_mappings };
      delete m[srcId];
      return { ...prev, credential_mappings: m };
    });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9000 }}>
      <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '24px', width: '560px', maxHeight: '80vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>🌐 Environments — {isPersonal ? 'Personal' : workspace.name}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '18px' }}>✕</button>
        </div>
        <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>
          Each environment maps credentials by ID. When running a workflow with an environment selected, credential IDs in node configs are substituted according to the mapping (e.g., dev DB → prod DB).
        </p>

        {/* Existing environments list */}
        {envs.length === 0 && !editingEnv && (
          <div style={{ color: 'var(--text-tertiary)', fontSize: '13px', textAlign: 'center', padding: '16px', border: '1px dashed var(--border-color)', borderRadius: '6px' }}>
            No environments yet. Add one below.
          </div>
        )}
        {envs.map(env => (
          <div key={env.id} style={{ background: 'var(--bg-node)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '14px' }}>{env.name}</div>
              <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                {Object.keys(env.credential_mappings || {}).length} credential mapping(s)
              </div>
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button onClick={() => startEdit(env)} style={{ background: 'var(--info-color)', color: 'var(--text-inverse)', border: 'none', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px' }}>Edit</button>
              <button onClick={() => deleteEnv(env.id)} disabled={deleting === env.id} style={{ background: 'var(--error-color)', color: 'var(--text-inverse)', border: 'none', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px' }}>
                {deleting === env.id ? '…' : 'Delete'}
              </button>
            </div>
          </div>
        ))}

        {/* Edit / Create form */}
        {editingEnv ? (
          <div style={{ background: 'var(--bg-primary)', border: '1px solid var(--accent-color)', borderRadius: '8px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Environment Name</label>
              <input
                value={editingEnv.name}
                onChange={(e) => setEditingEnv(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. dev, staging, production"
                style={{ width: '100%', padding: '7px 10px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '5px', fontSize: '13px', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Credential Mappings</div>
              {Object.entries(editingEnv.credential_mappings || {}).map(([srcId, tgtId]) => (
                <div key={srcId} style={{ display: 'flex', gap: '8px', marginBottom: '6px', alignItems: 'center' }}>
                  <select value={srcId} disabled style={{ flex: 1, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px', padding: '5px', fontSize: '12px' }}>
                    <option value={srcId}>{credentials.find(c => c.id === srcId)?.name || srcId}</option>
                  </select>
                  <span style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>→</span>
                  <select value={tgtId} onChange={(e) => updateMapping(srcId, e.target.value)} style={{ flex: 1, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '4px', padding: '5px', fontSize: '12px' }}>
                    {credentials.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <button onClick={() => removeMapping(srcId)} style={{ background: 'none', border: 'none', color: 'var(--error-color)', cursor: 'pointer', fontSize: '14px', padding: '0 4px' }}>✕</button>
                </div>
              ))}
              {/* Add new mapping row */}
              <AddMappingRow credentials={credentials} existingKeys={Object.keys(editingEnv.credential_mappings || {})} onAdd={(src, tgt) => updateMapping(src, tgt)} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button onClick={() => setEditingEnv(null)} style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '5px', padding: '6px 14px', cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
              <button onClick={saveEnv} disabled={saving} style={{ background: 'var(--accent-color)', color: 'white', border: 'none', borderRadius: '5px', padding: '6px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                {saving ? 'Saving…' : 'Save Environment'}
              </button>
            </div>
          </div>
        ) : (
          <button onClick={startNew} style={{ background: 'var(--accent-color)', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, alignSelf: 'flex-start' }}>
            + Add Environment
          </button>
        )}
      </div>
    </div>
  );
}

function AddMappingRow({ credentials, existingKeys, onAdd }) {
  const [srcId, setSrcId] = useState('');
  const [tgtId, setTgtId] = useState('');
  const available = credentials.filter(c => !existingKeys.includes(c.id));
  if (available.length === 0) return null;
  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '4px' }}>
      <select value={srcId} onChange={(e) => setSrcId(e.target.value)} style={{ flex: 1, background: 'var(--bg-secondary)', border: '1px dashed var(--border-color)', color: 'var(--text-secondary)', borderRadius: '4px', padding: '5px', fontSize: '12px' }}>
        <option value="">From credential…</option>
        {available.map(c => <option key={c.id} value={c.id}>{c.name} ({c.service_type})</option>)}
      </select>
      <span style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>→</span>
      <select value={tgtId} onChange={(e) => setTgtId(e.target.value)} style={{ flex: 1, background: 'var(--bg-secondary)', border: '1px dashed var(--border-color)', color: 'var(--text-secondary)', borderRadius: '4px', padding: '5px', fontSize: '12px' }}>
        <option value="">To credential…</option>
        {credentials.map(c => <option key={c.id} value={c.id}>{c.name} ({c.service_type})</option>)}
      </select>
      <button onClick={() => { if (srcId && tgtId) { onAdd(srcId, tgtId); setSrcId(''); setTgtId(''); } }} disabled={!srcId || !tgtId} style={{ background: 'var(--success-color)', color: 'white', border: 'none', borderRadius: '4px', padding: '5px 10px', cursor: srcId && tgtId ? 'pointer' : 'not-allowed', fontSize: '12px' }}>+</button>
    </div>
  );
}

function WorkflowGeneratorModal({ onClose, onApply, currentWorkspace }) {
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedName, setGeneratedName] = useState('Generated Workflow');
  const [generatedDefinitionText, setGeneratedDefinitionText] = useState('{\n  "nodes": [],\n  "edges": []\n}');
  const [placeholders, setPlaceholders] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [selectedCredentialId, setSelectedCredentialId] = useState('');

  useEffect(() => {
    const fetchCredentials = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/credentials/`);
        const allCredentials = Array.isArray(response.data) ? response.data : [];
        const aiCredentials = allCredentials.filter((credential) => {
          const serviceType = (credential?.service_type || '').toLowerCase();
          return serviceType === 'openai' || serviceType === 'gemini' || serviceType === 'google_generativeai';
        });
        setCredentials(aiCredentials);
        if (aiCredentials.length > 0) {
          setSelectedCredentialId(aiCredentials[0].id);
        } else {
          setSelectedCredentialId('');
        }
      } catch (error) {
        setCredentials([]);
        setSelectedCredentialId('');
      }
    };
    fetchCredentials();
  }, []);

  const generateWorkflow = async () => {
    if (!prompt.trim()) {
      alert('Please enter a prompt.');
      return;
    }
    if (!selectedCredentialId) {
      alert('Please select an AI credential.');
      return;
    }
    setIsGenerating(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/workflows/generate`, {
        prompt: prompt.trim(),
        credential_id: selectedCredentialId,
        workspace_id: currentWorkspace?.id || null
      });
      const payload = response.data;
      setGeneratedName(payload.name || 'Generated Workflow');
      setGeneratedDefinitionText(JSON.stringify(payload.definition || { nodes: [], edges: [] }, null, 2));
      setPlaceholders(payload.placeholders || []);
    } catch (error) {
      console.error('Failed to generate workflow', error);
      const detail = error.response?.data?.detail;
      if (detail?.validation_errors) {
        alert(`Generation returned invalid graph:\n${detail.validation_errors.join('\n')}`);
      } else {
        alert(`Failed to generate workflow: ${detail || error.message}`);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const applyGeneratedWorkflow = () => {
    try {
      const parsed = JSON.parse(generatedDefinitionText);
      if (!Array.isArray(parsed.nodes) || !Array.isArray(parsed.edges)) {
        alert('Definition must include nodes and edges arrays.');
        return;
      }
      onApply({
        name: generatedName?.trim() || 'Generated Workflow',
        definition: parsed
      });
    } catch (error) {
      alert(`Invalid JSON: ${error.message}`);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'var(--bg-modal-overlay)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1100
    }}>
      <div style={{
        width: '900px',
        maxWidth: '95vw',
        maxHeight: '92vh',
        overflow: 'auto',
        backgroundColor: 'var(--bg-modal)',
        padding: '20px',
        borderRadius: '8px',
        color: 'var(--text-primary)',
        border: '1px solid var(--border-color)',
        boxShadow: 'var(--shadow-strong)'
      }}>
        <h3 style={{ marginTop: 0 }}>Generate Workflow</h3>
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', marginBottom: '6px' }}>AI Credential</label>
          <select
            value={selectedCredentialId}
            onChange={(e) => setSelectedCredentialId(e.target.value)}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '4px',
              border: '1px solid var(--border-color)',
              background: 'var(--bg-input)',
              color: 'var(--text-primary)'
            }}
          >
            {credentials.length === 0 && <option value="">No AI credentials available</option>}
            {credentials.map((credential) => (
              <option key={credential.id} value={credential.id}>
                {credential.name} ({credential.service_type})
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', marginBottom: '6px' }}>Prompt</label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Describe the workflow to generate..."
            rows={4}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: '4px',
              border: '1px solid var(--border-color)',
              background: 'var(--bg-input)',
              color: 'var(--text-primary)',
              resize: 'vertical'
            }}
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <input
            type="text"
            value={generatedName}
            onChange={(e) => setGeneratedName(e.target.value)}
            placeholder="Workflow Name"
            style={{
              width: '60%',
              padding: '8px',
              borderRadius: '4px',
              border: '1px solid var(--border-color)',
              background: 'var(--bg-input)',
              color: 'var(--text-primary)'
            }}
          />
          <button
            onClick={generateWorkflow}
            disabled={isGenerating}
            style={btnStyle('var(--primary-color)', isGenerating)}
          >
            {isGenerating ? 'Generating...' : 'Generate'}
          </button>
        </div>

        <JsonEditor
          value={generatedDefinitionText}
          onChange={setGeneratedDefinitionText}
          placeholder='{"nodes":[],"edges":[]}'
          style={{ minHeight: '320px' }}
        />

        {placeholders.length > 0 && (
          <div style={{ marginTop: '10px', color: 'var(--warning-color)', fontSize: '12px' }}>
            Placeholders: {placeholders.join(', ')}
          </div>
        )}

        <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button onClick={onClose} style={btnStyle('transparent')}>Cancel</button>
          <button onClick={applyGeneratedWorkflow} style={btnStyle('var(--accent-color)')}>Apply to Canvas</button>
        </div>
      </div>
    </div>
  );
}

function TutorialModal({
  isOpen,
  steps,
  stepIndex,
  stepTargetSelector,
  canAdvance,
  disableAutoShow,
  setDisableAutoShow,
  forceAutoShow,
  onPrevious,
  onNext,
  onClose,
  onFinish
}) {
  const [targetRect, setTargetRect] = useState(null);
  const panelRef = useRef(null);
  const [panelHeight, setPanelHeight] = useState(420);

  useEffect(() => {
    if (!isOpen || !panelRef.current) return;
    const obs = new ResizeObserver(entries => {
      if (entries[0]) setPanelHeight(Math.ceil(entries[0].contentRect.height));
    });
    obs.observe(panelRef.current);
    return () => obs.disconnect();
  }, [isOpen, stepIndex]);

  useEffect(() => {
    if (!isOpen || !stepTargetSelector) {
      setTargetRect(null);
      return;
    }
    const updateTargetRect = () => {
      const target = document.querySelector(stepTargetSelector);
      if (!target) {
        setTargetRect(null);
        return;
      }
      const rect = target.getBoundingClientRect();
      setTargetRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        bottom: rect.bottom
      });
    };
    updateTargetRect();
    const intervalId = window.setInterval(updateTargetRect, 120);
    window.addEventListener('resize', updateTargetRect);
    window.addEventListener('scroll', updateTargetRect, true);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('resize', updateTargetRect);
      window.removeEventListener('scroll', updateTargetRect, true);
    };
  }, [isOpen, stepTargetSelector, stepIndex]);

  if (!isOpen || !steps.length) return null;
  const step = steps[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 720;
  const PAD = 12;
  const floatingWidth = Math.min(420, viewportWidth - PAD * 2);
  const floatingLeft = targetRect
    ? Math.min(Math.max(PAD, targetRect.left), viewportWidth - floatingWidth - PAD)
    : 0;
  const floatingTop = (() => {
    if (!targetRect) return 0;
    const spaceBelow = viewportHeight - targetRect.bottom - PAD * 2;
    if (spaceBelow >= panelHeight) return targetRect.bottom + PAD;
    const topIfAbove = targetRect.top - PAD - panelHeight;
    if (topIfAbove >= PAD) return topIfAbove;
    return Math.max(PAD, Math.min(targetRect.bottom + PAD, viewportHeight - panelHeight - PAD));
  })();

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'var(--bg-modal-overlay)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1200,
      pointerEvents: 'none'
    }}>
      {targetRect && (
        <div style={{
          position: 'fixed',
          top: `${targetRect.top - 6}px`,
          left: `${targetRect.left - 6}px`,
          width: `${targetRect.width + 12}px`,
          height: `${targetRect.height + 12}px`,
          border: '2px solid var(--accent-color)',
          borderRadius: '10px',
          boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.45)',
          pointerEvents: 'none'
        }} />
      )}
      <div ref={panelRef} style={{
        width: targetRect ? `${floatingWidth}px` : 'min(760px, 95vw)',
        maxWidth: '95vw',
        maxHeight: 'calc(100vh - 24px)',
        overflow: 'auto',
        backgroundColor: 'var(--bg-modal)',
        padding: '24px',
        borderRadius: '10px',
        color: 'var(--text-primary)',
        border: '1px solid var(--border-color)',
        boxShadow: 'var(--shadow-strong)',
        pointerEvents: 'auto',
        position: targetRect ? 'fixed' : 'relative',
        top: targetRect ? `${floatingTop}px` : 'auto',
        left: targetRect ? `${floatingLeft}px` : 'auto',
        boxSizing: 'border-box'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ margin: 0 }}>Welcome to xavvy Tutorial</h3>
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            Step {stepIndex + 1} of {steps.length}
          </span>
        </div>

        <div style={{
          height: '8px',
          background: 'var(--bg-input)',
          borderRadius: '999px',
          overflow: 'hidden',
          marginBottom: '20px'
        }}>
          <div style={{
            width: `${((stepIndex + 1) / steps.length) * 100}%`,
            height: '100%',
            background: 'var(--accent-color)',
            transition: 'width 0.2s ease'
          }} />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '20px' }}>{step.title}</h4>
          <p style={{ margin: 0, color: 'var(--text-secondary)', lineHeight: 1.7 }}>{step.description}</p>
        </div>

        {Array.isArray(step.bullets) && step.bullets.length > 0 && (
          <ul style={{ margin: '0 0 20px 18px', color: 'var(--text-secondary)', lineHeight: 1.8 }}>
            {step.bullets.map((item, idx) => (
              <li key={`${step.title}-${idx}`}>{item}</li>
            ))}
          </ul>
        )}

        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={disableAutoShow}
            disabled={forceAutoShow}
            onChange={(e) => setDisableAutoShow(e.target.checked)}
          />
          <span style={{ color: 'var(--text-secondary)' }}>
            {forceAutoShow
              ? 'Demo account always shows this tutorial on login'
              : 'Disable automatic tutorial on next login'}
          </span>
        </label>

        {step.actionHint && (
          <div style={{
            marginBottom: '20px',
            padding: '10px 12px',
            borderRadius: '8px',
            border: '1px solid var(--border-color)',
            background: 'var(--bg-input)',
            color: 'var(--text-secondary)'
          }}>
            {step.actionHint}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
          <button
            onClick={onClose}
            style={{ ...btnStyle('transparent'), flexShrink: 0 }}
          >
            Skip
          </button>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button
              onClick={onPrevious}
              disabled={isFirst}
              style={{ ...btnStyle('transparent', isFirst), flexShrink: 0 }}
            >
              Previous
            </button>
            {!isLast && (
              <button
                onClick={onNext}
                disabled={!canAdvance}
                style={{ ...btnStyle('var(--accent-color)', !canAdvance), flexShrink: 0 }}
              >
                Next
              </button>
            )}
            {isLast && (
              <button
                onClick={onFinish}
                style={{ ...btnStyle('var(--success-color)'), flexShrink: 0 }}
              >
                Finish
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Flow() {
  const reactFlowWrapper = useRef(null);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  // Keep latest nodes/edges in refs for use inside async/timer callbacks
  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);
  nodesRef.current = nodes;
  edgesRef.current = edges;
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedNodes, setSelectedNodes] = useState([]);
  const [copiedNodes, setCopiedNodes] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false); // Toggle for selection mode
  const [viewMode, setViewMode] = useState('canvas'); // 'canvas' | 'steps'
  // §4.79 Multi-Agent Orchestration Studio — workflow type discriminator
  const [workflowType, setWorkflowType] = useState('standard'); // 'standard' | 'agent_graph'
  const [showReasoningPanel, setShowReasoningPanel] = useState(false);
  const [agentReasoningEvents, setAgentReasoningEvents] = useState([]);
  const [showMemoryPanel, setShowMemoryPanel] = useState(false);
  
  const [token, setToken] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get('sso_token');
    if (ssoToken) {
      window.history.replaceState({}, '', window.location.pathname);
      localStorage.setItem('token', ssoToken);
      return ssoToken;
    }
    return localStorage.getItem('token');
  });
  const currentViewRef = useRef('home');

  // ── Tool connect modal (AI Agent tools handle) ─────────────────────────
  const [toolConnectModal, setToolConnectModal] = useState(null); // null | { pendingParams, sourceNodeId, targetNodeId, targetNodeLabel }
  const [toolConnectName, setToolConnectName] = useState('');
  const [toolConnectDesc, setToolConnectDesc] = useState('');
  // Cached node field schemas — populated from /api/nodes on mount, used to build
  // accurate tool parameter schemas for the AI agent's ReAct loop
  const nodeFieldsMapRef = useRef({});

  // ── Embed mode (§4.62) ─────────────────────────────────────────────────
  const [embedToken] = useState(() => new URLSearchParams(window.location.search).get('embed_token'));
  const [embedSession, setEmbedSession] = useState(null); // { allowed_node_types, theme_overrides, workspace_id }

  // ── Branding (§4.65) ──────────────────────────────────────────────────
  const [branding, setBranding] = useState({ app_name: 'xavvy', logo_url: null, favicon_url: null, accent_color: null, footer_text: null, product_mode: 'flow' });
  const isFormsOnly = branding?.product_mode === 'forms';
  const [licenseInfo, setLicenseInfo] = useState(null);

  // Fetch license info after authentication
  useEffect(() => {
    if (!token) return;
    axios.get(`${API_BASE_URL}/license`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setLicenseInfo(res.data))
      .catch(() => setLicenseInfo(null));
  }, [token]);

  // Load branding on mount (public endpoint — no auth needed)
  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/branding`).then(res => {
      const b = res.data;
      setBranding(b);
      if (b.app_name) document.title = b.app_name;
      if (b.favicon_url) {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
        link.href = b.favicon_url;
      }
      if (b.accent_color) {
        document.documentElement.style.setProperty('--accent-color', b.accent_color);
        document.documentElement.style.setProperty('--primary-color', b.accent_color);
      }
    }).catch(() => {});
  }, []);

  // NOTE: forms-only redirect moved after `view` useState declaration (TDZ fix)

  // Mobile detection
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Populate nodeFieldsMapRef so confirmToolConnect can attach field schemas to tool_connections
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${API_BASE_URL}/nodes`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const map = {};
        (res.data || []).forEach(node => {
          if (node.type && Array.isArray(node.fields)) {
            map[node.type] = node.fields.filter(f =>
              f.name && !f.name.startsWith('_') && f.type !== 'credential' && !f.readonly
            ).map(({ name, label, type, required, options, placeholder, description }) =>
              ({ name, label, type, required, options, placeholder, description })
            );
          }
        });
        nodeFieldsMapRef.current = map;
        // Re-hydrate any already-loaded AI Agent nodes (handles race where workflow loaded before this response)
        setNodes(nds => nds.map(n => {
          if (n.data?.type !== 'ai_agent') return n;
          const tcs = n.data?.params?.tool_connections;
          if (!Array.isArray(tcs) || tcs.length === 0) return n;
          const rehydrated = tcs.map(tc => {
            const fields = map[tc.node_type];
            return fields && fields.length > 0 ? { ...tc, _node_fields: fields } : tc;
          });
          const changed = rehydrated.some((tc, i) => tc !== tcs[i]);
          if (!changed) return n;
          return { ...n, data: { ...n.data, params: { ...n.data.params, tool_connections: rehydrated } } };
        }));
      })
      .catch(() => {});
  }, [setNodes]);

  // Validate embed token on mount if present
  useEffect(() => {
    if (!embedToken) return;
    axios.get(`${API_BASE_URL}/embed/validate/${embedToken}`).then(res => {
      setEmbedSession(res.data);
      // Apply theme overrides from session
      if (res.data.theme_overrides) {
        Object.entries(res.data.theme_overrides).forEach(([k, v]) => {
          document.documentElement.style.setProperty(k, v);
        });
      }
    }).catch(() => {
      console.warn('Invalid or expired embed token');
    });
  }, [embedToken]);

  const [showLogin, setShowLogin] = useState(false);
  const [showDocs, setShowDocs] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [schedulerTimezoneMeta, setSchedulerTimezoneMeta] = useState(null);
  
  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/users/me`);
      setIsAdmin(res.data.is_admin);
      setCurrentUser(res.data);
    } catch (error) {
      console.error("Failed to fetch user", error);
    }
  };

  const fetchSchedulerTimezone = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/system/scheduler-timezone`);
      setSchedulerTimezoneMeta(res.data);
    } catch (error) {
      const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Local';
      setSchedulerTimezoneMeta({
        timezone: clientTimezone,
        utc_offset_minutes: -new Date().getTimezoneOffset(),
        source: 'client_fallback'
      });
    }
  };
  
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
      fetchSchedulerTimezone();
      // Register Web Push subscription (§4.71)
      _registerPushSubscription(token);
    } else {
      delete axios.defaults.headers.common['Authorization'];
      setIsAdmin(false);
      setSchedulerTimezoneMeta(null);
    }
  }, [token]);

  // Handle xavvy Forms designer navigation from NodeConfigPanel picker
  useEffect(() => {
    const handler = (e) => {
      const formId = e.detail?.formId;
      if (formId) {
        setReturnView(currentViewRef.current);
        setEditingFormId(formId);
        setView('form_designer');
      }
    };
    window.addEventListener('xavvy:open-form-designer', handler);
    return () => window.removeEventListener('xavvy:open-form-designer', handler);
  }, []);

  async function _registerPushSubscription(authToken) {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
      const reg = await navigator.serviceWorker.ready;
      const keyRes = await axios.get(`${API_BASE_URL}/push/vapid-public-key`, { headers: { Authorization: `Bearer ${authToken}` } });
      const vapidPublicKey = keyRes.data?.public_key;
      if (!vapidPublicKey) return;
      // Convert VAPID key from base64url to Uint8Array
      const padding = '='.repeat((4 - (vapidPublicKey.length % 4)) % 4);
      const base64 = (vapidPublicKey + padding).replace(/-/g, '+').replace(/_/g, '/');
      const rawKey = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
      const subscription = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: rawKey });
      await axios.post(`${API_BASE_URL}/push/subscribe`, { subscription }, { headers: { Authorization: `Bearer ${authToken}` } });
    } catch (_) {
      // Push not available or user denied — silently ignore
    }
  }

  useEffect(() => {
    if (!token) {
      setNodeCatalog([]);
      return;
    }
    axios.get(`${API_BASE_URL}/nodes`)
      .then((res) => {
        setNodeCatalog(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        setNodeCatalog([]);
      });
  }, [token]);

  const handleLogin = async (newToken) => {
    setToken(newToken);
    // If the user came from /monitor deep-link, go there after login
    if (window.location.pathname === '/monitor') {
      setView('mobile_monitor');
      return;
    }
    // If the user came from a /case-client deep-link, go there
    if (window.location.pathname.startsWith('/case-client')) {
      setView('case_client');
      return;
    }
    // Worker-only routing rule: if the user has zero admin/editor workspace
    // memberships and isn't a system admin, send them straight to the Case
    // Client instead of the studio. They can still leave manually.
    try {
      const headers = { Authorization: `Bearer ${newToken}` };
      const meRes = await axios.get(`${API_BASE_URL}/users/me`, { headers });
      const isSystemAdmin = !!meRes.data?.is_admin;
      if (!isSystemAdmin) {
        const wsRes = await axios.get(`${API_BASE_URL}/workspaces/`, { headers, params: { member_only: true } });
        const ws = Array.isArray(wsRes.data) ? wsRes.data : [];
        const hasNonViewerStudioRole = ws.some(w => w.my_role === 'admin' || w.my_role === 'editor');
        if (ws.length > 0 && !hasNonViewerStudioRole) {
          setView('case_client');
          return;
        }
      }
    } catch (_) {
      // Fall through to default routing on any error.
    }
    setView('home');
  };

  const handleLogout = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/auth/logout`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const idpLogoutUrl = res.data?.logout_url;
      localStorage.removeItem('token');
      setToken(null);
      setWorkflowId(null);
      setView('home');
      window.location.href = idpLogoutUrl || '/';
    } catch {
      localStorage.removeItem('token');
      setToken(null);
      setWorkflowId(null);
      setView('home');
      window.location.href = '/';
    }
  };

  const [view, setView] = useState(() => {
    // Deep-link: /monitor (PWA start_url) → go straight to Run Monitor if already logged in
    if (localStorage.getItem('token') && window.location.pathname === '/monitor') return 'mobile_monitor';
    // Deep-link: /case-client[/<workspaceId>] → go straight to the worker shell if logged in
    if (localStorage.getItem('token') && window.location.pathname.startsWith('/case-client')) return 'case_client';
    return 'home';
  });

  // Parse a workspace id out of the URL on first land so the bootstrap picks
  // the right one when the user shared a deep link.
  const [caseClientInitialWorkspaceId] = useState(() => {
    const m = window.location.pathname.match(/^\/case-client\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  });
  useEffect(() => {
    currentViewRef.current = view;
  }, [view]);

  // Forms-only mode: redirect home view to forms list (placed after view useState to avoid TDZ)
  useEffect(() => {
    if (isFormsOnly && view === 'home' && token) {
      setView('xavvy_forms');
    }
  }, [isFormsOnly, view, token]);

  // Case navigation target (set from notification clicks)
  const [caseNavTarget, setCaseNavTarget] = useState(null); // { case_id, work_item_id }

  const [workflowId, setWorkflowId] = useState(null);
  const [workflowName, setWorkflowName] = useState("Untitled Workflow");
  const [workflowNameTouched, setWorkflowNameTouched] = useState(false);
  const [executionResult, setExecutionResult] = useState(null);
  const [nodeStatus, setNodeStatus] = useState({}); // { nodeId: { status, output } }
  const [validationWarnings, setValidationWarnings] = useState([]); // advisory warnings from pre-run validator
  const [isSaving, setIsSaving] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [showTriggerPicker, setShowTriggerPicker] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showMetrics, setShowMetrics] = useState(false);
  const [showProcessIntel, setShowProcessIntel] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [showVariableInspector, setShowVariableInspector] = useState(false);
  const [showWorkflowEnvVars, setShowWorkflowEnvVars] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [copilotState, setCopilotState] = useState({ isOpen: false, mode: 'explain', runId: null });
  const [showCopilotChat, setShowCopilotChat] = useState(false);
  const [cronSchedule, setCronSchedule] = useState("");
  const [isPaused, setIsPaused] = useState(false);
  const [workflowMaxConcurrentRuns, setWorkflowMaxConcurrentRuns] = useState(3);
  // Advanced Scheduler state (§4.50)
  const [advancedScheduler, setAdvancedScheduler] = useState({ skip_if_running: false, run_window_start: null, run_window_end: null, max_runs_per_hour: null, blackout_windows: null });
  // SLA Alerts (§4.70)
  const [slaMaxSeconds, setSlaMaxSeconds] = useState(null);
  const [runPriority, setRunPriority] = useState('normal');
  const [pinnedTriggerData, setPinnedTriggerData] = useState(null);
  const [pinnedTriggerMeta, setPinnedTriggerMeta] = useState(null);
  const [pinnedFirstNode, setPinnedFirstNode] = useState(null);
  const [nodeContextMenu, setNodeContextMenu] = useState(null); // {x, y, nodeId, nodeLabel, isPinned}
  const [edgePanel, setEdgePanel] = useState(null); // {x, y, edge, sourceNodeType}
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinModalNodeId, setPinModalNodeId] = useState(null);
  const [pinModalText, setPinModalText] = useState('');
  const [pinModalError, setPinModalError] = useState('');
  const [showAgentChat, setShowAgentChat] = useState(false);
  const [agentChatStarted, setAgentChatStarted] = useState(false);
  const [publicChatEnabled, setPublicChatEnabled] = useState(false);
  const [publicChatLink, setPublicChatLink] = useState("");
  const [publicChatLoading, setPublicChatLoading] = useState(false);
  const [publicChatToken, setPublicChatToken] = useState(null);
  const [formTriggerUrl, setFormTriggerUrl] = useState("");
  // Draft / Published state
  const [workflowPublishStatus, setWorkflowPublishStatus] = useState('draft'); // draft | review | published
  // Multi-environment
  const [currentEnvironmentId, setCurrentEnvironmentId] = useState(null);
  const [workspaceEnvironments, setWorkspaceEnvironments] = useState([]);
  const [nodeCatalog, setNodeCatalog] = useState([]);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddQuery, setQuickAddQuery] = useState('');
  const [quickAddIndex, setQuickAddIndex] = useState(0);
  const quickAddListRef = useRef(null);
  const quickAddItemRefs = useRef([]);
  const [showEnvModal, setShowEnvModal] = useState(false);
  const [showGitSyncModal, setShowGitSyncModal] = useState(false);
  const [gitSyncConfig, setGitSyncConfig] = useState(null);
  const [gitSyncDirty, setGitSyncDirty] = useState(false);
  // Real-time collaboration
  const collabWsRef = useRef(null);
  const cursorThrottleRef = useRef(0);
  const patchThrottleRef = useRef(0);
  const isDraggingRef = useRef(false);       // true while local user drags a node
  const bufferedPatchRef = useRef(null);     // patch held during local drag
  const applyRemotePatchRef = useRef(null);  // stable ref to applyRemotePatch
  const skipNextBroadcastRef = useRef(false); // suppress echo after remote apply / load
  const hasInitializedPatchRef = useRef(false); // skip first-render broadcast
  const lastBroadcastHashRef = useRef(null);    // hash of last meaningful canvas state sent

  // ── Undo / Redo ─────────────────────────────────────────────────────────
  const _MAX_HISTORY = 50;
  const undoStackRef = useRef([]);  // [{nodes, edges}, ...]
  const redoStackRef = useRef([]);

  const pushHistory = useCallback(() => {
    const snap = {
      nodes: JSON.parse(JSON.stringify(nodesRef.current)),
      edges: JSON.parse(JSON.stringify(edgesRef.current)),
    };
    undoStackRef.current = [...undoStackRef.current.slice(-(_MAX_HISTORY - 1)), snap];
    redoStackRef.current = [];
  }, []);

  const handleUndo = useCallback(() => {
    if (undoStackRef.current.length === 0) return;
    const prev = undoStackRef.current[undoStackRef.current.length - 1];
    undoStackRef.current = undoStackRef.current.slice(0, -1);
    redoStackRef.current = [...redoStackRef.current, {
      nodes: JSON.parse(JSON.stringify(nodesRef.current)),
      edges: JSON.parse(JSON.stringify(edgesRef.current)),
    }];
    skipNextBroadcastRef.current = true;
    setNodes(prev.nodes);
    setEdges(prev.edges);
    setSelectedNode(null);
    setSelectedNodes([]);
  }, [setNodes, setEdges]);

  const handleRedo = useCallback(() => {
    if (redoStackRef.current.length === 0) return;
    const next = redoStackRef.current[redoStackRef.current.length - 1];
    redoStackRef.current = redoStackRef.current.slice(0, -1);
    undoStackRef.current = [...undoStackRef.current, {
      nodes: JSON.parse(JSON.stringify(nodesRef.current)),
      edges: JSON.parse(JSON.stringify(edgesRef.current)),
    }];
    skipNextBroadcastRef.current = true;
    setNodes(next.nodes);
    setEdges(next.edges);
    setSelectedNode(null);
    setSelectedNodes([]);
  }, [setNodes, setEdges]);

  // Debounced history push for config-panel param edits — captures state once per
  // editing "burst" (first change in a 1 s window) so rapid typing doesn't flood the stack.
  const _configPanelDebounceRef = useRef(null);
  const configPanelSetNodes = useCallback((updater) => {
    if (!_configPanelDebounceRef.current) {
      pushHistory();
    }
    clearTimeout(_configPanelDebounceRef.current);
    _configPanelDebounceRef.current = setTimeout(() => {
      _configPanelDebounceRef.current = null;
    }, 1000);
    setNodes(updater);
  }, [setNodes, pushHistory]);

  const [collabUsers, setCollabUsers] = useState([]); // other users currently editing
  const [remoteCursors, setRemoteCursors] = useState({}); // {userId: {username,color,x,y}}
  const [remoteEditingNodes, setRemoteEditingNodes] = useState({}); // {userId: {nodeId,username,color}}
  const [myCollabColor, setMyCollabColor] = useState(null);
  const [conflictToast, setConflictToast] = useState(null);   // {username,color} | null
  const [lockedNodeToast, setLockedNodeToast] = useState(null); // {username,color} | null
  const [viewport, setViewport] = useState({ x: 0, y: 0, zoom: 1 });
  const [showAiMenu, setShowAiMenu] = useState(false);
  const aiMenuRef = useRef(null);
  const aiMenuDropdownRef = useRef(null);
  const [showPublishMenu, setShowPublishMenu] = useState(false);
  const publishMenuRef = useRef(null);
  const publishMenuDropdownRef = useRef(null);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef(null);
  const moreMenuDropdownRef = useRef(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);
  const userMenuDropdownRef = useRef(null);
  const [savedWorkflowSnapshot, setSavedWorkflowSnapshot] = useState(null);
  
  // Theme management
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved === 'light' || saved === 'dark') return saved;
    // Default to system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    if (!showAiMenu) return;
    const handler = (e) => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (aiMenuRef.current?.contains(t) || aiMenuDropdownRef.current?.contains(t)) return;
      setShowAiMenu(false);
    };
    document.addEventListener('pointerdown', handler, true);
    return () => document.removeEventListener('pointerdown', handler, true);
  }, [showAiMenu]);

  useEffect(() => {
    if (!showPublishMenu) return;
    const handler = (e) => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (publishMenuRef.current?.contains(t) || publishMenuDropdownRef.current?.contains(t)) return;
      setShowPublishMenu(false);
    };
    document.addEventListener('pointerdown', handler, true);
    return () => document.removeEventListener('pointerdown', handler, true);
  }, [showPublishMenu]);

  useEffect(() => {
    if (!showMoreMenu) return;
    const handler = (e) => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (moreMenuRef.current?.contains(t) || moreMenuDropdownRef.current?.contains(t)) return;
      setShowMoreMenu(false);
    };
    document.addEventListener('pointerdown', handler, true);
    return () => document.removeEventListener('pointerdown', handler, true);
  }, [showMoreMenu]);

  useEffect(() => {
    if (!showUserMenu) return;
    const handler = (e) => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (userMenuRef.current?.contains(t) || userMenuDropdownRef.current?.contains(t)) return;
      setShowUserMenu(false);
    };
    document.addEventListener('pointerdown', handler, true);
    return () => document.removeEventListener('pointerdown', handler, true);
  }, [showUserMenu]);

  useEffect(() => {
    const match = window.location.pathname.match(/^\/public\/chat\/([^/]+)$/);
    if (match) {
      setPublicChatToken(match[1]);
    }
  }, []);

  const currentWorkflowSnapshot = useMemo(
    () => buildWorkflowSnapshot(workflowName, nodes, edges, cronSchedule, isPaused, workflowMaxConcurrentRuns),
    [workflowName, nodes, edges, cronSchedule, isPaused, workflowMaxConcurrentRuns]
  );
  const hasUnsavedChanges = useMemo(() => {
    if (view !== 'editor') return false;
    if (!workflowId) return true;
    if (!savedWorkflowSnapshot) return true;
    return currentWorkflowSnapshot !== savedWorkflowSnapshot;
  }, [view, workflowId, savedWorkflowSnapshot, currentWorkflowSnapshot]);
  
  const cycleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const getThemeIcon = () => {
    return theme === 'dark' ? '🌙' : '☀️';
  };

  // const [showWorkflowList, setShowWorkflowList] = useState(false); // Removed in favor of 'view'
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflowIds, setSelectedWorkflowIds] = useState([]);

  const [returnView, setReturnView] = useState('home');
  const [editorReturnView, setEditorReturnView] = useState('flow');
  const [initialCredentialServiceType, setInitialCredentialServiceType] = useState(null);
  const [initialCredentialId, setInitialCredentialId] = useState(null);
  
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [allWorkspaces, setAllWorkspaces] = useState([]);
  const [showWsDropdown, setShowWsDropdown] = useState(false);
  const wsDropdownRef = React.useRef(null);

  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showBulkMoveModal, setShowBulkMoveModal] = useState(false);
  const [workflowToMove, setWorkflowToMove] = useState(null);
  const [heroFormCount, setHeroFormCount] = useState(0);
  const [heroCaseTypeCount, setHeroCaseTypeCount] = useState(0);
  const [heroDocuforgeCount, setHeroDocuforgeCount] = useState(0);

  // Folder state
  const [folders, setFolders] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null); // null = show all
  const [showFolderForm, setShowFolderForm] = useState(false);
  const [folderFormName, setFolderFormName] = useState('');
  const [editingFolder, setEditingFolder] = useState(null);
  const [showMoveToFolderModal, setShowMoveToFolderModal] = useState(false);
  const [workflowToMoveToFolder, setWorkflowToMoveToFolder] = useState(null);
  const [editingFormId, setEditingFormId] = useState(null); // xavvy Forms designer
  const [showWorkflowGeneratorModal, setShowWorkflowGeneratorModal] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [mobileWfSheet, setMobileWfSheet] = useState(null); // workflow object for bottom sheet
  const [showMobileFolderPicker, setShowMobileFolderPicker] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [commandPaletteQuery, setCommandPaletteQuery] = useState('');
  const [commandPaletteResults, setCommandPaletteResults] = useState(null);
  const [commandPaletteLoading, setCommandPaletteLoading] = useState(false);
  const [commandPaletteError, setCommandPaletteError] = useState('');
  const [commandPaletteIndex, setCommandPaletteIndex] = useState(-1);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [showWorkflowStartModal, setShowWorkflowStartModal] = useState(false);
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStepIndex, setTutorialStepIndex] = useState(0);
  const [showLicenseManager, setShowLicenseManager] = useState(false);
  const [disableTutorialAutoShow, setDisableTutorialAutoShow] = useState(false);
  const [tutorialAutoCheckDone, setTutorialAutoCheckDone] = useState(false);

  const tutorialPreferenceKey = currentUser?.id ? `xavvy:tutorial:auto_disabled:${currentUser.id}` : null;
  const tutorialCompletedKey = currentUser?.id ? `xavvy:tutorial:completed:${currentUser.id}` : null;
  const isDemoUser = (currentUser?.username || '').trim().toLowerCase() === 'demo';

  const onMoveClick = (e, workflow) => {
    e.stopPropagation();
    setWorkflowToMove(workflow);
    setShowMoveModal(true);
  };

  const onMoveWorkflow = async (workflowId, targetWorkspaceId) => {
      try {
          await axios.post(`${API_BASE_URL}/workflows/${workflowId}/move`, {
              target_workspace_id: targetWorkspaceId
          });
          setShowMoveModal(false);
          setWorkflowToMove(null);
          fetchWorkflows(); // Refresh list
      } catch (error) {
          console.error("Failed to move workflow", error);
          alert("Failed to move workflow");
      }
  };

  // --- Folder handlers ---
  const handleCreateFolder = async () => {
    const name = folderFormName.trim();
    if (!name) return;
    try {
      const payload = { name, workspace_id: currentWorkspace?.id || null };
      await axios.post(`${API_BASE_URL}/folders/`, payload);
      setFolderFormName('');
      setShowFolderForm(false);
      fetchFolders();
    } catch (error) {
      alert('Failed to create folder: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleRenameFolder = async () => {
    if (!editingFolder) return;
    const name = folderFormName.trim();
    if (!name) return;
    try {
      await axios.put(`${API_BASE_URL}/folders/${editingFolder.id}`, { name, workspace_id: editingFolder.workspace_id });
      setFolderFormName('');
      setEditingFolder(null);
      fetchFolders();
    } catch (error) {
      alert('Failed to rename folder: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!(await window.confirm('Delete this folder? Workflows inside will be moved to root.'))) return;
    try {
      await axios.delete(`${API_BASE_URL}/folders/${folderId}`);
      if (currentFolderId === folderId) setCurrentFolderId(null);
      fetchFolders();
      fetchWorkflows();
    } catch (error) {
      alert('Failed to delete folder: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleMoveWorkflowToFolder = async (workflowId, folderId) => {
    try {
      await axios.patch(`${API_BASE_URL}/workflows/${workflowId}/folder`, null, {
        params: { folder_id: folderId || undefined }
      });
      setShowMoveToFolderModal(false);
      setWorkflowToMoveToFolder(null);
      fetchWorkflows();
    } catch (error) {
      alert('Failed to move workflow: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleBulkMoveToFolder = async (folderId) => {
    try {
      await axios.post(`${API_BASE_URL}/bulk/workflows/folder`, {
        workflow_ids: selectedWorkflowIds,
        folder_id: folderId || null
      });
      setShowMoveToFolderModal(false);
      setWorkflowToMoveToFolder(null);
      fetchWorkflows();
    } catch (error) {
      alert('Failed to bulk move: ' + (error.response?.data?.detail || error.message));
    }
  };

  const canMoveOrDeleteWorkflow = useCallback((workflow) => {
    return Boolean(currentUser?.is_admin || currentWorkspace?.my_role === 'admin' || workflow?.owner_id === currentUser?.id);
  }, [currentUser, currentWorkspace]);

  const canPauseResumeWorkflow = useCallback((workflow) => {
    if (currentUser?.is_admin) return true;
    if (workflow?.workspace_id) {
      return currentWorkspace?.my_role !== 'viewer';
    }
    return workflow?.owner_id === currentUser?.id;
  }, [currentUser, currentWorkspace]);

  const toggleWorkflowSelection = useCallback((e, workflowId) => {
    e.stopPropagation();
    setSelectedWorkflowIds(prev => prev.includes(workflowId) ? prev.filter(id => id !== workflowId) : [...prev, workflowId]);
  }, []);

  const toggleSelectAllWorkflows = useCallback(() => {
    if (workflows.length === 0) {
      setSelectedWorkflowIds([]);
      return;
    }
    const allSelected = workflows.every(wf => selectedWorkflowIds.includes(wf.id));
    if (allSelected) {
      setSelectedWorkflowIds([]);
    } else {
      setSelectedWorkflowIds(workflows.map(wf => wf.id));
    }
  }, [workflows, selectedWorkflowIds]);

  const handleBackToHome = useCallback(async () => {
    if (hasUnsavedChanges) {
      const shouldLeave = await window.confirm("You have unsaved changes. Leave editor without saving?");
      if (!shouldLeave) {
        return;
      }
    }
    const target = editorReturnView || 'flow';
    setView(target);
    if (target === 'flow') {
      fetchWorkflows();
      fetchFolders();
    }
  }, [hasUnsavedChanges, editorReturnView]);

  const applyGeneratedWorkflowToCanvas = ({ name, definition }) => {
    const normalized = normalizeWorkflowDefinitionForCanvas(definition || { nodes: [], edges: [] });
    const generatedNodes = normalized.nodes;
    const generatedEdges = normalized.edges;
    setWorkflowName(name || 'Generated Workflow');
    setNodes(generatedNodes);
    setEdges(generatedEdges);
    setSelectedNode(null);
    setSelectedNodes([]);
    syncNextId(generatedNodes);
    setShowWorkflowGeneratorModal(false);
  };

  const openTutorial = (stepIndex = 0) => {
    const disabled = isDemoUser
      ? false
      : tutorialPreferenceKey ? localStorage.getItem(tutorialPreferenceKey) === '1' : false;
    setDisableTutorialAutoShow(disabled);
    setWorkflowNameTouched(false);
    setTutorialStepIndex(stepIndex);
    setShowTutorial(true);
  };

  const closeTutorial = (completed = false) => {
    if (tutorialPreferenceKey && !isDemoUser) {
      localStorage.setItem(tutorialPreferenceKey, disableTutorialAutoShow ? '1' : '0');
    }
    if (completed && tutorialCompletedKey && !isDemoUser) {
      localStorage.setItem(tutorialCompletedKey, '1');
    }
    setShowTutorial(false);
  };

  // In embed mode, restrict the node palette to allowed_node_types from the session
  const effectiveNodeCatalog = useMemo(() => {
    if (!embedSession?.allowed_node_types) return Array.isArray(nodeCatalog) ? nodeCatalog : [];
    const allowed = new Set(embedSession.allowed_node_types);
    return (Array.isArray(nodeCatalog) ? nodeCatalog : []).filter(n => allowed.has(n.type));
  }, [nodeCatalog, embedSession]);

  const quickAddNodes = useMemo(() => {
    const query = quickAddQuery.trim().toLowerCase();
    const source = effectiveNodeCatalog;
    const filtered = query
      ? source.filter((node) => {
          const tags = Array.isArray(node.tags) ? node.tags.join(' ') : '';
          return `${node.label || ''} ${node.type || ''} ${node.category || ''} ${tags}`.toLowerCase().includes(query);
        })
      : source;
    return filtered.slice(0, 40);
  }, [effectiveNodeCatalog, quickAddQuery]);

  const triggerNodeTypes = useMemo(() => {
    const triggerTypes = new Set(['webhook', 'cron']);
    const source = Array.isArray(nodeCatalog) ? nodeCatalog : [];
    source.forEach((nodeMeta) => {
      const nodeType = String(nodeMeta?.type || '').trim();
      if (!nodeType) {
        return;
      }
      const category = String(nodeMeta?.category || '').trim().toLowerCase();
      if (category === 'triggers' || category.includes('trigger')) {
        triggerTypes.add(nodeType);
      }
    });
    return triggerTypes;
  }, [nodeCatalog]);

  useEffect(() => {
    setQuickAddIndex(0);
  }, [quickAddQuery, showQuickAdd]);

  useEffect(() => {
    if (!showQuickAdd) {
      return;
    }
    const listElement = quickAddListRef.current;
    const activeItem = quickAddItemRefs.current[quickAddIndex];
    if (!listElement || !activeItem) {
      return;
    }
    const itemTop = activeItem.offsetTop;
    const itemBottom = itemTop + activeItem.offsetHeight;
    const viewportTop = listElement.scrollTop;
    const viewportBottom = viewportTop + listElement.clientHeight;
    if (itemTop < viewportTop) {
      listElement.scrollTop = itemTop;
      return;
    }
    if (itemBottom > viewportBottom) {
      listElement.scrollTop = itemBottom - listElement.clientHeight;
    }
  }, [showQuickAdd, quickAddIndex, quickAddNodes]);

  const insertNodeFromQuickAdd = (node) => {
    if (!node || !reactFlowInstance) {
      return;
    }
    const vp = reactFlowInstance.getViewport();
    const position = {
      x: (-vp.x + window.innerWidth * 0.45) / vp.zoom,
      y: (-vp.y + window.innerHeight * 0.35) / vp.zoom,
    };
    addNodeAtPosition(node.type, node.label || node.type, node.icon || null, position);
    setShowQuickAdd(false);
    setQuickAddQuery('');
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (showQuickAdd) {
        if (event.key === 'Escape') {
          event.preventDefault();
          setShowQuickAdd(false);
          setQuickAddQuery('');
          return;
        }
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          setQuickAddIndex((prev) => Math.min(prev + 1, Math.max(quickAddNodes.length - 1, 0)));
          return;
        }
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          setQuickAddIndex((prev) => Math.max(prev - 1, 0));
          return;
        }
        if (event.key === 'Enter') {
          event.preventDefault();
          if (quickAddNodes.length > 0) {
            insertNodeFromQuickAdd(quickAddNodes[quickAddIndex] || quickAddNodes[0]);
          }
          return;
        }
      }

      if (['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName) || document.activeElement.isContentEditable) {
        return;
      }

      if (view === 'editor' && !showQuickAdd && (event.key === '/' || event.key === ' ')) {
        event.preventDefault();
        setShowQuickAdd(true);
        return;
      }

      if (view === 'editor' && event.key === '?') {
        event.preventDefault();
        setShowShortcutsModal(true);
        return;
      }

      if (event.ctrlKey || event.metaKey) {
        if (event.key.toLowerCase() === 'k') {
          event.preventDefault();
          if (view === 'editor') {
            setShowQuickAdd(true);
          } else {
            setShowCommandPalette(true);
          }
          return;
        }
      }

      if (event.ctrlKey || event.metaKey) {
        if (event.key === 'z' && !event.shiftKey) {
          event.preventDefault();
          handleUndo();
          return;
        }
        if ((event.key === 'y') || (event.key === 'z' && event.shiftKey)) {
          event.preventDefault();
          handleRedo();
          return;
        }
      }

      if (event.ctrlKey || event.metaKey) {
        if (event.key === 'c') {
          if (selectedNodes.length > 0) {
            setCopiedNodes(JSON.parse(JSON.stringify(selectedNodes)));
          }
        } else if (event.key === 'v') {
          if (copiedNodes.length > 0) {
            pushHistory();
            const offset = 50;
            const newNodes = copiedNodes.map(node => {
                const newNodeId = getId();
                return {
                    ...node,
                    id: newNodeId,
                    position: {
                        x: node.position.x + offset,
                        y: node.position.y + offset
                    },
                    selected: true,
                    data: {
                        ...node.data,
                        label: node.data.label ? `${node.data.label} (Copy)` : 'Node (Copy)'
                    }
                };
            });
            
            setNodes((nds) => nds.map(n => ({...n, selected: false})).concat(newNodes));
            setSelectedNodes(newNodes);
            if (newNodes.length === 1) setSelectedNode(newNodes[0]);
            
            setCopiedNodes(copiedNodes.map(node => ({
                ...node,
                position: {
                    x: node.position.x + offset,
                    y: node.position.y + offset
                }
            })));
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedNodes, copiedNodes, setNodes, setSelectedNodes, setSelectedNode, showQuickAdd, quickAddNodes, quickAddIndex, insertNodeFromQuickAdd, view, handleUndo, handleRedo]);

  // When nodeStatus updates, we can update the nodes visualization
  useEffect(() => {
    setNodes((nds) => nds.map((node) => {
        const status = nodeStatus[node.id];

        const newStyle = { ...node.style };
        // Clean up previous style overrides if any
        if (newStyle.border) delete newStyle.border;
        if (newStyle.boxShadow) delete newStyle.boxShadow;

        return {
            ...node,
            data: {
                ...node.data,
                status: status ? status.status : null,
                credential_reauth: status?.credential_reauth || null,
            },
            style: newStyle
        };
    }));
  }, [nodeStatus, setNodes]);

  const fetchFolders = useCallback(async () => {
    if (!token) return;
    try {
      const params = {};
      if (currentWorkspace) params.workspace_id = currentWorkspace.id;
      const res = await axios.get(`${API_BASE_URL}/folders/`, { params });
      setFolders(res.data);
    } catch (error) {
      console.error("Failed to fetch folders", error);
    }
  }, [token, currentWorkspace]);

  const fetchWorkflows = useCallback(async () => {
    if (!token) return;
    try {
      // Ensure header is set before request
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const params = {};
      if (currentWorkspace) {
        params.workspace_id = currentWorkspace.id;
      }
      const res = await axios.get(`${API_BASE_URL}/workflows/`, { params });
      setWorkflows(res.data);
      setSelectedWorkflowIds(prev => prev.filter(id => res.data.some(wf => wf.id === id)));
    } catch (error) {
      console.error("Failed to fetch workflows", error);
      if (error.response && error.response.status === 401) {
          handleLogout();
      }
    }
  }, [token, currentWorkspace]);

  const fetchWorkspaceEnvironments = useCallback(async () => {
    if (!token) { setWorkspaceEnvironments([]); return; }
    try {
      const url = currentWorkspace
        ? `${API_BASE_URL}/workspaces/${currentWorkspace.id}/environments`
        : `${API_BASE_URL}/users/me/environments`;
      const res = await axios.get(url);
      setWorkspaceEnvironments(res.data);
    } catch (error) {
      setWorkspaceEnvironments([]);
    }
  }, [token, currentWorkspace]);

  useEffect(() => {
    if ((view === 'home' || view === 'flow') && token && !isFormsOnly) {
      fetchWorkflows();
      fetchFolders();
    }
  }, [view, token, fetchWorkflows, fetchFolders, isFormsOnly]);

  // Fetch hero card counts (forms + case types) for the home dashboard
  useEffect(() => {
    if (view !== 'home' || !token || !currentWorkspace) return;
    axios.get(`${API_BASE_URL}/xavvy-forms`, { params: { workspace_id: currentWorkspace.id } })
      .then(res => setHeroFormCount(Array.isArray(res.data) ? res.data.length : 0))
      .catch(() => setHeroFormCount(0));
    if (licenseInfo && !licenseInfo.locked_features?.includes('casematrix.case_management')) {
      axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${currentWorkspace.id}/case-types`)
        .then(res => setHeroCaseTypeCount(Array.isArray(res.data) ? res.data.length : 0))
        .catch(() => setHeroCaseTypeCount(0));
    }
    axios.get(`${API_BASE_URL}/api/docuforge/workspaces/${currentWorkspace.id}/documents`, { params: { limit: 1 } })
      .then(res => {
        const data = res.data;
        const total = (data && typeof data.total === 'number') ? data.total
          : (Array.isArray(data) ? data.length
          : (Array.isArray(data?.documents) ? data.documents.length : 0));
        setHeroDocuforgeCount(total);
      })
      .catch(() => setHeroDocuforgeCount(0));
  }, [view, token, currentWorkspace?.id]);

  // Fetch all workspaces and auto-select default on login
  const fetchAllWorkspaces = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/workspaces/`, { params: { member_only: true } });
      const list = res.data || [];
      setAllWorkspaces(list);
      return list;
    } catch { return []; }
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchAllWorkspaces().then(list => {
      if (!list || list.length === 0) return;
      if (!currentWorkspace) {
        // Pick default workspace (is_default=true) or first
        const def = list.find(w => w.is_default) || list[0];
        setCurrentWorkspace(def);
      }
    });
  }, [token]);

  // Auto-migrate personal content to default workspace on first visit
  useEffect(() => {
    if (!token || !currentUser) return;
    const migrationKey = `xavvy:migrated:${currentUser.id}`;
    if (localStorage.getItem(migrationKey)) return;
    axios.post(`${API_BASE_URL}/migrate/personal-to-workspace`)
      .then(res => {
        localStorage.setItem(migrationKey, '1');
        if (res.data.default_workspace_id && !currentWorkspace) {
          fetchAllWorkspaces();
        }
      })
      .catch(() => {});
  }, [token, currentUser]);

  // Close workspace dropdown on click outside
  useEffect(() => {
    const handler = (e) => {
      if (wsDropdownRef.current && !wsDropdownRef.current.contains(e.target)) {
        setShowWsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    fetchWorkspaceEnvironments();
  }, [fetchWorkspaceEnvironments]);

  const fetchGitSyncConfig = useCallback(async () => {
    if (!token) { setGitSyncConfig(null); setGitSyncDirty(false); return; }
    try {
      const url = currentWorkspace
        ? `${API_BASE_URL}/workspaces/${currentWorkspace.id}/git-sync`
        : `${API_BASE_URL}/users/me/git-sync`;
      const res = await axios.get(url);
      const cfg = res.data || null;
      setGitSyncConfig(cfg);
      if (cfg) {
        try {
          const diffRes = await axios.get(`${url}/diff`);
          const diffs = Array.isArray(diffRes.data) ? diffRes.data : (diffRes.data?.diffs || []);
          setGitSyncDirty(diffs.some(d => d.status !== 'in_sync'));
        } catch {
          setGitSyncDirty(false);
        }
      } else {
        setGitSyncDirty(false);
      }
    } catch {
      setGitSyncConfig(null);
      setGitSyncDirty(false);
    }
  }, [token, currentWorkspace]);

  useEffect(() => {
    fetchGitSyncConfig();
  }, [fetchGitSyncConfig]);

  useEffect(() => {
    if (!token || !currentUser?.id || tutorialAutoCheckDone || publicChatToken) {
      return;
    }
    const disabled = isDemoUser ? false : localStorage.getItem(`xavvy:tutorial:auto_disabled:${currentUser.id}`) === '1';
    const completed = isDemoUser ? false : localStorage.getItem(`xavvy:tutorial:completed:${currentUser.id}`) === '1';
    setDisableTutorialAutoShow(disabled);
    if (isDemoUser || (!disabled && !completed)) {
      setTutorialStepIndex(0);
      setShowTutorial(true);
    }
    setTutorialAutoCheckDone(true);
  }, [token, currentUser, tutorialAutoCheckDone, publicChatToken, isDemoUser]);

  useEffect(() => {
    if (!token) {
      setTutorialAutoCheckDone(false);
      setShowTutorial(false);
    }
  }, [token]);

  useEffect(() => {
    if (!currentUser?.id) {
      return;
    }
    setTutorialAutoCheckDone(false);
  }, [currentUser?.id]);

  const tutorialSteps = useMemo(() => [
    // ── Home view ──────────────────────────────────────────────────────────
    {
      id: 'home-workspaces',
      view: 'home',
      targetId: 'tutorial-home-workspaces',
      title: 'Home: Workspaces',
      description: 'Switch between your personal scope and shared team workspaces.',
      bullets: [
        'Personal workspace: only you can see it, no role restrictions.',
        'Team workspaces have RBAC roles: admin, editor, and viewer.',
        'Organize workflows into folders — create and move them from the workflow list.',
        'Workspace changes update the visible workflow list and credentials.'
      ]
    },
    {
      id: 'home-credentials',
      view: 'home',
      targetId: 'tutorial-home-credentials',
      title: 'Home: Credentials',
      description: 'Securely store API keys, OAuth tokens, and database passwords.',
      bullets: [
        'Credentials are encrypted at rest and never embedded in workflows.',
        'Reference them in node config — resolved at run-time only.',
        'Use "Test Connection" to verify before attaching to a workflow.',
        'OAuth apps (client ID + secret) are registered here and used by OAuth credential types.'
      ]
    },
    {
      id: 'home-profile',
      view: 'home',
      targetId: 'tutorial-home-profile',
      title: 'Home: Profile & Settings',
      description: 'Manage your account, generate API keys, and update preferences.',
      bullets: [
        'Create long-lived API keys (mf_live_…) for programmatic access.',
        'Change your password and review active sessions.'
      ]
    },
    {
      id: 'home-audit-logs',
      view: 'home',
      targetId: 'tutorial-home-audit-logs',
      title: 'Home: Audit Logs',
      description: 'A full record of every create, update, delete, run, and AI action.',
      bullets: [
        'Useful for governance, compliance, and debugging.',
        'Filterable by action type and exportable as CSV.'
      ]
    },
    {
      id: 'home-extras',
      view: 'home',
      title: 'Home: More Tools (User Menu)',
      description: 'The user avatar menu gives access to platform-wide tools beyond credentials and profile.',
      bullets: [
        '🌐 Global Variables — key-value pairs injected into every run\'s context; optionally encrypted.',
        '🗄️ Data Store — a persistent key-value store your workflows can read and write at run-time.',
        '📅 Schedule Calendar — a calendar view of all upcoming scheduled workflow runs across workspaces.',
        '🔗 Webhooks — the Webhook Dashboard where you can inspect, filter, and replay every inbound webhook event.',
        '⎇ Git Sync — connect a GitHub/GitLab repo to push or pull workflow definitions as JSON files.'
      ]
    },
    {
      id: 'home-refresh',
      view: 'home',
      targetId: 'tutorial-home-refresh',
      title: 'Home: Refresh',
      description: 'Reload the workflow list to see the latest state.',
      bullets: [
        'Handy when a teammate just created or moved workflows.'
      ]
    },
    {
      id: 'home-theme',
      view: 'home',
      targetId: 'tutorial-home-theme',
      title: 'Home: Theme Toggle',
      description: 'Cycle through dark, light, and system themes.',
      bullets: [
        'Applies immediately across the entire app.',
        'Also available in the editor toolbar.'
      ]
    },
    {
      id: 'home-new-workflow',
      view: 'home',
      targetId: 'tutorial-home-new-workflow',
      title: 'Create Your First Workflow',
      description: 'Click + New Workflow to open the visual canvas editor.',
      bullets: [
        'You can also start from a template or import a .xavvy bundle.',
        'A blank canvas opens ready for nodes and connections.'
      ],
      actionHint: 'Click + New Workflow to continue.'
    },
    // ── Editor toolbar ─────────────────────────────────────────────────────
    {
      id: 'toolbar-back',
      view: 'editor',
      targetId: 'tutorial-toolbar-back',
      title: 'Toolbar: Back to Dashboard',
      description: 'Return to the workflow list without losing your canvas state.',
      bullets: [
        'Unsaved changes stay in memory until you navigate away or refresh.',
        'The workflow is not deleted — it just stays where you left it.'
      ]
    },
    {
      id: 'toolbar-ai-menu',
      view: 'editor',
      targetId: 'tutorial-toolbar-generate',
      title: 'Toolbar: AI Actions (✨ AI)',
      description: 'All AI-assisted features — and keyboard shortcuts — live in this dropdown.',
      bullets: [
        '✨ Generate Workflow — describe what you want in plain English; AI drafts the graph.',
        '💡 Explain Workflow — get a plain-English summary of what the workflow does.',
        '🧪 Test Inputs — generate realistic test payloads to validate execution paths.',
        '⌨ Keyboard Shortcuts — view all canvas and toolbar shortcuts (also in this menu).',
        '🤖 Agent Mode — open a live chat panel (requires a Chat Trigger node in the workflow).',
        '🔗 Public Link — share a chat workflow with external users without login.',
        '📋 Form URL — auto-appears when your workflow has a Form Trigger node; copy the public intake form URL.'
      ]
    },
    {
      id: 'toolbar-run',
      view: 'editor',
      targetId: 'tutorial-editor-run-workflow',
      title: 'Toolbar: Run',
      description: 'Execute the workflow from its trigger nodes.',
      bullets: [
        'When a workflow has multiple trigger nodes a ▾ chevron appears — click it to choose which trigger to start from.',
        'Nodes update in real time: amber = running, green = done, red = failed.',
        'Click any completed node to inspect its output in the config panel.',
        'Requires a saved workflow.'
      ]
    },
    {
      id: 'toolbar-save',
      view: 'editor',
      targetId: 'tutorial-editor-save-workflow',
      title: 'Toolbar: Save',
      description: 'Persist the workflow definition and name to the database.',
      bullets: [
        'First save creates a workflow ID and version 1.',
        'Every subsequent save stores a version snapshot (up to 50 versions).',
        'An unsaved-changes dot (●) appears in the name bar when there are pending edits.'
      ]
    },
    {
      id: 'toolbar-icon-strip',
      view: 'editor',
      targetId: 'tutorial-toolbar-icon-strip',
      title: 'Toolbar: Icon Strip',
      description: 'Quick-access icons for workflow tools. Less-used tools are consolidated in the ··· overflow menu to keep the toolbar compact.',
      bullets: [
        '⏰ Schedule — set a cron expression for automatic runs.',
        '📜 History — browse past runs with per-node outputs, step-through debugger, and Resume from Checkpoint.',
        '⛶ Selection Mode — toggle drag-to-pan vs drag-to-box-select on the canvas.',
        '··· More — opens an overflow menu with: ≡ Steps/Canvas view toggle, 🔍 Variable Inspector, 📊 Metrics & Alerting, 🕘 Versions, ⚙ Workflow Settings, and 📑 Duplicate Workflow.'
      ]
    },
    {
      id: 'toolbar-publish',
      view: 'editor',
      title: 'Publish Lifecycle',
      description: 'Use the Publish button (next to Save) to manage the workflow lifecycle.',
      bullets: [
        'Draft: all saves land here. Manual runs always use the latest draft.',
        'Click Publish → Submit for Review to request admin approval (workspace workflows).',
        'Admins see Approve & Publish or Reject Review options in the same dropdown.',
        'Personal workspace: publish directly with no review step required.',
        'Unpublish: returns to Draft and removes automatic triggers.'
      ]
    },
    {
      id: 'toolbar-envs',
      view: 'editor',
      title: 'Environments',
      description: 'Run the same workflow against different credential sets without editing its definition.',
      bullets: [
        '🌐 Open Environments from the account menu (top-right) — available to all users for personal workspace, admins for team workspaces.',
        'Create Dev, Staging, and Production environments — each maps credential IDs to env-specific ones.',
        'Select the active environment from the ··· (More) menu in the icon strip — it appears at the top of the menu when environments exist.',
        'Run records are tagged with the environment name for easy filtering in History.'
      ]
    },
    // ── Canvas ─────────────────────────────────────────────────────────────
    {
      id: 'editor-sidebar',
      view: 'editor',
      targetId: 'tutorial-editor-sidebar',
      title: 'Node Palette',
      description: 'Drag nodes from here onto the canvas to build your workflow.',
      bullets: [
        'Nodes are grouped by category: Triggers, Logic, Data, AI, Databases, Cloud, and more.',
        'Trigger types include Schedule, Webhook, Form (public intake form), Polling (GitHub, Jira, Slack, RSS…), Chat, and Email.',
        'Canvas Tools at the bottom lets you drop Sticky Notes and Node Groups.',
        'Custom plugin nodes appear alongside built-ins after upload via Plugin Governance.'
      ]
    },
    {
      id: 'editor-config-panel',
      view: 'editor',
      targetId: 'tutorial-editor-config-panel',
      title: 'Configuration Panel',
      description: 'Click any node on the canvas to configure it here.',
      bullets: [
        "Use {{ nodes['Node Name'].field }} templates to reference upstream outputs.",
        'Use helpers.* in expressions for date math, string operations, type conversions, and array utilities — no Code node required.',
        'Select credentials by name — values are injected at run-time, never stored in the workflow.',
        'Right-click any node → Pin test data to freeze its output and test downstream nodes in isolation.'
      ]
    },
    // ── Guided actions ─────────────────────────────────────────────────────
    {
      id: 'editor-rename-workflow',
      view: 'editor',
      targetId: 'tutorial-editor-workflow-name',
      title: 'Name Your Workflow',
      description: 'Give the workflow a meaningful name before saving.',
      bullets: [
        'Click the name field in the toolbar and type the name.',
        'Names appear in the workflow list, folder tree, version history, and audit logs.'
      ],
      actionHint: 'Type your workflow name, then click Next when ready.'
    },
    {
      id: 'editor-save-workflow',
      view: 'editor',
      targetId: 'tutorial-editor-save-workflow',
      title: 'Save It',
      description: 'Persist the workflow so it can be executed and versioned.',
      bullets: [
        'Click the highlighted 💾 Save button.',
        'First save creates the workflow ID and stores version 1.'
      ],
      actionHint: 'Click Save to continue.'
    },
    {
      id: 'editor-run-workflow',
      view: 'editor',
      targetId: 'tutorial-editor-run-workflow',
      title: 'Run Your Workflow',
      description: 'Click Run to execute and watch nodes light up in real time.',
      bullets: [
        'Node overlays update live: amber while running, green on success, red on failure.',
        'Open 📜 History after the run to inspect per-node outputs and replay or resume from any node.',
        'You can continue the tutorial without running now.'
      ]
    },
    {
      id: 'complete',
      title: '🎉 Tour Complete',
      description: 'You know the essentials. Here is what to explore next.',
      bullets: [
        'Add nodes from the palette, connect them, and Save + Run.',
        'Use ✨ AI → Generate to draft a workflow from plain English.',
        'Add a Form Trigger or Polling Trigger node to start workflows automatically from external events.',
        'Open ⚙ Settings → enable checkpointing so long workflows can resume from the failure point instead of restarting.',
        'Use helpers.* in any expression field for dates, strings, and array utilities without a Code node.',
        'Open 🔗 Webhooks from the user menu to inspect and replay inbound webhook events.',
        'Set an ⏰ schedule or browse the 📅 Schedule Calendar for an overview of all upcoming runs.',
        'Use ··· (More) in the icon strip to switch canvas ↔ steps view, open the Variable Inspector, or duplicate the workflow.',
        'Replay this tutorial any time from the user avatar menu → Tutorial.'
      ]
    }
  ], []);

  const activeTutorialStep = tutorialSteps[tutorialStepIndex];
  const tutorialTargetSelector = activeTutorialStep?.targetId
    ? `[data-tutorial-id="${activeTutorialStep.targetId}"]`
    : null;
  const isTutorialStepComplete = useMemo(() => {
    if (!activeTutorialStep) {
      return true;
    }
    if (activeTutorialStep.id === 'home-new-workflow') {
      return view === 'editor';
    }
    if (activeTutorialStep.id === 'editor-rename-workflow') {
      const trimmedName = (workflowName || '').trim();
      return Boolean(trimmedName) && workflowNameTouched;
    }
    if (activeTutorialStep.id === 'editor-save-workflow') {
      return Boolean(workflowId);
    }
    return true;
  }, [activeTutorialStep, view, workflowName, workflowNameTouched, workflowId]);

  useEffect(() => {
    if (!showTutorial) {
      setWorkflowNameTouched(false);
      return;
    }
    if (activeTutorialStep?.id === 'editor-rename-workflow') {
      setWorkflowNameTouched(false);
    }
  }, [showTutorial, tutorialStepIndex, activeTutorialStep]);

  useEffect(() => {
    if (!showTutorial || !activeTutorialStep?.view) {
      return;
    }
    if (activeTutorialStep.id === 'home-new-workflow' && view === 'editor') {
      return;
    }
    // Don't let the tutorial redirect away from the Run Monitor deep-link
    if (view === 'mobile_monitor') {
      return;
    }
    if (activeTutorialStep.view !== view) {
      setView(activeTutorialStep.view);
    }
  }, [showTutorial, activeTutorialStep, view]);

  const onConnect = useCallback((params) => {
    const sourceNode = nodes.find((node) => node.id === params.source);

    // AI Agent tools handle — show config modal before creating the edge
    if (sourceNode?.data?.type === 'ai_agent' && params.sourceHandle === 'tools') {
      // history pushed in confirmToolConnect after modal
      const targetNode = nodes.find(n => n.id === params.target);
      const defaultName = (targetNode?.data?.label || targetNode?.data?.type || 'tool').toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
      setToolConnectModal({ pendingParams: params, sourceNodeId: params.source, targetNodeId: params.target, targetNodeLabel: targetNode?.data?.label || targetNode?.data?.type || params.target });
      setToolConnectName(defaultName);
      setToolConnectDesc('');
      return; // edge added after modal confirm
    }

    // §4.79 Multi-Agent Orchestration Studio — delegate handle creates a delegate_to edge
    if (params.sourceHandle === 'delegate') {
      const targetNode = nodes.find(n => n.id === params.target);
      // Only allow delegating to other AI Agent nodes
      if (targetNode?.data?.type !== 'ai_agent') {
        return; // silently reject
      }
      pushHistory();
      const edge = {
        ...params,
        type: 'delegate_to',
        data: { edge_type: 'delegate_to' },
        label: 'delegates to',
        markerEnd: { type: 'arrowclosed', color: '#8B5CF6' },
      };
      setEdges((eds) => addEdge(edge, eds));
      return;
    }

    pushHistory();
    setEdges((eds) => {
      const srcType = sourceNode?.data?.type;
      const srcData = sourceNode?.data;
      if (['if', 'loop', 'try_catch', 'switch', 'human_approval'].includes(srcType)) {
        const available = getAvailableConditions(srcType, srcData);
        const existingCount = eds.filter(e => e.source === params.source && e.data?.condition).length;
        const condition = available[existingCount % available.length] || available[0];
        const c = EDGE_CONDITION_COLORS[condition] || EDGE_CONDITION_COLORS._default;
        return addEdge({ ...params, type: 'condition', markerEnd: { type: 'arrowclosed', color: c.stroke }, label: condition, data: { condition } }, eds);
      }
      return addEdge({ ...params, markerEnd: { type: 'arrowclosed' } }, eds);
    });
  }, [setEdges, nodes, pushHistory]);

  const confirmToolConnect = useCallback(() => {
    if (!toolConnectModal) return;
    pushHistory();
    const { pendingParams, sourceNodeId, targetNodeId } = toolConnectModal;
    // OpenAI/Anthropic tool names allow [a-zA-Z0-9_-]. Normalise the user's
    // free-text label by replacing spaces with underscores and stripping only
    // truly invalid characters. The previous regex `[^a-z0-9_]` accidentally
    // stripped every uppercase letter, turning e.g. "Get Table Definition"
    // into "et_able_efinition" — which the LLM then sees as a meaningless name.
    const toolName = toolConnectName.trim().replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '') || 'tool';
    const description = toolConnectDesc.trim();
    const targetNode = nodes.find(n => n.id === targetNodeId);
    const nodeType = targetNode?.data?.type || 'unknown';

    // Create the visual edge (dashed purple via ConditionEdge)
    const edge = {
      ...pendingParams,
      type: 'condition',
      data: { edge_type: 'tool', tool_name: toolName, description, node_type: nodeType },
      label: toolName,
      markerEnd: { type: 'arrowclosed', color: '#a855f7' },
    };
    setEdges(eds => addEdge(edge, eds));

    // Sync tool_connections into AI Agent node params, including field schema for AI parameter generation
    const nodeFields = nodeFieldsMapRef.current[nodeType] || [];
    setNodes(nds => nds.map(n => {
      if (n.id !== sourceNodeId) return n;
      const existing = n.data?.params?.tool_connections || [];
      const updated = [...existing.filter(tc => tc.node_id !== targetNodeId), {
        node_id: targetNodeId,
        node_type: nodeType,
        tool_name: toolName,
        description,
        _node_fields: nodeFields,
      }];
      return { ...n, data: { ...n.data, params: { ...n.data?.params, tool_connections: updated } } };
    }));

    setToolConnectModal(null);
  }, [toolConnectModal, toolConnectName, toolConnectDesc, nodes, setEdges, setNodes, pushHistory]);

  const onEdgeClick = useCallback((event, edge) => {
      const sourceNode = nodes.find((node) => node.id === edge.source);
      setEdgePanel({ x: event.clientX, y: event.clientY, edge, sourceNode });
  }, [nodes]);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const getDefaultNodeParams = useCallback((type) => {
    if (type === 'openai_agent') return { api_key: '', prompt: '' };
    if (type === 'ai_agent') return { provider: 'openai', model: 'gpt-3.5-turbo', system_prompt: 'You are a helpful AI assistant.', prompt: '' };
    if (type === 'gemini_agent') return { api_key: '', prompt: '' };
    if (type === 'http_request') return { url: 'https://httpbin.org/get', method: 'GET' };
    if (type === 'edit_fields') return { assignments: [{ field: 'key', value: 'value' }] };
    if (type === 'webhook') {
      return {
        slug: `wh-${Array.from(crypto.getRandomValues(new Uint8Array(5))).map(b => b.toString(16).padStart(2, '0')).join('')}`,
        method: 'POST'
      };
    }
    if (type === 'gmail_trigger') return { delegate_to: '', topic_name: '' };
    if (type === 'outlook_trigger') return { app_base_url: '', fetch_content: true };
    if (type === 'if') return { logic: 'all', conditions: [{ left: '', operator: '==', right: '' }] };
    if (type === 'switch') return { value: '', cases: [], default_case: 'Default' };
    if (type === 'human_approval') return { title: 'Human Approval', message: 'Please review and choose approve or reject.', request_details: '', approvers: '', recipients: '', options: 'approve, reject, cancel', timeout_seconds: '', on_timeout: 'reject' };
    if (type === 'form_input') return { title: 'Form Input', message: 'Please complete and submit the form.', recipients: '', fields: [{ key: 'name', type: 'text', required: true }], prefill_data: '', timeout_seconds: '', on_timeout: 'cancel' };
    if (type === 'try_catch') return {};
    if (type === 'file_type_detector') return { base64_content: '' };
    if (type === 'embeddings') return { provider: 'huggingface', model_name: 'sentence-transformers/all-MiniLM-L6-v2' };
    if (type === 'vector_store') return { db_type: 'chroma', collection_name: 'my_collection', operation: 'store' };
    if (type === 'teams') return { webhook_url: '', message: '' };
    if (type === 'slack') return { action: 'send_message', channel_id: '', message: '' };
    return { message: 'Debug Message' };
  }, []);

  const addNodeAtPosition = useCallback((type, label, icon, position) => {
    pushHistory();
    if (type === 'sticky_note') {
      setNodes(nds => nds.concat({
        id: getId(),
        type: 'sticky_note',
        position,
        data: { label: 'Note', type: 'sticky_note', text: '', color: 'yellow' },
      }));
      return;
    }
    // §4.79 Multi-Agent Orchestration Studio — render ai_agent as agent_graph_node in agent_graph workflows
    const reactFlowType = (workflowType === 'agent_graph' && type === 'ai_agent') ? 'agent_graph_node' : 'icon';
    setNodes((nds) => nds.concat({
      id: getId(),
      type: reactFlowType,
      position,
      data: {
        label,
        type,
        icon: icon || null,
        params: getDefaultNodeParams(type)
      },
    }));
  }, [getDefaultNodeParams, setNodes, pushHistory, workflowType]);

  // Copilot chat callbacks — applied when user clicks "Apply" on a suggestion card
  const copilotAddNode = useCallback((type, label, params) => {
    pushHistory();
    // Position new node in the visible viewport center, with a small random offset
    // to avoid stacking multiple copilot-added nodes exactly on top of each other
    let position = { x: 250, y: 200 };
    if (reactFlowInstance) {
      const vp = reactFlowInstance.getViewport();
      position = {
        x: (-vp.x + window.innerWidth * 0.40) / vp.zoom + (Math.random() * 40 - 20),
        y: (-vp.y + window.innerHeight * 0.40) / vp.zoom + (Math.random() * 40 - 20),
      };
    }
    setNodes(nds => nds.concat({
      id: getId(),
      type: 'icon',
      position,
      data: { label: label || type, type, icon: null, params: { ...getDefaultNodeParams(type), ...params } },
    }));
  }, [getDefaultNodeParams, setNodes, reactFlowInstance, pushHistory]);

  const copilotModifyNode = useCallback((nodeLabel, paramsPatch) => {
    pushHistory();
    setNodes(nds => nds.map(n =>
      n.data?.label === nodeLabel
        ? { ...n, data: { ...n.data, params: { ...(n.data.params || {}), ...paramsPatch } } }
        : n
    ));
  }, [setNodes, pushHistory]);

  const copilotRemoveNode = useCallback((nodeLabel) => {
    pushHistory();
    setNodes(nds => {
      const target = nds.find(n => n.data?.label === nodeLabel);
      if (!target) return nds;
      setEdges(eds => eds.filter(e => e.source !== target.id && e.target !== target.id));
      return nds.filter(n => n.id !== target.id);
    });
  }, [setNodes, setEdges, pushHistory]);

  const runNode = async (nodeId, context, nodeDefinition = null) => {
      if (currentWorkspace?.my_role === 'viewer') {
          alert("Viewers cannot run nodes.");
          return;
      }
      if (!workflowId) return alert("Please save the workflow first.");
      
      // Update status to running
      setNodeStatus(prev => ({
          ...prev,
          [nodeId]: { status: 'running', output: null }
      }));

      try {
          const res = await axios.post(`${API_BASE_URL}/workflows/${workflowId}/nodes/${nodeId}/run`, {
              context: context,
              node: nodeDefinition
          });
          
          if (res.data.status === 'success') {
              setNodeStatus(prev => ({
                  ...prev,
                  [nodeId]: { status: 'completed', output: res.data.result }
              }));
          } else if (res.data.status === 'paused') {
              setNodeStatus(prev => ({
                  ...prev,
                  [nodeId]: { status: 'waiting', output: res.data.payload || null }
              }));
          }
      } catch (error) {
          console.error("Error running node", error);
          setNodeStatus(prev => ({
              ...prev,
              [nodeId]: { status: 'failed', output: error.response?.data?.detail || error.message }
          }));
      }
  };

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');
      const label = event.dataTransfer.getData('application/reactflow/label');
      const icon = event.dataTransfer.getData('application/reactflow/icon');

      if (typeof type === 'undefined' || !type) {
        return;
      }

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      
      addNodeAtPosition(type, `${label}`, icon, position);
    },
    [addNodeAtPosition, reactFlowInstance]
  );

  // Add a sticky note to the center of the visible viewport
  const addStickyNote = useCallback(() => {
    if (!reactFlowInstance) return;
    const vp = reactFlowInstance.getViewport();
    const position = {
      x: (-vp.x + window.innerWidth * 0.45) / vp.zoom,
      y: (-vp.y + window.innerHeight * 0.35) / vp.zoom,
    };
    setNodes(nds => nds.concat({
      id: getId(),
      type: 'sticky_note',
      position,
      data: { label: 'Note', type: 'sticky_note', text: '', color: 'yellow' },
    }));
  }, [reactFlowInstance, setNodes]);

  // Group the currently selected nodes into a GroupNode
  const groupSelectedNodes = useCallback(() => {
    const eligible = selectedNodes.filter(n => n.type !== 'group' && !n.parentId);
    if (eligible.length < 2) return;
    const PADDING = 40;
    const HEADER_H = 36;
    const minX = Math.min(...eligible.map(n => n.position.x));
    const minY = Math.min(...eligible.map(n => n.position.y));
    const maxX = Math.max(...eligible.map(n => n.position.x + (n.width || 150)));
    const maxY = Math.max(...eligible.map(n => n.position.y + (n.height || 60)));
    const groupX = minX - PADDING;
    const groupY = minY - HEADER_H - PADDING;
    const groupW = maxX - minX + PADDING * 2;
    const groupH = maxY - minY + HEADER_H + PADDING * 2;
    const groupId = getId();
    const eligibleIds = new Set(eligible.map(n => n.id));
    setNodes(nds => [
      {
        id: groupId,
        type: 'group',
        position: { x: groupX, y: groupY },
        style: { width: groupW, height: groupH },
        data: { label: 'Group', type: 'group', collapsed: false, expandedHeight: groupH, expandedWidth: groupW },
        zIndex: -1,
      },
      ...nds.map(n => {
        if (!eligibleIds.has(n.id)) return n;
        return {
          ...n,
          parentId: groupId,
          extent: 'parent',
          position: { x: n.position.x - groupX, y: n.position.y - groupY },
          selected: false,
        };
      }),
    ]);
  }, [selectedNodes, setNodes]);

  // Remove a group and restore children to absolute positions
  const ungroupNode = useCallback((groupId) => {
    setNodes(nds => {
      const group = nds.find(n => n.id === groupId);
      if (!group) return nds;
      return nds
        .filter(n => n.id !== groupId)
        .map(n => {
          if (n.parentId !== groupId) return n;
          const { parentId, extent, ...rest } = n;
          return {
            ...rest,
            hidden: false,
            position: { x: n.position.x + group.position.x, y: n.position.y + group.position.y },
          };
        });
    });
    setEdges(eds => eds.map(e => ({ ...e, hidden: false })));
  }, [setNodes, setEdges]);

  const onNodeClick = useCallback((event, node) => {
    setEdgePanel(null);
    // Canvas-only nodes (sticky notes, groups) don't use the config panel
    if (node.type === 'sticky_note' || node.type === 'group') {
      setSelectedNode(null);
      const ws = collabWsRef.current;
      if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'editing_node', node_id: null }));
      return;
    }
    setSelectedNode(node);
    const ws = collabWsRef.current;
    if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'editing_node', node_id: node.id }));
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setNodeContextMenu(null);
    setEdgePanel(null);
    const ws = collabWsRef.current;
    if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'editing_node', node_id: null }));
  }, []);

  const onNodeContextMenu = useCallback((event, node) => {
    event.preventDefault();
    if (node.type === 'sticky_note' || node.type === 'group') return;
    setNodeContextMenu({
      x: event.clientX,
      y: event.clientY,
      nodeId: node.id,
      nodeLabel: node.data?.label || node.data?.type || node.id,
      isPinned: !!node.data?.pinnedData,
    });
  }, []);

  const onSelectionChange = useCallback(({ nodes: selectedNodes }) => {
    setSelectedNodes(selectedNodes || []);
    setSelectedNode(selectedNodes && selectedNodes.length > 0 ? selectedNodes[0] : null);
  }, []);

  // useEffect(() => {
  //   if (workflowId) {
  //     try {
  //       const workflowData = {
  //           name: workflowName,
  //           definition: { nodes, edges },
  //           save_history: true
  //       };
  //       axios.put(`${API_BASE_URL}/workflows/${workflowId}`, workflowData).catch(err => console.error("Auto-save failed", err));
  //     } catch (error) {
  //       console.error("Failed to auto-save workflow", error);
  //     }
  //   }
  // }, [nodes, edges, workflowName]); // Auto-save on changes

  const saveWorkflow = async () => {
    if (currentWorkspace?.my_role === 'viewer') {
        alert("Viewers cannot save workflows.");
        return;
    }
    if (!workflowName) return;
    setIsSaving(true);
    try {
      const workflowData = {
        name: workflowName,
        definition: { nodes, edges },
        save_history: true,
        cron_schedule: cronSchedule,
        is_paused: isPaused,
        max_concurrent_runs: workflowMaxConcurrentRuns,
        workspace_id: currentWorkspace?.id || null,
        // Advanced Scheduler (§4.50)
        scheduler_blackout_windows: advancedScheduler.blackout_windows ?? null,
        scheduler_skip_if_running: advancedScheduler.skip_if_running ?? false,
        scheduler_run_window_start: advancedScheduler.run_window_start ?? null,
        scheduler_run_window_end: advancedScheduler.run_window_end ?? null,
        scheduler_max_runs_per_hour: advancedScheduler.max_runs_per_hour ?? null,
        // SLA Alerts (§4.70)
        sla_max_seconds: slaMaxSeconds ?? null,
        // §4.79 Multi-Agent Orchestration Studio
        workflow_type: workflowType || 'standard',
        // Run Priority Classes
        priority: ['high', 'normal', 'low'].includes(runPriority) ? runPriority : 'normal',
      };
      
      let res;
      if (workflowId) {
        res = await axios.put(`${API_BASE_URL}/workflows/${workflowId}`, workflowData);
      } else {
        res = await axios.post(`${API_BASE_URL}/workflows/`, workflowData);
        setWorkflowId(res.data.id);
        // Update URL without reload
        window.history.pushState({}, '', `/${res.data.id}`);
      }
      setSavedWorkflowSnapshot(buildWorkflowSnapshot(workflowName, nodes, edges, cronSchedule, isPaused, workflowMaxConcurrentRuns));
      setWorkflowPublishStatus(res.data.publish_status || 'draft');
      // Notify embed host
      if (embedToken) window.parent.postMessage({ type: 'workflow:saved', workflow_id: res.data.id, name: res.data.name }, '*');
      // Notify collaborators that the canvas was updated
      const cws = collabWsRef.current;
      if (cws && cws.readyState === WebSocket.OPEN) cws.send(JSON.stringify({ type: 'canvas_update' }));
      // alert('Workflow saved!');
    } catch (error) {
      console.error("Failed to save workflow", error);
      alert('Failed to save workflow');
    } finally {
      setIsSaving(false);
    }
  };

  const loadWorkflow = async (id) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/workflows/${id}`);
      const wf = res.data;
      setWorkflowName(wf.name);
      setWorkflowId(wf.id);
      setCronSchedule(wf.cron_schedule || "");
      setIsPaused(wf.is_paused || false);
      setWorkflowMaxConcurrentRuns(wf.max_concurrent_runs || 3);
      setAdvancedScheduler({ skip_if_running: wf.scheduler_skip_if_running ?? false, run_window_start: wf.scheduler_run_window_start ?? null, run_window_end: wf.scheduler_run_window_end ?? null, max_runs_per_hour: wf.scheduler_max_runs_per_hour ?? null, blackout_windows: wf.scheduler_blackout_windows ?? null });
      setSlaMaxSeconds(wf.sla_max_seconds ?? null);
      setRunPriority(['high', 'normal', 'low'].includes(wf.priority) ? wf.priority : 'normal');
      setPublicChatEnabled(Boolean(wf.public_chat_enabled));
      setPublicChatLink(wf.public_chat_token ? `${window.location.origin}/public/chat/${wf.public_chat_token}` : "");
      setFormTriggerUrl(wf.form_token ? `${window.location.origin}/forms/${wf.form_token}` : "");
      setWorkflowPublishStatus(wf.publish_status || 'draft');
      setCurrentEnvironmentId(null);
      // §4.79 Multi-Agent Orchestration Studio — restore workflow_type and auto-show reasoning panel
      const loadedWfType = (wf.workflow_type || 'standard');
      setWorkflowType(loadedWfType);
      if (loadedWfType === 'agent_graph') {
        setShowReasoningPanel(true);
      } else {
        setShowReasoningPanel(false);
        setShowMemoryPanel(false);
      }
      setAgentReasoningEvents([]);
      
      let loadedNodes = [];
      let loadedEdges = [];
      
      try {
        if (wf.definition) {
          let def;
          if (typeof wf.definition === 'string') {
              try {
                  def = JSON.parse(wf.definition);
              } catch (e) {
                  console.error("Failed to parse definition string:", wf.definition);
                  throw e;
              }
          } else if (typeof wf.definition === 'object') {
              def = wf.definition;
          } else {
              console.warn("Unknown definition type:", typeof wf.definition);
              def = { nodes: [], edges: [] };
          }

          const normalized = normalizeWorkflowDefinitionForCanvas(def);
          loadedNodes = normalized.nodes.map(n => {
            // §4.79 Multi-Agent Orchestration Studio — render ai_agent nodes as agent_graph_node
            // when this workflow is an agent_graph
            let mapped = n;
            if (loadedWfType === 'agent_graph' && n.data?.type === 'ai_agent') {
              mapped = { ...n, type: 'agent_graph_node' };
            }
            if (mapped.data?.type !== 'ai_agent') return mapped;
            const tcs = mapped.data?.params?.tool_connections;
            if (!Array.isArray(tcs) || tcs.length === 0) return mapped;
            const rehydrated = tcs.map(tc => {
              const fields = nodeFieldsMapRef.current[tc.node_type];
              return fields && fields.length > 0 ? { ...tc, _node_fields: fields } : tc;
            });
            return { ...mapped, data: { ...mapped.data, params: { ...mapped.data.params, tool_connections: rehydrated } } };
          });
          // §4.79 — restore ReactFlow `type` on edges that carry edge_type=delegate_to so the
          // ConditionEdge renderer kicks in (delegate_to key maps to ConditionEdge in edgeTypes).
          loadedEdges = normalized.edges.map(e => {
            if (e?.data?.edge_type === 'delegate_to' && e.type !== 'delegate_to') {
              return { ...e, type: 'delegate_to' };
            }
            return e;
          });
          syncNextId(loadedNodes);
        }
      } catch (e) {
        console.error("Failed to parse workflow definition", e);
      }
      
      undoStackRef.current = [];
      redoStackRef.current = [];
      skipNextBroadcastRef.current = true;
      lastBroadcastHashRef.current = JSON.stringify(loadedNodes.map(({ selected: _s, dragging: _d, ...n }) => n)) + '|' + JSON.stringify(loadedEdges);
      setNodes(loadedNodes);
      setEdges(loadedEdges);
      setSelectedNode(null);
      setSelectedNodes([]);
      setSavedWorkflowSnapshot(buildWorkflowSnapshot(wf.name, loadedNodes, loadedEdges, wf.cron_schedule || "", wf.is_paused || false, wf.max_concurrent_runs || 3));
      
      // Restore status overlays for all pinned nodes
      const allPinnedNodes = loadedNodes.filter(n => n.data?.pinnedData);
      if (allPinnedNodes.length > 0) {
          const statusUpdates = {};
          allPinnedNodes.forEach(n => {
              statusUpdates[n.id] = { status: 'completed', output: n.data.pinnedData };
          });
          setNodeStatus(prev => ({ ...prev, ...statusUpdates }));
          const firstPinned = allPinnedNodes[0];
          setPinnedFirstNode(firstPinned);
          setPinnedTriggerData(firstPinned.data.pinnedData);
      } else {
          setPinnedFirstNode(null);
          setPinnedTriggerData(null);
          setPinnedTriggerMeta(null);
      }

      setEditorReturnView(currentViewRef.current === 'editor' ? editorReturnView : currentViewRef.current);
      setView('editor');
    } catch (error) {
      console.error("Failed to load workflow", error);
      alert("Failed to load workflow");
    }
  };

  const handleVersionRestored = (wf) => {
    setWorkflowName(wf.name);
    setWorkflowId(wf.id);
    setCronSchedule(wf.cron_schedule || "");
    setIsPaused(wf.is_paused || false);
    setWorkflowMaxConcurrentRuns(wf.max_concurrent_runs || 3);
    setAdvancedScheduler({ skip_if_running: wf.scheduler_skip_if_running ?? false, run_window_start: wf.scheduler_run_window_start ?? null, run_window_end: wf.scheduler_run_window_end ?? null, max_runs_per_hour: wf.scheduler_max_runs_per_hour ?? null, blackout_windows: wf.scheduler_blackout_windows ?? null });
    setPublicChatEnabled(Boolean(wf.public_chat_enabled));
    setPublicChatLink(wf.public_chat_token ? `${window.location.origin}/public/chat/${wf.public_chat_token}` : "");
    setFormTriggerUrl(wf.form_token ? `${window.location.origin}/forms/${wf.form_token}` : "");

    let definition = { nodes: [], edges: [] };
    if (typeof wf.definition === 'string') {
      try {
        definition = JSON.parse(wf.definition);
      } catch (error) {
        definition = { nodes: [], edges: [] };
      }
    } else if (wf.definition && typeof wf.definition === 'object') {
      definition = wf.definition;
    }
    const normalized = normalizeWorkflowDefinitionForCanvas(definition);
    const loadedNodes = normalized.nodes;
    const loadedEdges = normalized.edges;
    syncNextId(loadedNodes);
    undoStackRef.current = [];
    redoStackRef.current = [];
    skipNextBroadcastRef.current = true;
    lastBroadcastHashRef.current = JSON.stringify(loadedNodes.map(({ selected: _s, dragging: _d, ...n }) => n)) + '|' + JSON.stringify(loadedEdges);
    setNodes(loadedNodes);
    setEdges(loadedEdges);
    setSelectedNode(null);
    setSelectedNodes([]);
    setSavedWorkflowSnapshot(buildWorkflowSnapshot(
      wf.name,
      loadedNodes,
      loadedEdges,
      wf.cron_schedule || "",
      wf.is_paused || false,
      wf.max_concurrent_runs || 3
    ));
  };

  const createBlankWorkflow = () => {
    if (currentWorkspace?.my_role === 'viewer') {
        alert("Viewers cannot create workflows.");
        return;
    }
    setWorkflowId(null);
    setWorkflowName(`Untitled Workflow ${new Date().toLocaleString()}`);
    setNodes(initialNodes);
    setEdges([]);
    syncNextId(initialNodes);
    undoStackRef.current = [];
    redoStackRef.current = [];
    setExecutionResult(null);
    setCronSchedule("");
    setIsPaused(false);
    setWorkflowMaxConcurrentRuns(3);
    setAdvancedScheduler({ skip_if_running: false, run_window_start: null, run_window_end: null, max_runs_per_hour: null, blackout_windows: null });
    setPublicChatEnabled(false);
    setPublicChatLink("");
    setFormTriggerUrl("");
    setSavedWorkflowSnapshot(null);
    setShowWorkflowStartModal(false);
    setWorkflowType('standard');
    setShowReasoningPanel(false);
    setShowMemoryPanel(false);
    setAgentReasoningEvents([]);
    setEditorReturnView(currentViewRef.current === 'editor' ? editorReturnView : currentViewRef.current);
    setView('editor');
  };

  // §4.79 Multi-Agent Orchestration Studio — start a fresh agent_graph workflow
  // Seeds a single supervisor AI Agent node so the canvas isn't empty.
  const createAgentGraphWorkflow = () => {
    if (currentWorkspace?.my_role === 'viewer') {
        alert("Viewers cannot create workflows.");
        return;
    }
    setWorkflowId(null);
    setWorkflowName(`Agent Graph ${new Date().toLocaleString()}`);
    const seedNode = {
      id: 'supervisor_1',
      type: 'agent_graph_node',
      position: { x: 240, y: 160 },
      data: {
        label: 'Supervisor',
        type: 'ai_agent',
        params: {
          provider: 'openai',
          model: 'gpt-4o-mini',
          system_prompt: 'You are the supervisor agent. Coordinate sub-agents to solve the task.',
          prompt: '{{ trigger.body.task }}',
          agent_role: 'supervisor',
          enable_tools: true,
          shared_memory_read: true,
          shared_memory_write: false,
        },
      },
    };
    setNodes([seedNode]);
    setEdges([]);
    syncNextId([seedNode]);
    undoStackRef.current = [];
    redoStackRef.current = [];
    setExecutionResult(null);
    setCronSchedule("");
    setIsPaused(false);
    setWorkflowMaxConcurrentRuns(3);
    setAdvancedScheduler({ skip_if_running: false, run_window_start: null, run_window_end: null, max_runs_per_hour: null, blackout_windows: null });
    setPublicChatEnabled(false);
    setPublicChatLink("");
    setFormTriggerUrl("");
    setSavedWorkflowSnapshot(null);
    setShowWorkflowStartModal(false);
    setWorkflowType('agent_graph');
    setShowReasoningPanel(true);
    setAgentReasoningEvents([]);
    setEditorReturnView(currentViewRef.current === 'editor' ? editorReturnView : currentViewRef.current);
    setView('editor');
  };

  const createNewWorkflow = () => {
    if (currentWorkspace?.my_role === 'viewer') {
      alert("Viewers cannot create workflows.");
      return;
    }
    setShowWorkflowStartModal(true);
  };

  const startWithAiGenerator = () => {
    if (currentWorkspace?.my_role === 'viewer') {
      alert("Viewers cannot create workflows.");
      return;
    }
    createBlankWorkflow();
    setShowWorkflowGeneratorModal(true);
  };

  const importWorkflowFromStart = () => {
    if (currentWorkspace?.my_role === 'viewer') {
      alert("Viewers cannot import workflows.");
      return;
    }
    setShowWorkflowStartModal(false);
    importFileRef.current?.click();
  };

  const loadTemplate = (template) => {
    if (currentWorkspace?.my_role === 'viewer') {
      alert("Viewers cannot create workflows.");
      return;
    }
    setWorkflowId(null);
    setWorkflowName(template.name);
    const normalized = normalizeWorkflowDefinitionForCanvas(template.definition || { nodes: [], edges: [] });
    const loadedNodes = normalized.nodes;
    const loadedEdges = normalized.edges;
    skipNextBroadcastRef.current = true;
    lastBroadcastHashRef.current = JSON.stringify(loadedNodes.map(({ selected: _s, dragging: _d, ...n }) => n)) + '|' + JSON.stringify(loadedEdges);
    setNodes(loadedNodes);
    setEdges(loadedEdges);
    setSelectedNode(null);
    setSelectedNodes([]);
    syncNextId(loadedNodes);
    setExecutionResult(null);
    setCronSchedule("");
    setIsPaused(false);
    setWorkflowMaxConcurrentRuns(3);
    setPublicChatEnabled(false);
    setPublicChatLink("");
    setFormTriggerUrl("");
    setSavedWorkflowSnapshot(null);
    setShowWorkflowStartModal(false);
    setShowTemplatesModal(false);
    setEditorReturnView(currentViewRef.current === 'editor' ? editorReturnView : currentViewRef.current);
    setView('editor');
  };

  const importFileRef = useRef(null);
  const [importResult, setImportResult] = useState(null); // { imported: [], errors: [], count: N } | null
  const [showZapierImport, setShowZapierImport] = useState(false); // §4.99 Zapier/Make importer

  const handleZapierImported = async (result) => {
    setShowZapierImport(false);
    await fetchWorkflows();
    if (result?.workflow_id) {
      loadWorkflow(result.workflow_id);
    }
  };

  const exportWorkflow = async (e, workflowId, workflowName) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/workflows/${workflowId}/export`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${workflowName.replace(/[^a-zA-Z0-9_\-]/g, '_')}.xavvy`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Export failed: ' + err.message);
    }
  };

  const exportAllWorkflows = async () => {
    try {
      const token = localStorage.getItem('token');
      const wsParam = currentWorkspace ? `?workspace_id=${currentWorkspace.id}` : '';
      const res = await fetch(`${API_BASE_URL}/workflows/export-workspace/bundle${wsParam}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `xavvy_export_${new Date().toISOString().slice(0,10)}.xavvy`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Export all failed: ' + err.message);
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    if (currentWorkspace) formData.append('workspace_id', currentWorkspace.id);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/workflows/import`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || JSON.stringify(data));
      setImportResult(data);
      fetchWorkflows();
    } catch (err) {
      alert('Import failed: ' + err.message);
    }
  };

  const duplicateWorkflow = async (workflowId) => {
      if (currentWorkspace?.my_role === 'viewer') {
          alert("Viewers cannot duplicate workflows.");
          return;
      }
      if (!workflowId) return;
      try {
          await axios.post(`${API_BASE_URL}/workflows/${workflowId}/duplicate`);
          fetchWorkflows(); // Refresh list
      } catch (error) {
          console.error("Failed to duplicate workflow", error);
          alert("Failed to duplicate workflow");
      }
  };

  // Persistent WebSocket connection for status updates with reconnection
  useEffect(() => {
    if (!workflowId) return;

    let ws;
    let reconnectTimer;
    let isMounted = true;

    const connect = () => {
        try {
            ws = new WebSocket(`${WS_BASE_URL}/ws/workflows/${workflowId}/status`);

            ws.onopen = () => {
                console.log("Connected to Status WebSocket");
            };

            ws.onmessage = (event) => {
                const data = JSON.parse(event.data);
                if (data.type === 'node_update') {
                    setNodeStatus(prev => {
                        const entry = { status: data.status, output: data.output, error: data.error };
                        if (data.credential_reauth) entry.credential_reauth = data.credential_reauth;
                        const next = {
                            ...prev,
                            [data.node_id]: entry
                        };
                        const toolCalls = Array.isArray(data?.output?.tool_calls_made) ? data.output.tool_calls_made : [];
                        for (const tc of toolCalls) {
                            const toolNodeId = tc?.node_id ? String(tc.node_id) : '';
                            if (!toolNodeId) continue;
                            next[toolNodeId] = {
                                status: tc?.status || 'running',
                                output: {
                                    tool: tc?.tool || '',
                                    tool_input: tc?.input,
                                    tool_output: tc?.output,
                                    result: tc?.result,
                                    source: 'ai_agent_tool'
                                },
                                error: tc?.status === 'failed' ? (typeof tc?.result === 'string' ? tc.result : null) : null
                            };
                        }
                        return next;
                    });
                } else if (data.type === 'workflow_paused') {
                    setIsRunning(false);
                    setExecutionResult({
                      paused: true,
                      run_id: data.run_id,
                      interaction_id: data.interaction_id,
                      node_id: data.node_id
                    });
                } else if (data.type === 'workflow_complete') {
                    setIsRunning(false);
                    setExecutionResult(data.result || null);
                } else if (data.type === 'workflow_error') {
                    setIsRunning(false);
                    const errResult = { error: data.error || 'Workflow failed', run_id: data.run_id };
                    if (data.credential_reauth) errResult.credential_reauth = data.credential_reauth;
                    setExecutionResult(errResult);
                } else if (data.type === 'agent_reasoning') {
                    // §4.79 Multi-Agent Orchestration Studio — append CoT event for the reasoning panel
                    setAgentReasoningEvents(prev => [...prev, {
                        agent_id: data.agent_id,
                        phase: data.phase,
                        content: data.content,
                        timestamp: data.timestamp || new Date().toISOString(),
                    }]);
                }
            };

            ws.onclose = () => {
                if (isMounted) {
                    console.log("Status WebSocket closed. Reconnecting...");
                    reconnectTimer = setTimeout(connect, 3000);
                }
            };

            ws.onerror = (error) => {
                console.error("Status WebSocket error:", error);
                ws.close();
            };
        } catch (error) {
            console.error("Failed to create WebSocket:", error);
            if (isMounted) {
                reconnectTimer = setTimeout(connect, 3000);
            }
        }
    };

    connect();

    return () => {
        isMounted = false;
        if (ws) {
            ws.onclose = null; // Prevent reconnect on cleanup
            ws.close();
        }
        if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, [workflowId]);

  // Collaboration WebSocket — joins the collab room whenever a workflow is open
  useEffect(() => {
    if (!workflowId || !currentUser) {
      if (collabWsRef.current) { collabWsRef.current.onclose = null; collabWsRef.current.close(); collabWsRef.current = null; }
      setCollabUsers([]);
      setRemoteCursors({});
      setMyCollabColor(null);
      return;
    }
    let ws;
    let isMounted = true;
    let reconnectTimer;
    const connect = () => {
      try {
        ws = new WebSocket(`${WS_BASE_URL}/ws/workflows/${workflowId}/collab`);
        collabWsRef.current = ws;
        ws.onopen = () => {
          ws.send(JSON.stringify({ type: 'join', user_id: String(currentUser.id), username: currentUser.username || 'User' }));
        };
        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            const myId = String(currentUser.id);
            if (data.type === 'joined') {
              setMyCollabColor(data.color);
            } else if (data.type === 'presence_update') {
              setCollabUsers((data.users || []).filter(u => u.user_id !== myId));
            } else if (data.type === 'cursor_move') {
              if (data.user_id !== myId) {
                setRemoteCursors(prev => ({ ...prev, [data.user_id]: { username: data.username, color: data.color, x: data.x, y: data.y } }));
              }
            } else if (data.type === 'editing_node') {
              if (data.user_id !== myId) {
                setRemoteEditingNodes(prev => ({
                  ...prev,
                  [data.user_id]: { nodeId: data.node_id || null, username: data.username, color: data.color }
                }));
              }
            } else if (data.type === 'canvas_patch') {
              if (isDraggingRef.current) {
                bufferedPatchRef.current = data; // defer until local drag ends
              } else {
                applyRemotePatchRef.current?.(data);
              }
            } else if (data.type === 'canvas_update') {
              // Changes already applied via canvas_patch; this is just a "saved" ping
              setConflictToast({ username: data.username, color: data.color });
            } else if (data.type === 'ping') {
              const activeWs = collabWsRef.current;
              if (activeWs?.readyState === WebSocket.OPEN) activeWs.send(JSON.stringify({ type: 'pong' }));
            }
          } catch (e) {}
        };
        ws.onclose = () => { if (isMounted) reconnectTimer = setTimeout(connect, 4000); };
        ws.onerror = () => { ws.close(); };
      } catch (e) {
        if (isMounted) reconnectTimer = setTimeout(connect, 4000);
      }
    };
    connect();
    return () => {
      isMounted = false;
      if (ws) { ws.onclose = null; ws.close(); }
      if (reconnectTimer) clearTimeout(reconnectTimer);
      collabWsRef.current = null;
      setCollabUsers([]);
      setRemoteCursors({});
      setRemoteEditingNodes({});
      setMyCollabColor(null);
    };
  }, [workflowId, currentUser]);

  // Apply a canvas patch received from another user
  const applyRemotePatch = useCallback((data) => {
    const remoteNodes = data.nodes;
    const remoteEdges = data.edges;
    if (!Array.isArray(remoteNodes)) return;
    skipNextBroadcastRef.current = true;
    setNodes(local => remoteNodes.map(rn => {
      const localNode = local.find(n => n.id === rn.id);
      // Don't override a node the local user is actively dragging
      if (localNode?.dragging) return localNode;
      // Strip remote UI-only props; preserve local selection so peers don't affect our highlights
      const { selected: _s, dragging: _d, ...remoteRest } = rn;
      return { ...remoteRest, selected: localNode?.selected ?? false };
    }));
    if (Array.isArray(remoteEdges)) setEdges(remoteEdges);
    // Pre-update the broadcast hash so the effect triggered by setNodes/setEdges skips the echo
    const cleanNodes = remoteNodes.map(({ selected: _s, dragging: _d, ...n }) => n);
    const cleanEdges = Array.isArray(remoteEdges) ? remoteEdges : edgesRef.current;
    lastBroadcastHashRef.current = JSON.stringify(cleanNodes) + '|' + JSON.stringify(cleanEdges);
  }, [setNodes, setEdges]);
  applyRemotePatchRef.current = applyRemotePatch;

  // Drag tracking — buffer incoming patches during local drag to avoid interrupting it
  const handleNodeDragStart = useCallback(() => {
    isDraggingRef.current = true;
    pushHistory();
  }, [pushHistory]);
  const handleNodeDragStop = useCallback(() => {
    isDraggingRef.current = false;
    if (bufferedPatchRef.current) {
      applyRemotePatchRef.current?.(bufferedPatchRef.current);
      bufferedPatchRef.current = null;
    }
  }, []);

  // Wrap onNodesChange to block deleting nodes that a collaborator is editing
  const safeOnNodesChange = useCallback((changes) => {
    const lockedNodeIds = new Set(
      Object.values(remoteEditingNodes)
        .filter(e => e.nodeId)
        .map(e => e.nodeId)
    );
    if (lockedNodeIds.size > 0) {
      const blocked = changes.filter(c => c.type === 'remove' && lockedNodeIds.has(c.id));
      if (blocked.length > 0) {
        const editor = Object.values(remoteEditingNodes).find(e => blocked.some(c => c.id === e.nodeId));
        setLockedNodeToast({ username: editor?.username || 'Another user', color: editor?.color || '#6366f1' });
        const safe = changes.filter(c => !(c.type === 'remove' && lockedNodeIds.has(c.id)));
        if (safe.length > 0) onNodesChange(safe);
        return;
      }
    }
    // When nodes are deleted, push history then purge tool_connections
    const removedIds = changes.filter(c => c.type === 'remove').map(c => c.id);
    if (removedIds.length > 0) {
      pushHistory();
      const removedSet = new Set(removedIds);
      setNodes(nds => nds.map(n => {
        const tcs = n.data?.params?.tool_connections;
        if (!Array.isArray(tcs) || tcs.length === 0) return n;
        const filtered = tcs.filter(tc => !removedSet.has(tc.node_id));
        if (filtered.length === tcs.length) return n;
        return { ...n, data: { ...n.data, params: { ...n.data.params, tool_connections: filtered } } };
      }));
    }
    onNodesChange(changes);
  }, [onNodesChange, remoteEditingNodes, setNodes, pushHistory]);

  // Wrap onEdgesChange to block removing edges connected to locked nodes and to
  // push history + clean up tool_connections when edges are deleted (keyboard Delete/Backspace)
  const safeOnEdgesChange = useCallback((changes) => {
    const lockedNodeIds = new Set(
      Object.values(remoteEditingNodes)
        .filter(e => e.nodeId)
        .map(e => e.nodeId)
    );
    let effectiveChanges = changes;
    if (lockedNodeIds.size > 0) {
      const safe = changes.filter(c => {
        if (c.type !== 'remove') return true;
        const edge = edgesRef.current.find(e => e.id === c.id);
        return !edge || (!lockedNodeIds.has(edge.source) && !lockedNodeIds.has(edge.target));
      });
      if (safe.length < changes.length) {
        if (safe.length > 0) effectiveChanges = safe;
        else return;
      }
    }
    const removes = effectiveChanges.filter(c => c.type === 'remove');
    if (removes.length > 0) {
      pushHistory();
      const removedIds = new Set(removes.map(c => c.id));
      const toolEdges = edgesRef.current.filter(e => removedIds.has(e.id) && e.data?.edge_type === 'tool');
      if (toolEdges.length > 0) {
        setNodes(nds => nds.map(n => {
          const fromThis = toolEdges.filter(e => e.source === n.id);
          if (!fromThis.length) return n;
          const removedTargets = new Set(fromThis.map(e => e.target));
          const updated = (n.data?.params?.tool_connections || []).filter(tc => !removedTargets.has(tc.node_id));
          return { ...n, data: { ...n.data, params: { ...n.data?.params, tool_connections: updated } } };
        }));
      }
    }
    onEdgesChange(effectiveChanges);
  }, [onEdgesChange, remoteEditingNodes, pushHistory, setNodes]);

  // Broadcast the full canvas state to collaborators whenever nodes or edges change.
  // Throttled to 150 ms; skipped for remote-applied, workflow-load, or selection-only changes.
  useEffect(() => {
    if (!hasInitializedPatchRef.current) { hasInitializedPatchRef.current = true; return; }
    if (skipNextBroadcastRef.current) { skipNextBroadcastRef.current = false; return; }
    const ws = collabWsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !workflowId) return;
    // Strip UI-only props (selected, dragging) so selection clicks don't trigger spurious sends
    const computeHash = () => {
      const ns = nodesRef.current.map(({ selected: _s, dragging: _d, ...n }) => n);
      return JSON.stringify(ns) + '|' + JSON.stringify(edgesRef.current);
    };
    if (computeHash() === lastBroadcastHashRef.current) return;
    const now = Date.now();
    const since = now - patchThrottleRef.current;
    const send = () => {
      const ws2 = collabWsRef.current;
      if (ws2?.readyState === WebSocket.OPEN) {
        const ns = nodesRef.current.map(({ selected: _s, dragging: _d, ...n }) => n);
        const es = edgesRef.current;
        const hash = JSON.stringify(ns) + '|' + JSON.stringify(es);
        if (hash === lastBroadcastHashRef.current) return;
        ws2.send(JSON.stringify({ type: 'canvas_patch', nodes: ns, edges: es }));
        lastBroadcastHashRef.current = hash;
        patchThrottleRef.current = Date.now();
      }
    };
    if (since >= 150) {
      send();
    } else {
      const t = setTimeout(send, 150 - since);
      return () => clearTimeout(t);
    }
  }, [nodes, edges, workflowId]);

  // Auto-dismiss collab toasts
  useEffect(() => {
    if (!conflictToast) return;
    const t = setTimeout(() => setConflictToast(null), 4000);
    return () => clearTimeout(t);
  }, [conflictToast]);

  useEffect(() => {
    if (!lockedNodeToast) return;
    const t = setTimeout(() => setLockedNodeToast(null), 3500);
    return () => clearTimeout(t);
  }, [lockedNodeToast]);

  const sendCursorMove = useCallback((flowX, flowY) => {
    const ws = collabWsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const now = Date.now();
    if (now - cursorThrottleRef.current < 50) return;
    cursorThrottleRef.current = now;
    ws.send(JSON.stringify({ type: 'cursor_move', x: flowX, y: flowY }));
  }, []);

  const handleCanvasMouseMove = useCallback((e) => {
    if (!reactFlowInstance || !workflowId) return;
    const flowPos = reactFlowInstance.screenToFlowPosition({ x: e.clientX, y: e.clientY });
    sendCursorMove(flowPos.x, flowPos.y);
  }, [reactFlowInstance, workflowId, sendCursorMove]);

  const runWorkflow = async (startNodeIds = null) => {
    if (currentWorkspace?.my_role === 'viewer') {
        alert("Viewers cannot run workflows.");
        return;
    }
    if (!workflowId) {
        alert("Please save the workflow first.");
        return;
    }

    // Pre-run validation (§4.57) — block on errors, surface warnings
    try {
        const valRes = await axios.post(`${API_BASE_URL}/workflows/${workflowId}/validate`);
        const { errors: valErrors = [] } = valRes.data;
        const blocking = valErrors.filter(e => e.severity === 'error');
        const warnings = valErrors.filter(e => e.severity === 'warning');
        if (blocking.length > 0) {
            const lines = blocking.map(e => `• ${e.node_label}: ${e.message}`).join('\n');
            alert(`Fix these errors before running:\n\n${lines}`);
            return;
        }
        setValidationWarnings(warnings);
    } catch {
        setValidationWarnings([]);
    }

    setShowTriggerPicker(false);
    setIsRunning(true);
    setExecutionResult(null);
    setNodeStatus({}); // Reset status

    try {
        const body = { params: {}, environment_id: currentEnvironmentId || null };
        if (startNodeIds) body.start_node_ids = startNodeIds;
        const res = await axios.post(`${API_BASE_URL}/workflows/${workflowId}/run`, body);

        if (res.data?.paused) {
            setExecutionResult({
              paused: true,
              run_id: res.data.run_id,
              interaction_id: res.data.interaction_id,
              node_id: res.data.node_id
            });
        } else if (res.data?.status === 'queued') {
            setExecutionResult({
              status: 'queued',
              run_id: res.data.run_id,
              workflow_id: res.data.workflow_id
            });
        } else if (res.data?.status === 'success') {
            setExecutionResult(res.data.result);
        } else {
            setExecutionResult(res.data);
        }
    } catch (error) {
        console.error("Failed to run workflow", error);
        setExecutionResult({ error: error.response?.data?.detail || error.message });
        alert("Failed to run workflow");
    } finally {
        setIsRunning(false);
    }
  };

  const resolvePublicChatLink = (link) => {
    if (!link) return "";
    try {
      const url = new URL(link);
      const parts = url.pathname.split("/").filter(Boolean);
      const token = parts[parts.length - 1];
      if (token) return `${window.location.origin}/public/chat/${token}`;
    } catch (error) {
      const parts = link.split("/");
      const token = parts[parts.length - 1];
      if (token) return `${window.location.origin}/public/chat/${token}`;
    }
    return link;
  };

  const togglePublicChat = async () => {
    if (currentWorkspace?.my_role === 'viewer') {
        alert("Viewers cannot update public links.");
        return;
    }
    if (!workflowId) return;
    setPublicChatLoading(true);
    try {
      const enabled = !publicChatEnabled;
      const res = await axios.post(`${API_BASE_URL}/workflows/${workflowId}/public-chat`, { enabled });
      setPublicChatEnabled(res.data.enabled);
      const link = resolvePublicChatLink(res.data.link);
      setPublicChatLink(res.data.enabled ? link : "");
    } catch (error) {
      console.error("Failed to update public chat link", error);
      alert(error.response?.data?.detail || "Failed to update public chat link");
    } finally {
      setPublicChatLoading(false);
    }
  };

  const copyPublicChatLink = async () => {
    if (!publicChatLink) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(publicChatLink);
      } else {
        const tempInput = document.createElement('input');
        tempInput.value = publicChatLink;
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
      }
      alert("Public link copied");
    } catch (error) {
      console.error("Failed to copy public link", error);
      alert("Failed to copy public link");
    }
  };

  const handlePinTriggerData = (data, runInfo, nodeInfo) => {
    if (!data || !nodeInfo) return;
    setPinnedTriggerData(data);
    setPinnedTriggerMeta(runInfo);
    setPinnedFirstNode(nodeInfo);
    setNodeStatus((prev) => ({
      ...prev,
      [nodeInfo.id]: { status: 'completed', output: data }
    }));
    
    // Update nodes state for persistence
    setNodes(nds => nds.map(node => {
        if (node.id === nodeInfo.id) {
            return {
                ...node,
                data: {
                    ...node.data,
                    pinnedData: data
                }
            };
        }
        return node;
    }));
    
    setShowHistory(false);
  };

  const clearPinnedTriggerData = () => {
    // Clear pinnedData from all nodes and their status overlays
    setNodeStatus(prev => {
      const next = { ...prev };
      nodes.forEach(n => { if (n.data?.pinnedData) delete next[n.id]; });
      return next;
    });
    setNodes(nds => nds.map(node => {
      if (node.data?.pinnedData) {
        const newData = { ...node.data };
        delete newData.pinnedData;
        return { ...node, data: newData };
      }
      return node;
    }));
    setPinnedTriggerData(null);
    setPinnedTriggerMeta(null);
    setPinnedFirstNode(null);
  };

  const clearNodePin = (nodeId) => {
    setNodes(nds => nds.map(node => {
      if (node.id === nodeId && node.data?.pinnedData) {
        const newData = { ...node.data };
        delete newData.pinnedData;
        return { ...node, data: newData };
      }
      return node;
    }));
    setNodeStatus(prev => {
      const next = { ...prev };
      delete next[nodeId];
      return next;
    });
    if (pinnedFirstNode?.id === nodeId) {
      setPinnedTriggerData(null);
      setPinnedTriggerMeta(null);
      setPinnedFirstNode(null);
    }
  };

  const openPinModal = (nodeId) => {
    const node = nodes.find(n => n.id === nodeId);
    const existing = node?.data?.pinnedData;
    setPinModalNodeId(nodeId);
    setPinModalText(existing ? JSON.stringify(existing, null, 2) : '');
    setPinModalError('');
    setShowPinModal(true);
    setNodeContextMenu(null);
  };

  const savePinData = () => {
    if (!pinModalNodeId) return;
    let parsed;
    try {
      parsed = JSON.parse(pinModalText);
    } catch {
      setPinModalError('Invalid JSON — please enter a valid JSON object or array.');
      return;
    }
    setNodes(nds => nds.map(node => {
      if (node.id === pinModalNodeId) {
        return { ...node, data: { ...node.data, pinnedData: parsed } };
      }
      return node;
    }));
    setNodeStatus(prev => ({
      ...prev,
      [pinModalNodeId]: { status: 'completed', output: parsed }
    }));
    setShowPinModal(false);
    setPinModalNodeId(null);
    setPinModalText('');
    setPinModalError('');
  };

  const deleteWorkflow = async (e, id) => {
      e.stopPropagation();
      const wf = workflows.find(w => w.id === id);
      const canDelete = canMoveOrDeleteWorkflow(wf);
      
      if (!canDelete) {
          alert("Only Workflow Owner or Workspace Admin can delete workflows.");
          return;
      }

      if (!(await window.confirm("Are you sure you want to delete this workflow?"))) return;
      
      try {
          await axios.delete(`${API_BASE_URL}/workflows/${id}`);
          setSelectedWorkflowIds(prev => prev.filter(workflowId => workflowId !== id));
          fetchWorkflows();
      } catch (error) {
          console.error("Failed to delete workflow", error);
          alert("Failed to delete workflow: " + (error.response?.data?.detail || error.message));
      }
  };

  const bulkDeleteWorkflows = async () => {
    if (selectedWorkflowIds.length === 0) return;
    const selectedWorkflows = workflows.filter(wf => selectedWorkflowIds.includes(wf.id));
    const unauthorized = selectedWorkflows.find(wf => !canMoveOrDeleteWorkflow(wf));
    if (unauthorized) {
      alert("Only Workflow Owner or Workspace Admin can delete workflows.");
      return;
    }
    if (!(await window.confirm(`Delete ${selectedWorkflowIds.length} selected workflows? This action cannot be undone.`))) return;
    try {
      await axios.post(`${API_BASE_URL}/bulk/workflows/delete`, { workflow_ids: selectedWorkflowIds });
      setSelectedWorkflowIds([]);
      fetchWorkflows();
    } catch (error) {
      console.error("Failed to bulk delete workflows", error);
      alert("Failed to bulk delete workflows: " + (error.response?.data?.detail || error.message));
    }
  };

  const bulkPauseResumeWorkflows = async (isPausedValue) => {
    if (selectedWorkflowIds.length === 0) return;
    const selectedWorkflows = workflows.filter(wf => selectedWorkflowIds.includes(wf.id));
    const unauthorized = selectedWorkflows.find(wf => !canPauseResumeWorkflow(wf));
    if (unauthorized) {
      alert("You are not authorized to update one or more selected workflows.");
      return;
    }
    try {
      await axios.post(`${API_BASE_URL}/bulk/workflows/pause`, {
        workflow_ids: selectedWorkflowIds,
        is_paused: isPausedValue
      });
      fetchWorkflows();
    } catch (error) {
      console.error("Failed to bulk update workflow schedule", error);
      alert("Failed to update selected workflows: " + (error.response?.data?.detail || error.message));
    }
  };

  const bulkMoveWorkflows = async (targetWorkspaceId) => {
    if (selectedWorkflowIds.length === 0) return;
    const selectedWorkflows = workflows.filter(wf => selectedWorkflowIds.includes(wf.id));
    const unauthorized = selectedWorkflows.find(wf => !canMoveOrDeleteWorkflow(wf));
    if (unauthorized) {
      alert("Only Workflow Owner or Workspace Admin can move workflows.");
      return;
    }
    try {
      await axios.post(`${API_BASE_URL}/bulk/workflows/move`, {
        workflow_ids: selectedWorkflowIds,
        target_workspace_id: targetWorkspaceId
      });
      setShowBulkMoveModal(false);
      setSelectedWorkflowIds([]);
      fetchWorkflows();
    } catch (error) {
      console.error("Failed to bulk move workflows", error);
      alert("Failed to move selected workflows: " + (error.response?.data?.detail || error.message));
    }
  };

  // Public xavvy Forms renderer — no auth required
  const _xfMatch = window.location.pathname.match(/^\/xf\/([^/]+)/);
  if (_xfMatch) {
    return <XavvyFormRenderer token={_xfMatch[1]} />;
  }

  if (publicChatToken) {
    return (
      <div style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary)' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img src={logo} alt="xavvy" style={{ width: '28px', height: '28px' }} />
            <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Public Agent Chat</div>
          </div>
          <button 
            onClick={() => window.location.href = '/'} 
            style={{ background: 'transparent', border: '1px solid var(--border-color)', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', color: 'var(--text-primary)' }}
          >
            Back to Home
          </button>
        </div>
        <div style={{ flex: 1 }}>
          <AgentChat
            workflowId={null}
            token={null}
            isPublic={true}
            publicToken={publicChatToken}
            variant="page"
          />
        </div>
      </div>
    );
  }

  if (!token) {
    // Public legal pages — accessible at /privacy and /terms without auth
    if (window.location.pathname === '/privacy') {
      return <PrivacyPolicyPage branding={branding} />;
    }
    if (window.location.pathname === '/terms') {
      return <TermsOfServicePage branding={branding} />;
    }
    if (showDocs) {
        return <Docs onBack={() => setShowDocs(false)} />;
    }
    if (showLogin) {
        return <Login onLogin={handleLogin} onBack={() => setShowLogin(false)} branding={branding} />;
    }
    if (isFormsOnly) {
      return <FormsLandingPage onLoginClick={() => setShowLogin(true)} onDocsClick={() => setShowDocs(true)} branding={branding} />;
    }
    return <LandingPage onLoginClick={() => setShowLogin(true)} onDocsClick={() => setShowDocs(true)} branding={branding} />;
  }

  // ── Global Command Palette (portal — works across all views) ────────────────
  const commandPalettePortal = showCommandPalette ? ReactDOM.createPortal((() => {
    const closeCP = () => {
      setShowCommandPalette(false);
      setCommandPaletteQuery('');
      setCommandPaletteResults(null);
      setCommandPaletteError('');
      setCommandPaletteIndex(-1);
    };
    const searchCP = async (q) => {
      setCommandPaletteIndex(-1);
      if (!q.trim()) { setCommandPaletteResults(null); setCommandPaletteError(''); return; }
      setCommandPaletteLoading(true);
      setCommandPaletteError('');
      try {
        const params = new URLSearchParams({ q });
        if (currentWorkspace) params.set('workspace_id', currentWorkspace.id);
        const res = await axios.get(`${API_BASE_URL}/search?${params}`, { headers: { Authorization: `Bearer ${token}` } });
        setCommandPaletteResults(res.data);
      } catch (_) {
        setCommandPaletteError('Search failed. Please try again.');
      }
      setCommandPaletteLoading(false);
    };

    // Build a flat list of all result items so ↑↓ can navigate across sections
    const flatItems = commandPaletteResults ? [
      ...(commandPaletteResults.workflows || []).map(w => ({
        key: w.id, icon: '⚡', label: w.name,
        sub: w.updated_at ? new Date(w.updated_at).toLocaleDateString() : undefined,
        action: () => { loadWorkflow(w.id); }
      })),
      ...(commandPaletteResults.forms || []).map(f => ({
        key: f.id, icon: '📋', label: f.name, sub: f.status,
        action: () => { setEditingFormId(f.id); setView('form_designer'); }
      })),
      ...(commandPaletteResults.credentials || []).map(c => ({
        key: c.id, icon: '🔑', label: c.name, sub: c.service_type,
        action: () => { setReturnView('home'); setInitialCredentialId(c.id); setInitialCredentialServiceType(null); setView('credentials'); }
      })),
      ...(commandPaletteResults.runs || []).map(r => ({
        key: r.id, icon: '📊',
        label: r.id.slice(0, 8) + '…',
        sub: `${r.status} · ${r.start_time ? new Date(r.start_time).toLocaleString() : ''}`,
        action: () => { setView('monitor'); }
      })),
    ] : [];

    const total = flatItems.length;

    const runItem = (idx) => {
      if (idx >= 0 && idx < flatItems.length) {
        flatItems[idx].action();
        closeCP();
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') { closeCP(); return; }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setCommandPaletteIndex(i => Math.min(i + 1, flatItems.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setCommandPaletteIndex(i => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        runItem(commandPaletteIndex);
      }
    };

    // Map each flat item back to its section index for rendering
    const wfItems = commandPaletteResults?.workflows || [];
    const formItems = commandPaletteResults?.forms || [];
    const credItems = commandPaletteResults?.credentials || [];
    const runItems = commandPaletteResults?.runs || [];
    const wfOffset = 0;
    const formOffset = wfItems.length;
    const credOffset = formOffset + formItems.length;
    const runOffset = credOffset + credItems.length;

    const ItemRow = ({ flatIdx, label, sub, icon, action }) => {
      const isActive = flatIdx === commandPaletteIndex;
      return (
        <div
          onClick={() => { action(); closeCP(); }}
          onMouseEnter={() => setCommandPaletteIndex(flatIdx)}
          style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '9px 16px',
            cursor: 'pointer', borderRadius: 7, margin: '1px 8px',
            background: isActive ? 'var(--bg-primary)' : 'transparent'
          }}>
          <span style={{ fontSize: 16, width: 22, textAlign: 'center', flexShrink: 0 }}>{icon}</span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
            {sub && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</div>}
          </div>
        </div>
      );
    };
    const SectionHeader = ({ title }) => (
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-tertiary)', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '8px 16px 4px' }}>{title}</div>
    );

    return (
      <div onClick={closeCP} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 10100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '12vh' }}>
        <div onClick={e => e.stopPropagation()} style={{ width: 'min(600px, 92vw)', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: 14, boxShadow: '0 24px 64px rgba(0,0,0,0.5)', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: '1px solid var(--border-color)' }}>
            <span style={{ color: 'var(--text-tertiary)', fontSize: 16 }}>🔍</span>
            <input
              autoFocus
              value={commandPaletteQuery}
              onChange={e => { setCommandPaletteQuery(e.target.value); searchCP(e.target.value); }}
              onKeyDown={handleKeyDown}
              placeholder="Search workflows, credentials, forms, runs…"
              style={{ flex: 1, background: 'none', border: 'none', outline: 'none', fontSize: 15, color: 'var(--text-primary)', caretColor: 'var(--accent-color)' }}
            />
            {commandPaletteLoading && <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>…</span>}
            <kbd style={{ fontSize: 11, color: 'var(--text-tertiary)', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 4, padding: '2px 6px' }}>Esc</kbd>
          </div>
          <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingBottom: 8 }}>
            {commandPaletteError ? (
              <div style={{ padding: '24px 16px', textAlign: 'center', color: '#ef4444', fontSize: 13 }}>{commandPaletteError}</div>
            ) : !commandPaletteQuery.trim() ? (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
                Start typing to search across your workflows, credentials, forms, and runs
              </div>
            ) : commandPaletteResults && total === 0 ? (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>No results for "{commandPaletteQuery}"</div>
            ) : commandPaletteResults ? (
              <>
                {wfItems.length > 0 && <><SectionHeader title="Workflows" />{wfItems.map((w, i) => <ItemRow key={w.id} flatIdx={wfOffset + i} icon="⚡" label={w.name} sub={w.updated_at ? new Date(w.updated_at).toLocaleDateString() : undefined} action={flatItems[wfOffset + i].action} />)}</>}
                {formItems.length > 0 && <><SectionHeader title="Forms" />{formItems.map((f, i) => <ItemRow key={f.id} flatIdx={formOffset + i} icon="📋" label={f.name} sub={f.status} action={flatItems[formOffset + i].action} />)}</>}
                {credItems.length > 0 && <><SectionHeader title="Credentials" />{credItems.map((c, i) => <ItemRow key={c.id} flatIdx={credOffset + i} icon="🔑" label={c.name} sub={c.service_type} action={flatItems[credOffset + i].action} />)}</>}
                {runItems.length > 0 && <><SectionHeader title="Runs" />{runItems.map((r, i) => <ItemRow key={r.id} flatIdx={runOffset + i} icon="📊" label={r.id.slice(0, 8) + '…'} sub={`${r.status} · ${r.start_time ? new Date(r.start_time).toLocaleString() : ''}`} action={flatItems[runOffset + i].action} />)}</>}
              </>
            ) : null}
          </div>
          <div style={{ padding: '8px 16px', borderTop: '1px solid var(--border-color)', display: 'flex', gap: 16, fontSize: 11, color: 'var(--text-tertiary)' }}>
            <span><kbd style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 3, padding: '1px 4px' }}>↑↓</kbd> navigate</span>
            <span><kbd style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 3, padding: '1px 4px' }}>↵</kbd> open</span>
            <span><kbd style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 3, padding: '1px 4px' }}>Esc</kbd> close</span>
            <span style={{ marginLeft: 'auto' }}>⌘K</span>
          </div>
        </div>
      </div>
    );
  })(), document.body) : null;

  // Forms-only mode: redirect workflow-only views to forms list
  if (isFormsOnly && ['editor', 'monitoring', 'webhooks', 'customRoutes', 'scheduler_calendar', 'plugins', 'marketplace', 'variables', 'datastore', 'mobile_monitor'].includes(view)) {
    setView('xavvy_forms');
    return null;
  }

  if (view === 'admin') {
    return <>{commandPalettePortal}<AdminPanel onBack={() => setView(returnView)} /></>;
  }

  if (view === 'profile') {
    return <>{commandPalettePortal}<Profile onBack={() => setView(returnView)} token={token} /></>;
  }

  if (view === 'credentials') {
    return <>{commandPalettePortal}<CredentialManager
      onBack={() => {
        setView(returnView);
        setInitialCredentialServiceType(null);
        setInitialCredentialId(null);
      }}
      initialServiceType={initialCredentialServiceType}
      initialCredentialId={initialCredentialId}
    /></>;
  }

  if (view === 'audit_logs') {
    return <>{commandPalettePortal}<AuditLogViewer onBack={() => setView(returnView)} /></>;
  }

  if (view === 'monitoring') {
    return <>{commandPalettePortal}<MonitoringDashboard onBack={() => setView(returnView)} /></>;
  }

  if (view === 'mobile_monitor') {
    return (
      <>{commandPalettePortal}
      <MobileRunMonitor
        getAuthHeaders={() => { const t = localStorage.getItem('token'); return t ? { Authorization: `Bearer ${t}` } : {}; }}
        currentWorkspace={currentWorkspace}
        onBack={() => setView(returnView || 'home')}
      /></>
    );
  }

  // xavvy_forms is now rendered inside the shared toolbar layout below

  if (view === 'case_client') {
    return (
      <>{commandPalettePortal}
      <CaseClientBootstrap
        token={token}
        currentUser={currentUser}
        allWorkspaces={allWorkspaces}
        currentWorkspace={currentWorkspace}
        setCurrentWorkspace={setCurrentWorkspace}
        setAllWorkspaces={setAllWorkspaces}
        initialWorkspaceId={caseClientInitialWorkspaceId}
        onLogout={handleLogout}
        onExit={() => { window.history.replaceState({}, '', '/'); setView('home'); }}
      /></>
    );
  }

  if (view === 'form_designer') {
    return (
      <>{commandPalettePortal}
      <XavvyFormDesigner
        formId={editingFormId}
        authToken={token}
        workspaceId={currentWorkspace?.id || null}
        onBack={() => setView('xavvy_forms')}
      /></>
    );
  }

  if (view === 'variables') {
    return <>{commandPalettePortal}<GlobalVariables onBack={() => setView(returnView)} /></>;
  }

  if (view === 'datastore') {
    return <>{commandPalettePortal}<DataStore onBack={() => setView(returnView)} /></>;
  }

  if (view === 'tables') {
    return (
      <>{commandPalettePortal}
      <Tables
        onBack={() => setView(returnView)}
        currentWorkspaceId={currentWorkspace?.id || null}
        currentWorkspaceRole={currentWorkspace?.my_role || null}
      /></>
    );
  }

  if (view === 'docs') {
    return <>{commandPalettePortal}<Docs onBack={() => setView(returnView)} /></>;
  }

  if (view === 'scheduler_calendar') {
    return (
      <>{commandPalettePortal}
      <SchedulerCalendar
        onBack={() => setView(returnView)}
        token={token}
        workspaceId={currentWorkspace?.id || null}
        onOpenWorkflow={(workflowId) => {
          loadWorkflow(workflowId);
        }}
      /></>
    );
  }

  if (view === 'plugins') {
    return <>{commandPalettePortal}<PluginGovernance onBack={() => setView(returnView)} /></>;
  }

  if (view === 'webhooks') {
    return <>{commandPalettePortal}<WebhookDashboard onBack={() => setView(returnView)} /></>;
  }

  if (view === 'customRoutes') {
    return <>{commandPalettePortal}<CustomRouteManager token={token} onBack={() => setView(returnView)} /></>;
  }

  if (view === 'marketplace') {
    return <>{commandPalettePortal}<MarketplaceModal onBack={() => setView(returnView)} isAdmin={isAdmin} currentWorkspace={currentWorkspace} /></>;
  }

  if (view === 'workspaces') {
    return (
      <>{commandPalettePortal}
        <div style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary)', overflow: 'auto' }}>
            <div style={{ padding: '20px', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center' }}>
                <button onClick={() => setView('home')} style={btnStyle('transparent')}>Back to Home</button>
            </div>
            <WorkspaceManager
                token={token}
                onSelectWorkspace={(ws) => {
                    setCurrentWorkspace(ws);
                    setView('home');
                }}
            />
        </div>
      </>
    );
  }

  // casematrix is now rendered inside the shared toolbar layout below

  // casematrix_types is now rendered inside the shared toolbar layout below

  if (view === 'home' || view === 'flow' || view === 'xavvy_forms' || view === 'casematrix' || view === 'casematrix_types' || view === 'reports' || view === 'docuforge' || view === 'rules') {
    const visibleWorkflows = currentFolderId === null
      ? workflows
      : currentFolderId === '__root__'
        ? workflows.filter(w => !w.folder_id)
        : workflows.filter(w => w.folder_id === currentFolderId);
    return (
      <div style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary)', color: 'var(--text-primary)', overflow: 'hidden' }}>
        {/* Home Toolbar */}
        <div style={{ padding: '0 16px', height: '56px', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          {/* Brand — clickable to go home */}
          <div onClick={() => setView('home')} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', flexShrink: 0 }} title="Home">
            {branding.logo_url
              ? <img src={branding.logo_url} alt={branding.app_name || 'xavvy'} style={{ height: '28px', maxWidth: '120px', objectFit: 'contain' }} />
              : <img src={logo} alt={branding.app_name || 'xavvy'} style={{ height: '28px', width: '28px' }} />
            }
            {!isMobile && <span style={{ color: 'var(--text-primary)', fontSize: '18px', fontWeight: 700 }}>{branding.app_name || 'xavvy'}</span>}
          </div>
          {licenseInfo && (
            <button
              onClick={() => isAdmin && setShowLicenseManager(true)}
              title={isAdmin ? 'Manage License' : `Plan: ${licenseInfo.plan}`}
              style={{
                padding: '2px 9px', borderRadius: '10px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
                background: licenseInfo.plan === 'community' ? 'rgba(107,114,128,0.15)' : licenseInfo.plan === 'pro' ? 'rgba(99,102,241,0.15)' : licenseInfo.plan.startsWith('casematrix') ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)',
                color: licenseInfo.plan === 'community' ? '#9ca3af' : licenseInfo.plan === 'pro' ? '#818cf8' : licenseInfo.plan.startsWith('casematrix') ? '#10b981' : '#fbbf24',
                border: `1px solid ${licenseInfo.plan === 'community' ? '#6b728030' : licenseInfo.plan === 'pro' ? '#6366f130' : licenseInfo.plan.startsWith('casematrix') ? '#10b98130' : '#f59e0b30'}`,
                cursor: isAdmin ? 'pointer' : 'default', flexShrink: 0,
              }}
            >
              {licenseInfo.plan === 'community' ? 'Community' : licenseInfo.plan === 'pro' ? 'Pro' : licenseInfo.plan.startsWith('casematrix') ? 'casematrix' : 'Enterprise'}
            </button>
          )}

          {/* Workspace selector dropdown */}
          <div ref={wsDropdownRef} style={{ position: 'relative', flexShrink: 0 }}>
            <button
              onClick={() => { if (!showWsDropdown) fetchAllWorkspaces(); setShowWsDropdown(v => !v); }}
              data-tutorial-id="tutorial-home-workspaces"
              style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '4px 12px', color: 'var(--text-secondary)', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap', maxWidth: isMobile ? '120px' : 'none', overflow: 'hidden', textOverflow: 'ellipsis' }}
              title="Switch Workspace"
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentWorkspace ? currentWorkspace.name : 'Select Workspace'}</span>
              <span style={{ opacity: 0.5, fontSize: '10px', flexShrink: 0 }}>▾</span>
            </button>
            {showWsDropdown && (
              <div style={{ position: 'absolute', left: 0, top: '36px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '4px', minWidth: '220px', boxShadow: '0 6px 24px rgba(0,0,0,0.25)', zIndex: 10000, maxHeight: '300px', overflowY: 'auto' }}>
                {allWorkspaces.map(ws => (
                  <div key={ws.id} style={{ display: 'flex', alignItems: 'center', borderRadius: '6px', background: ws.id === currentWorkspace?.id ? 'var(--bg-primary)' : 'transparent' }}
                    onMouseOver={(e) => { if (ws.id !== currentWorkspace?.id) e.currentTarget.style.background = 'var(--bg-primary)'; }}
                    onMouseOut={(e) => { if (ws.id !== currentWorkspace?.id) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <button
                      onClick={() => { setCurrentWorkspace(ws); setShowWsDropdown(false); }}
                      style={{ flex: 1, textAlign: 'left', padding: '8px 12px', background: 'transparent', border: 'none', color: 'var(--text-primary)', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}
                    >
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ws.name}</span>
                      {ws.is_default && <span style={{ fontSize: '10px', color: 'var(--accent-color)', flexShrink: 0 }}>default</span>}
                      {ws.id === currentWorkspace?.id && <span style={{ color: 'var(--accent-color)', flexShrink: 0, fontSize: '12px' }}>✓</span>}
                    </button>
                    {!ws.is_default && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          axios.put(`${API_BASE_URL}/users/me/default-workspace`, { workspace_id: ws.id })
                            .then(() => {
                              setAllWorkspaces(prev => prev.map(w => ({ ...w, is_default: w.id === ws.id })));
                            })
                            .catch(() => {});
                        }}
                        title="Set as default workspace"
                        style={{ background: 'transparent', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', padding: '4px 8px', fontSize: '10px', flexShrink: 0, borderRadius: '4px' }}
                        onMouseOver={(e) => e.currentTarget.style.color = 'var(--accent-color)'}
                        onMouseOut={(e) => e.currentTarget.style.color = 'var(--text-tertiary)'}
                      >set default</button>
                    )}
                  </div>
                ))}
                {allWorkspaces.length === 0 && <div style={{ padding: '12px', color: 'var(--text-tertiary)', fontSize: '12px', textAlign: 'center' }}>No workspaces</div>}
              </div>
            )}
          </div>
          {/* Git Sync — available for flows and forms */}
          {!isMobile && (view === 'flow' || view === 'xavvy_forms' || view === 'home') && (currentWorkspace?.my_role === 'admin' || currentUser?.is_admin) && (
            <button
              onClick={() => { fetchGitSyncConfig(); setShowGitSyncModal(true); }}
              title="Git Sync — configure push/pull for this workspace"
              style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '4px 12px', color: gitSyncConfig ? (gitSyncDirty ? 'var(--warning-color)' : 'var(--success-color)') : 'var(--text-secondary)', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              {gitSyncConfig ? (gitSyncDirty ? '⎇ Git Sync ●' : '⎇ Git Synced') : '⎇ Git Sync'}
            </button>
          )}
          {/* Environments — flows only */}
          {!isMobile && view === 'flow' && (
            <button
              onClick={() => setShowEnvModal(true)}
              title="Manage Environments — run workflows against different credential sets"
              style={tabPillStyle(false)}
            >
              🌐 Environments{workspaceEnvironments.length > 0 ? ` (${workspaceEnvironments.length})` : ''}
            </button>
          )}

          {/* ── Nav tabs: Flows, Forms, Cases, Reports ── monochrome pills, color comes from icons only */}
          {!isFormsOnly && (
            <button
              onClick={() => { setView(view === 'flow' ? 'home' : 'flow'); if (view !== 'flow') { fetchWorkflows(); fetchFolders(); } }}
              title="Workflow Automation"
              style={tabPillStyle(view === 'flow')}
            >
              ⚡ Flows
            </button>
          )}
          <button
            onClick={() => setView('xavvy_forms')}
            title="Xavvy Forms"
            style={tabPillStyle(view === 'xavvy_forms')}
          >
            📝 Forms
          </button>
          {!isFormsOnly && currentWorkspace && licenseInfo && !licenseInfo.locked_features?.includes('casematrix.case_management') && (
            <button
              onClick={() => setView('casematrix')}
              title="Case Manager"
              style={tabPillStyle(view === 'casematrix' || view === 'casematrix_types')}
            >
              📋 Cases
            </button>
          )}
          {!isFormsOnly && currentWorkspace && licenseInfo && !licenseInfo.locked_features?.includes('reports.designer') && (
            <button
              onClick={() => setView('reports')}
              title="Reports & Dashboards"
              style={tabPillStyle(view === 'reports')}
            >
              📊 Reports
            </button>
          )}
          {!isFormsOnly && currentWorkspace && (
            <button
              onClick={() => setView('docuforge')}
              title="docuforge — Intelligent Document Processing"
              style={tabPillStyle(view === 'docuforge')}
            >
              📥 docuforge
            </button>
          )}
          {!isFormsOnly && currentWorkspace && (
            <button
              onClick={() => setView('rules')}
              title="Rules Engine"
              style={tabPillStyle(view === 'rules')}
            >
              ⚖️ Rules
            </button>
          )}

          <div style={{ flex: 1 }} />

          {/* Notifications */}
          <NotificationCenter
            token={token}
            onNavigate={(params) => {
              if (params.workflow_id) {
                loadWorkflow(params.workflow_id);
              }
            }}
          />

          {/* Theme toggle — hidden on mobile to save space */}
          {!isMobile && (
            <button
              onClick={cycleTheme}
              data-tutorial-id="tutorial-home-theme"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '6px', color: 'var(--text-secondary)', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0 }}
              onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'}
              onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
              title={`Theme: ${theme}`}
            >
              {getThemeIcon()}
            </button>
          )}

          {/* User menu */}
          <div ref={userMenuRef} style={{ position: 'relative', flexShrink: 0 }}>
            <button
              onClick={() => setShowUserMenu(v => !v)}
              style={{ background: showUserMenu ? 'var(--bg-primary)' : 'transparent', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '4px 10px 4px 6px', color: 'var(--text-primary)', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 500 }}
              title="Account & Settings"
            >
              <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'var(--accent-color)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#fff', fontWeight: 700, flexShrink: 0 }}>
                {currentUser?.username?.[0]?.toUpperCase() || '?'}
              </span>
              <span style={{ maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {currentUser?.username || 'Account'}
              </span>
              <span style={{ fontSize: '10px', opacity: 0.5, flexShrink: 0 }}>▾</span>
            </button>

            {showUserMenu && (
              <div ref={userMenuDropdownRef} style={{ position: 'absolute', right: 0, top: '44px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '6px', minWidth: '200px', boxShadow: '0 6px 24px rgba(0,0,0,0.2)', zIndex: 10000, display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border-color)', marginBottom: '4px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{currentUser?.username}</div>
                  {currentUser?.email && <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '1px' }}>{currentUser.email}</div>}
                </div>
                {[
                  { label: '👤 Profile', id: 'tutorial-home-profile', action: () => { setReturnView('home'); setView('profile'); setShowUserMenu(false); } },
                  { label: '🏢 Manage Workspaces', action: () => { setReturnView('home'); setView('workspaces'); setShowUserMenu(false); } },
                  { label: '🔑 Credentials', id: 'tutorial-home-credentials', action: () => { setReturnView('home'); setInitialCredentialServiceType(null); setView('credentials'); setShowUserMenu(false); } },
                  ...(!isFormsOnly ? [{ label: '🌐 Global Variables', action: () => { setReturnView('home'); setView('variables'); setShowUserMenu(false); } }] : []),
                  ...(!isFormsOnly ? [{ label: '🗄️ Data Store', action: () => { setReturnView('home'); setView('datastore'); setShowUserMenu(false); } }] : []),
                  { label: '📋 Tables', action: () => { setReturnView('home'); setView('tables'); setShowUserMenu(false); } },
                  ...(!isFormsOnly ? [{ label: '📅 Schedule Calendar', action: () => { setReturnView('home'); setView('scheduler_calendar'); setShowUserMenu(false); } }] : []),
                  ...(!isFormsOnly ? [{ label: '🔗 Webhooks', action: () => { setReturnView('home'); setView('webhooks'); setShowUserMenu(false); } }] : []),
                  ...(isAdmin && !isFormsOnly ? [{ label: '🛣️ Custom API Routes', action: () => { setReturnView('home'); setView('customRoutes'); setShowUserMenu(false); } }] : []),
                  ...(!isFormsOnly ? [{ label: '🛒 Marketplace', action: () => { setReturnView('home'); setView('marketplace'); setShowUserMenu(false); } }] : []),
                  { label: '📋 Audit Logs', id: 'tutorial-home-audit-logs', action: () => { setReturnView('home'); setView('audit_logs'); setShowUserMenu(false); } },
                  ...(!isFormsOnly ? [{ label: '📱 Run Monitor', action: () => { setReturnView('home'); setView('mobile_monitor'); setShowUserMenu(false); } }] : []),
                  ...(isAdmin ? [
                    { label: '⚙ Admin Panel', action: () => { setReturnView('home'); setView('admin'); setShowUserMenu(false); } },
                    ...(!isFormsOnly ? [{ label: '📈 Monitoring', action: () => { setReturnView('home'); setView('monitoring'); setShowUserMenu(false); } }] : []),
                    ...(!isFormsOnly ? [{ label: '🧩 Plugin Governance', action: () => { setReturnView('home'); setView('plugins'); setShowUserMenu(false); } }] : []),
                    { label: '🔑 License', action: () => { setShowLicenseManager(true); setShowUserMenu(false); } },
                  ] : []),
                  { label: '🎓 Tutorial', action: () => { openTutorial(0); setShowUserMenu(false); } },
                ].map(item => (
                  <button key={item.label} onClick={item.action} data-tutorial-id={item.id} style={menuItemSt()}
                    onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'}
                    onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                  >{item.label}</button>
                ))}
                <div style={{ borderTop: '1px solid var(--border-color)', margin: '4px 0' }} />
                <button onClick={handleLogout} style={{ ...menuItemSt(), color: 'var(--error-color)' }}
                  onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'}
                  onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                >↪ Logout</button>
              </div>
            )}
          </div>
        </div>
        {/* Body: hero cards (home) or folder sidebar + workflow grid (flow) */}
        {view === 'home' ? (
          <div style={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', justifyContent: 'center', maxWidth: '900px' }}>

              {/* Flow card — always shown unless forms-only mode */}
              {!isFormsOnly && (
                <button
                  onClick={() => { setView('flow'); fetchWorkflows(); fetchFolders(); }}
                  style={{ flex: '1 1 240px', maxWidth: '280px', minHeight: '200px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '28px 24px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '12px', textAlign: 'left', transition: 'border-color 0.2s, box-shadow 0.2s' }}
                  onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--accent-color)'; e.currentTarget.style.boxShadow = '0 0 0 1px var(--accent-color)'; }}
                  onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--border-color)'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(99,102,241,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>⚡</div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)' }}>Flow</div>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>Visual workflow automation — build, schedule, and monitor workflows with 140+ nodes.</div>
                  <div style={{ fontSize: '12px', color: 'var(--accent-color)', fontWeight: 600, marginTop: 'auto' }}>{workflows.length} workflow{workflows.length !== 1 ? 's' : ''}</div>
                </button>
              )}

              {/* Forms card */}
              <button
                onClick={() => { setReturnView('home'); setView('xavvy_forms'); }}
                style={{ flex: '1 1 240px', maxWidth: '280px', minHeight: '200px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '28px 24px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '12px', textAlign: 'left', transition: 'border-color 0.2s, box-shadow 0.2s' }}
                onMouseOver={(e) => { e.currentTarget.style.borderColor = '#22c55e'; e.currentTarget.style.boxShadow = '0 0 0 1px #22c55e'; }}
                onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--border-color)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(34,197,94,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>📝</div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)' }}>Forms</div>
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>Design, publish, and manage forms with analytics and submission tracking.</div>
                <div style={{ fontSize: '12px', color: '#22c55e', fontWeight: 600, marginTop: 'auto' }}>{heroFormCount} form{heroFormCount !== 1 ? 's' : ''}</div>
              </button>

              {/* Cases card — shown when casematrix license and workspace selected */}
              {!isFormsOnly && currentWorkspace && licenseInfo && !licenseInfo.locked_features?.includes('casematrix.case_management') && (
                <button
                  onClick={() => { setReturnView('home'); setView('casematrix'); }}
                  style={{ flex: '1 1 240px', maxWidth: '280px', minHeight: '200px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '28px 24px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '12px', textAlign: 'left', transition: 'border-color 0.2s, box-shadow 0.2s' }}
                  onMouseOver={(e) => { e.currentTarget.style.borderColor = '#f59e0b'; e.currentTarget.style.boxShadow = '0 0 0 1px #f59e0b'; }}
                  onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--border-color)'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(245,158,11,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>📋</div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)' }}>Cases</div>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>Case management and BPM — manage cases, tasks, SLAs, and process automation.</div>
                  <div style={{ fontSize: '12px', color: '#f59e0b', fontWeight: 600, marginTop: 'auto' }}>{heroCaseTypeCount} case type{heroCaseTypeCount !== 1 ? 's' : ''}</div>
                </button>
              )}

              {/* Reports card — shown when reports designer license and workspace selected */}
              {!isFormsOnly && currentWorkspace && licenseInfo && !licenseInfo.locked_features?.includes('reports.designer') && (
                <button
                  onClick={() => { setReturnView('home'); setView('reports'); }}
                  style={{ flex: '1 1 240px', maxWidth: '280px', minHeight: '200px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '28px 24px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '12px', textAlign: 'left', transition: 'border-color 0.2s, box-shadow 0.2s' }}
                  onMouseOver={(e) => { e.currentTarget.style.borderColor = '#06b6d4'; e.currentTarget.style.boxShadow = '0 0 0 1px #06b6d4'; }}
                  onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--border-color)'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(6,182,212,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>📊</div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)' }}>Reports</div>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>Build reports and dashboards over cases, tasks, SLAs, and workflow runs — with charts and CSV export.</div>
                  <div style={{ fontSize: '12px', color: '#06b6d4', fontWeight: 600, marginTop: 'auto' }}>Designer & dashboards</div>
                </button>
              )}

              {/* Rules Engine card — shown when workspace selected and not forms-only */}
              {!isFormsOnly && currentWorkspace && (
                <button
                  onClick={() => { setReturnView('home'); setView('rules'); }}
                  style={{ flex: '1 1 240px', maxWidth: '280px', minHeight: '200px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '28px 24px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '12px', textAlign: 'left', transition: 'border-color 0.2s, box-shadow 0.2s' }}
                  onMouseOver={(e) => { e.currentTarget.style.borderColor = '#8b5cf6'; e.currentTarget.style.boxShadow = '0 0 0 1px #8b5cf6'; }}
                  onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--border-color)'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(139,92,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>⚖️</div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)' }}>Rules Engine</div>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>Decision tables, condition/action rules, decision trees, and scorecards — no-code decisioning with full audit traces.</div>
                  <div style={{ fontSize: '12px', color: '#8b5cf6', fontWeight: 600, marginTop: 'auto' }}>4 artifact types</div>
                </button>
              )}

              {/* Docuforge card — shown when workspace selected and not forms-only (docuforge.basic is Community+) */}
              {!isFormsOnly && currentWorkspace && (
                <button
                  onClick={() => { setReturnView('home'); setView('docuforge'); }}
                  style={{ flex: '1 1 240px', maxWidth: '280px', minHeight: '200px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '16px', padding: '28px 24px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '12px', textAlign: 'left', transition: 'border-color 0.2s, box-shadow 0.2s' }}
                  onMouseOver={(e) => { e.currentTarget.style.borderColor = '#ec4899'; e.currentTarget.style.boxShadow = '0 0 0 1px #ec4899'; }}
                  onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--border-color)'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'rgba(236,72,153,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>📥</div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text-primary)' }}>Docuforge</div>
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>Intelligent document processing — ingest, classify, and extract structured fields with human-in-the-loop review.</div>
                  <div style={{ fontSize: '12px', color: '#ec4899', fontWeight: 600, marginTop: 'auto' }}>{heroDocuforgeCount} document{heroDocuforgeCount !== 1 ? 's' : ''}</div>
                </button>
              )}

            </div>
          </div>
        ) : view === 'xavvy_forms' ? (
          <XavvyForms
            authToken={token}
            workspaceId={currentWorkspace?.id}
            onDesign={(id) => { setEditingFormId(id); setView('form_designer'); }}
            onBack={null}
            isFormsOnly={isFormsOnly}
          />
        ) : view === 'casematrix' && currentWorkspace ? (
          <CaseManager onBack={null} onManageTypes={() => setView('casematrix_types')} workspaceId={currentWorkspace.id} isAdmin={isAdmin || currentWorkspace?.my_role === 'admin'} navTarget={caseNavTarget} onNavTargetConsumed={() => setCaseNavTarget(null)} />
        ) : view === 'casematrix_types' && currentWorkspace ? (
          <CaseTypeAdmin onBack={() => setView('casematrix')} workspaceId={currentWorkspace.id} currentUser={currentUser} />
        ) : view === 'reports' && currentWorkspace ? (
          <Reports workspaceId={currentWorkspace.id} onBack={() => setView('home')} />
        ) : view === 'docuforge' && currentWorkspace ? (
          <Docuforge workspaceId={currentWorkspace.id} onBack={() => setView('home')} />
        ) : view === 'rules' && currentWorkspace ? (
          <Rules workspaceId={currentWorkspace.id} onBack={() => setView('home')} />
        ) : (
        <>
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden', flexDirection: 'column' }}>

          {/* Mobile: folder tabs row */}
          {isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, overflowX: 'auto', padding: '8px 12px', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)', flexShrink: 0, scrollbarWidth: 'none' }}>
              <button onClick={() => setCurrentFolderId(null)} style={{ flexShrink: 0, padding: '5px 14px', borderRadius: 999, border: `1px solid ${currentFolderId === null ? 'var(--accent-color)' : 'var(--border-color)'}`, background: currentFolderId === null ? 'var(--accent-color)' : 'transparent', color: currentFolderId === null ? '#fff' : 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>All</button>
              <button onClick={() => setCurrentFolderId('__root__')} style={{ flexShrink: 0, padding: '5px 14px', borderRadius: 999, border: `1px solid ${currentFolderId === '__root__' ? 'var(--accent-color)' : 'var(--border-color)'}`, background: currentFolderId === '__root__' ? 'var(--accent-color)' : 'transparent', color: currentFolderId === '__root__' ? '#fff' : 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>Uncategorized</button>
              {folders.map(folder => (
                <button key={folder.id} onClick={() => setCurrentFolderId(folder.id)} style={{ flexShrink: 0, padding: '5px 14px', borderRadius: 999, border: `1px solid ${currentFolderId === folder.id ? 'var(--accent-color)' : 'var(--border-color)'}`, background: currentFolderId === folder.id ? 'var(--accent-color)' : 'transparent', color: currentFolderId === folder.id ? '#fff' : 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>📁 {folder.name}</button>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Folder Tree Sidebar — desktop only */}
          {!isMobile && <div style={{ width: '200px', flexShrink: 0, borderRight: '1px solid var(--border-color)', background: 'var(--bg-secondary)', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
            <div style={{ padding: '12px 12px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Folders</span>
              <button
                title="New folder"
                onClick={() => { setEditingFolder(null); setFolderFormName(''); setShowFolderForm(v => !v); }}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '16px', lineHeight: 1, padding: '2px 4px', borderRadius: '4px' }}
                onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
              >+</button>
            </div>
            {showFolderForm && !editingFolder && (
              <div style={{ padding: '0 8px 8px' }}>
                <input
                  autoFocus
                  value={folderFormName}
                  onChange={e => setFolderFormName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleCreateFolder(); if (e.key === 'Escape') { setShowFolderForm(false); setFolderFormName(''); } }}
                  placeholder="Folder name"
                  style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', borderRadius: '4px', color: 'var(--text-primary)', fontSize: '12px' }}
                />
              </div>
            )}
            {/* All Workflows entry */}
            <button
              onClick={() => setCurrentFolderId(null)}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px', background: currentFolderId === null ? 'var(--accent-color)' : 'transparent', color: currentFolderId === null ? '#fff' : 'var(--text-primary)', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '13px', width: '100%' }}
              onMouseOver={(e) => { if (currentFolderId !== null) e.currentTarget.style.background = 'var(--bg-primary)'; }}
              onMouseOut={(e) => { if (currentFolderId !== null) e.currentTarget.style.background = 'transparent'; }}
            >
              <span>📂</span> All Workflows
              <span style={{ marginLeft: 'auto', fontSize: '11px', color: currentFolderId === null ? 'rgba(255,255,255,0.7)' : 'var(--text-tertiary)' }}>{workflows.length}</span>
            </button>
            {/* Uncategorized entry */}
            <button
              onClick={() => setCurrentFolderId('__root__')}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px 7px 20px', background: currentFolderId === '__root__' ? 'var(--bg-primary)' : 'transparent', color: 'var(--text-secondary)', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '12px', width: '100%' }}
              onMouseOver={(e) => { if (currentFolderId !== '__root__') e.currentTarget.style.background = 'var(--bg-primary)'; }}
              onMouseOut={(e) => { if (currentFolderId !== '__root__') e.currentTarget.style.background = 'transparent'; }}
            >
              <span>📄</span> Uncategorized
              <span style={{ marginLeft: 'auto', fontSize: '11px', color: 'var(--text-tertiary)' }}>{workflows.filter(w => !w.folder_id).length}</span>
            </button>
            {/* Named folders */}
            {folders.map(folder => (
              <div key={folder.id} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                {editingFolder?.id === folder.id ? (
                  <input
                    autoFocus
                    value={folderFormName}
                    onChange={e => setFolderFormName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleRenameFolder(); if (e.key === 'Escape') { setEditingFolder(null); setFolderFormName(''); } }}
                    style={{ flex: 1, margin: '2px 8px', padding: '4px 8px', background: 'var(--bg-input)', border: '1px solid var(--border-color)', borderRadius: '4px', color: 'var(--text-primary)', fontSize: '12px' }}
                  />
                ) : (
                  <button
                    onClick={() => setCurrentFolderId(folder.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 8px 7px 20px', background: currentFolderId === folder.id ? 'var(--accent-color)' : 'transparent', color: currentFolderId === folder.id ? '#fff' : 'var(--text-primary)', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '13px', flex: 1, minWidth: 0 }}
                    onMouseOver={(e) => { if (currentFolderId !== folder.id) e.currentTarget.style.background = 'var(--bg-primary)'; }}
                    onMouseOut={(e) => { if (currentFolderId !== folder.id) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span>📁</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{folder.name}</span>
                    <span style={{ fontSize: '11px', color: currentFolderId === folder.id ? 'rgba(255,255,255,0.7)' : 'var(--text-tertiary)', flexShrink: 0 }}>{workflows.filter(w => w.folder_id === folder.id).length}</span>
                  </button>
                )}
                {editingFolder?.id !== folder.id && (
                  <div style={{ display: 'flex', gap: '2px', paddingRight: '4px', flexShrink: 0 }}>
                    <button
                      title="Rename"
                      onClick={() => { setEditingFolder(folder); setFolderFormName(folder.name); setShowFolderForm(false); }}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: '11px', padding: '3px 4px', borderRadius: '3px' }}
                      onMouseOver={(e) => e.currentTarget.style.color = 'var(--text-primary)'}
                      onMouseOut={(e) => e.currentTarget.style.color = 'var(--text-tertiary)'}
                    >✏</button>
                    <button
                      title="Delete folder"
                      onClick={() => handleDeleteFolder(folder.id)}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', fontSize: '11px', padding: '3px 4px', borderRadius: '3px' }}
                      onMouseOver={(e) => e.currentTarget.style.color = 'var(--error-color)'}
                      onMouseOut={(e) => e.currentTarget.style.color = 'var(--text-tertiary)'}
                    >✕</button>
                  </div>
                )}
              </div>
            ))}
          </div>}

          {/* Main workflow content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '16px 12px 100px' : '28px 40px' }}>
          {/* Breadcrumb / current folder header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px' }}>
            {!isMobile && <h2 style={{ margin: 0, flexShrink: 0 }}>
              {currentFolderId && currentFolderId !== '__root__'
                ? folders.find(f => f.id === currentFolderId)?.name || 'Folder'
                : currentFolderId === '__root__'
                  ? 'Uncategorized'
                  : (currentWorkspace ? currentWorkspace.name : 'My Workflows')}
            </h2>}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: isMobile ? 'nowrap' : 'wrap', justifyContent: 'flex-end', flex: isMobile ? 1 : 'initial' }}>
              <button
                onClick={fetchWorkflows}
                data-tutorial-id="tutorial-home-refresh"
                style={{ background: 'transparent', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '5px 8px', cursor: 'pointer', fontSize: '14px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center' }}
                title="Refresh"
              >🔄</button>
              {!isMobile && <>
                <button
                  onClick={() => setShowTemplatesModal(true)}
                  style={{ background: 'transparent', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '5px 10px', cursor: 'pointer', fontSize: '13px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '5px' }}
                >📋 Templates</button>
                <button
                  onClick={() => importFileRef.current?.click()}
                  disabled={currentWorkspace?.my_role === 'viewer'}
                  style={{ background: 'transparent', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '5px 10px', cursor: currentWorkspace?.my_role === 'viewer' ? 'not-allowed' : 'pointer', fontSize: '13px', color: currentWorkspace?.my_role === 'viewer' ? 'var(--text-tertiary)' : 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '5px', opacity: currentWorkspace?.my_role === 'viewer' ? 0.5 : 1 }}
                  title="Import workflows from a .xavvy bundle"
                >⤵ Import</button>
                <button
                  onClick={() => setShowZapierImport(true)}
                  disabled={currentWorkspace?.my_role === 'viewer'}
                  style={{ background: 'transparent', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '5px 10px', cursor: currentWorkspace?.my_role === 'viewer' ? 'not-allowed' : 'pointer', fontSize: '13px', color: currentWorkspace?.my_role === 'viewer' ? 'var(--text-tertiary)' : 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '5px', opacity: currentWorkspace?.my_role === 'viewer' ? 0.5 : 1 }}
                  title="Import an existing Zapier Zap or Make scenario"
                >⚡ Migrate</button>
                <button
                  onClick={exportAllWorkflows}
                  disabled={workflows.length === 0}
                  style={{ background: 'transparent', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '5px 10px', cursor: workflows.length === 0 ? 'not-allowed' : 'pointer', fontSize: '13px', color: workflows.length === 0 ? 'var(--text-tertiary)' : 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '5px', opacity: workflows.length === 0 ? 0.5 : 1 }}
                  title="Export all visible workflows as a .xavvy bundle"
                >⤴ Export All</button>
              </>}
              <input ref={importFileRef} type="file" accept=".xavvy" style={{ display: 'none' }} onChange={handleImportFile} />
              {!isMobile && (
                <button
                  onClick={createNewWorkflow}
                  data-tutorial-id="tutorial-home-new-workflow"
                  disabled={currentWorkspace?.my_role === 'viewer'}
                  style={{ padding: '6px 16px', background: currentWorkspace?.my_role === 'viewer' ? 'var(--button-disabled-bg)' : 'var(--accent-color)', color: currentWorkspace?.my_role === 'viewer' ? 'var(--text-tertiary)' : 'white', border: 'none', borderRadius: '6px', cursor: currentWorkspace?.my_role === 'viewer' ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '13px', flexShrink: 0 }}
                >+ New Workflow</button>
              )}
            </div>
          </div>

          {/* Bulk actions — desktop only */}
          {!isMobile && visibleWorkflows.length > 0 && (
            <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', padding: '9px 12px', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-secondary)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                <input
                  type="checkbox"
                  checked={visibleWorkflows.length > 0 && visibleWorkflows.every(wf => selectedWorkflowIds.includes(wf.id))}
                  onChange={toggleSelectAllWorkflows}
                />
                Select all
              </label>
              <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{selectedWorkflowIds.length} selected</span>
              {selectedWorkflowIds.length > 0 && (<>
                <button onClick={bulkDeleteWorkflows} style={{ padding: '3px 9px', fontSize: '11px', fontWeight: 500, background: 'transparent', color: 'var(--error-color)', border: '1px solid var(--error-color)', borderRadius: '5px', cursor: 'pointer' }}>Delete ({selectedWorkflowIds.length})</button>
                <button onClick={() => setShowBulkMoveModal(true)} style={{ padding: '3px 9px', fontSize: '11px', fontWeight: 500, background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: '5px', cursor: 'pointer' }}>Workspace ({selectedWorkflowIds.length})</button>
                <button onClick={() => { setWorkflowToMoveToFolder(null); setShowMoveToFolderModal(true); }} style={{ padding: '3px 9px', fontSize: '11px', fontWeight: 500, background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: '5px', cursor: 'pointer' }}>Folder ({selectedWorkflowIds.length})</button>
                <button onClick={() => bulkPauseResumeWorkflows(true)} style={{ padding: '3px 9px', fontSize: '11px', fontWeight: 500, background: 'transparent', color: 'var(--warning-color)', border: '1px solid var(--warning-color)', borderRadius: '5px', cursor: 'pointer' }}>Pause ({selectedWorkflowIds.length})</button>
                <button onClick={() => bulkPauseResumeWorkflows(false)} style={{ padding: '3px 9px', fontSize: '11px', fontWeight: 500, background: 'transparent', color: 'var(--success-color)', border: '1px solid var(--success-color)', borderRadius: '5px', cursor: 'pointer' }}>Resume ({selectedWorkflowIds.length})</button>
              </>)}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(250px, 1fr))', gap: isMobile ? '10px' : '20px' }}>
            {visibleWorkflows.map(wf => (
              isMobile ? (
                /* Mobile card — tap to open action sheet */
                <div
                  key={wf.id}
                  onClick={() => setMobileWfSheet(wf)}
                  style={{ background: 'var(--bg-node)', padding: '14px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{wf.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 3 }}>{formatLocalDate(wf.updated_at)}</div>
                  </div>
                  {wf.is_paused && (
                    <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 6px', borderRadius: '10px', flexShrink: 0, background: 'var(--bg-tertiary)', color: 'var(--warning-color)', border: '1px solid var(--warning-color)' }}>⏸ Paused</span>
                  )}
                  {wf.publish_status && wf.publish_status !== 'draft' && (
                    <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 6px', borderRadius: '10px', flexShrink: 0, background: wf.publish_status === 'published' ? 'var(--success-color)' : 'var(--warning-color)', color: 'var(--text-inverse)' }}>
                      {wf.publish_status === 'published' ? 'Live' : 'Review'}
                    </span>
                  )}
                  <span style={{ color: 'var(--text-tertiary)', fontSize: 18, flexShrink: 0 }}>›</span>
                </div>
              ) : (
              <div
                key={wf.id}
                onClick={() => loadWorkflow(wf.id)}
                style={{
                  background: 'var(--bg-node)',
                  padding: '20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  border: selectedWorkflowIds.includes(wf.id) ? '1px solid var(--accent-color)' : '1px solid var(--border-color)',
                  boxShadow: selectedWorkflowIds.includes(wf.id) ? '0 0 0 2px var(--accent-color)' : 'none',
                  transition: 'border-color 0.15s, box-shadow 0.15s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={selectedWorkflowIds.includes(wf.id)}
                    onChange={(e) => toggleWorkflowSelection(e, wf.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>{wf.name}</h3>
                  {wf.is_paused && (
                    <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 6px', borderRadius: '10px', flexShrink: 0, background: 'var(--bg-tertiary)', color: 'var(--warning-color)', border: '1px solid var(--warning-color)' }}>⏸ Paused</span>
                  )}
                  {wf.publish_status && wf.publish_status !== 'draft' && (
                    <span style={{
                      fontSize: '10px', fontWeight: 600, padding: '2px 6px', borderRadius: '10px', flexShrink: 0,
                      background: wf.publish_status === 'published' ? 'var(--success-color)' : 'var(--warning-color)',
                      color: 'var(--text-inverse)'
                    }} title={wf.publish_status === 'draft_pending' ? 'Live — has unpublished edits' : undefined}>
                      {wf.publish_status === 'published' ? 'Published' : wf.publish_status === 'draft_pending' ? 'Live (Edits Pending)' : 'In Review'}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '0.8em', color: 'var(--text-tertiary)' }}>Updated: {formatLocalDate(wf.updated_at)}</div>
                <div style={{ display: 'flex', gap: '6px', marginTop: '12px', paddingTop: '10px', borderTop: '1px solid var(--border-color)', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
                      <button onClick={(e) => exportWorkflow(e, wf.id, wf.name)} title="Export as .xavvy file" style={{ padding: '3px 9px', fontSize: '11px', fontWeight: 500, background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: '5px', cursor: 'pointer' }}>⤴ Export</button>
                      <button onClick={(e) => { e.stopPropagation(); setWorkflowToMoveToFolder(wf); setShowMoveToFolderModal(true); }} disabled={!canMoveOrDeleteWorkflow(wf)} title="Move to folder" style={{ padding: '3px 9px', fontSize: '11px', fontWeight: 500, background: 'transparent', color: canMoveOrDeleteWorkflow(wf) ? 'var(--text-secondary)' : 'var(--text-tertiary)', border: '1px solid var(--border-color)', borderRadius: '5px', cursor: canMoveOrDeleteWorkflow(wf) ? 'pointer' : 'not-allowed', opacity: canMoveOrDeleteWorkflow(wf) ? 1 : 0.45 }}>📁 Folder</button>
                      <button onClick={(e) => onMoveClick(e, wf)} disabled={!canMoveOrDeleteWorkflow(wf)} title="Move to another workspace" style={{ padding: '3px 9px', fontSize: '11px', fontWeight: 500, background: 'transparent', color: canMoveOrDeleteWorkflow(wf) ? 'var(--text-secondary)' : 'var(--text-tertiary)', border: '1px solid var(--border-color)', borderRadius: '5px', cursor: canMoveOrDeleteWorkflow(wf) ? 'pointer' : 'not-allowed', opacity: canMoveOrDeleteWorkflow(wf) ? 1 : 0.45 }}>⇄ Workspace</button>
                    </div>
                    <button onClick={(e) => deleteWorkflow(e, wf.id)} disabled={!canMoveOrDeleteWorkflow(wf)} title="Delete workflow" style={{ padding: '3px 9px', fontSize: '11px', fontWeight: 500, background: 'transparent', color: canMoveOrDeleteWorkflow(wf) ? 'var(--error-color)' : 'var(--text-tertiary)', border: `1px solid ${canMoveOrDeleteWorkflow(wf) ? 'var(--error-color)' : 'var(--border-color)'}`, borderRadius: '5px', cursor: canMoveOrDeleteWorkflow(wf) ? 'pointer' : 'not-allowed', opacity: canMoveOrDeleteWorkflow(wf) ? 1 : 0.45, marginLeft: 'auto' }}>Delete</button>
                </div>
                {wf.folder_id && folders.find(f => f.id === wf.folder_id) && (
                  <div style={{ marginTop: '6px', fontSize: '11px', color: 'var(--text-tertiary)' }}>
                    📁 {folders.find(f => f.id === wf.folder_id)?.name}
                  </div>
                )}
              </div>
              )
            ))}

            {visibleWorkflows.length === 0 && (
              <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)', border: '2px dashed var(--border-color)', borderRadius: '8px' }}>
                {currentFolderId ? 'No workflows in this folder.' : 'No workflows found. Create one to get started!'}
              </div>
            )}
          </div>
          </div>{/* end main content */}
          </div>{/* end inner flex row (sidebar + content) */}
        </div>{/* end outer body column */}


        {/* Mobile bottom sheet — workflow actions */}
        {isMobile && mobileWfSheet && (
          <>
            <div onClick={() => setMobileWfSheet(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000 }} />
            <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, background: 'var(--bg-secondary)', borderRadius: '16px 16px 0 0', padding: '20px 16px 36px', zIndex: 1001, boxShadow: '0 -4px 24px rgba(0,0,0,0.3)' }}>
              <div style={{ width: 40, height: 4, borderRadius: 2, background: 'var(--border-color)', margin: '0 auto 16px' }} />
              <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--text-primary)', marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{mobileWfSheet.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 20 }}>Updated {formatLocalDate(mobileWfSheet.updated_at)}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button
                  onClick={() => { setMobileWfSheet(null); setReturnView('home'); setView('mobile_monitor'); }}
                  style={{ padding: '14px 16px', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 10, color: 'var(--text-primary)', fontSize: 15, fontWeight: 500, cursor: 'pointer', textAlign: 'left' }}
                >📱 View Runs</button>
                <button
                  onClick={async () => {
                    try {
                      const t = localStorage.getItem('token');
                      await axios.post(`${API_BASE_URL}/workflows/${mobileWfSheet.id}/run`, {}, { headers: { Authorization: `Bearer ${t}` } });
                      setMobileWfSheet(null);
                      alert('Workflow started');
                    } catch (e) { alert(e?.response?.data?.detail || 'Failed to run'); }
                  }}
                  style={{ padding: '14px 16px', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 10, color: 'var(--text-primary)', fontSize: 15, fontWeight: 500, cursor: 'pointer', textAlign: 'left' }}
                >▶ Run Now</button>
                <button
                  onClick={async () => {
                    try {
                      const t = localStorage.getItem('token');
                      const paused = mobileWfSheet.is_paused;
                      await axios.put(`${API_BASE_URL}/workflows/${mobileWfSheet.id}`, { is_paused: !paused }, { headers: { Authorization: `Bearer ${t}` } });
                      setMobileWfSheet(null);
                      fetchWorkflows();
                    } catch (e) { alert(e?.response?.data?.detail || 'Failed'); }
                  }}
                  style={{ padding: '14px 16px', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 10, color: mobileWfSheet.is_paused ? 'var(--success-color)' : 'var(--warning-color)', fontSize: 15, fontWeight: 500, cursor: 'pointer', textAlign: 'left' }}
                >{mobileWfSheet.is_paused ? '▶ Resume Schedule' : '⏸ Pause Schedule'}</button>
                <button
                  onClick={(e) => { exportWorkflow(e, mobileWfSheet.id, mobileWfSheet.name); setMobileWfSheet(null); }}
                  style={{ padding: '14px 16px', background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: 10, color: 'var(--text-primary)', fontSize: 15, fontWeight: 500, cursor: 'pointer', textAlign: 'left' }}
                >⤴ Export</button>
                {canMoveOrDeleteWorkflow(mobileWfSheet) && (
                  <button
                    onClick={async (e) => {
                      setMobileWfSheet(null);
                      await deleteWorkflow(e, mobileWfSheet.id);
                    }}
                    style={{ padding: '14px 16px', background: 'var(--bg-primary)', border: '1px solid var(--error-color)', borderRadius: 10, color: 'var(--error-color)', fontSize: 15, fontWeight: 500, cursor: 'pointer', textAlign: 'left' }}
                  >🗑 Delete</button>
                )}
              </div>
            </div>
          </>
        )}

        {showMoveToFolderModal && (
          <MoveToFolderModal
            folders={folders}
            isBulk={!workflowToMoveToFolder}
            workflowName={workflowToMoveToFolder?.name}
            onClose={() => { setShowMoveToFolderModal(false); setWorkflowToMoveToFolder(null); }}
            onMove={(folderId) => workflowToMoveToFolder
              ? handleMoveWorkflowToFolder(workflowToMoveToFolder.id, folderId)
              : handleBulkMoveToFolder(folderId)
            }
          />
        )}
        {showMoveModal && workflowToMove && (
            <MoveWorkflowModal
                workflow={workflowToMove}
                onClose={() => {
                    setShowMoveModal(false);
                    setWorkflowToMove(null);
                }}
                onMove={(targetWorkspaceId) => onMoveWorkflow(workflowToMove.id, targetWorkspaceId)}
            />
        )}
        {showBulkMoveModal && (
            <MoveWorkflowModal
                selectedCount={selectedWorkflowIds.length}
                onClose={() => setShowBulkMoveModal(false)}
                onMove={bulkMoveWorkflows}
            />
        )}
        {importResult && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
            <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '24px', minWidth: '340px', maxWidth: '500px', width: '90%' }}>
              <h3 style={{ margin: '0 0 16px', color: 'var(--text-primary)' }}>Import Complete</h3>
              <p style={{ margin: '0 0 10px', color: 'var(--text-secondary)', fontSize: '14px' }}>
                {importResult.count} workflow{importResult.count !== 1 ? 's' : ''} imported successfully.
              </p>
              {importResult.imported.length > 0 && (
                <ul style={{ margin: '0 0 12px', paddingLeft: '18px', fontSize: '13px', color: 'var(--text-secondary)', maxHeight: '160px', overflowY: 'auto' }}>
                  {importResult.imported.map(wf => <li key={wf.id}>{wf.name}</li>)}
                </ul>
              )}
              {importResult.errors.length > 0 && (
                <>
                  <p style={{ margin: '0 0 6px', color: 'var(--error-color)', fontSize: '13px', fontWeight: 600 }}>{importResult.errors.length} error{importResult.errors.length !== 1 ? 's' : ''}:</p>
                  <ul style={{ margin: '0 0 12px', paddingLeft: '18px', fontSize: '12px', color: 'var(--error-color)', maxHeight: '80px', overflowY: 'auto' }}>
                    {importResult.errors.map((e, i) => <li key={i}>{e.file}: {e.error}</li>)}
                  </ul>
                </>
              )}
              <p style={{ margin: '0 0 16px', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                Imported workflows are paused — review and enable schedules before running.
              </p>
              <button
                onClick={() => setImportResult(null)}
                style={{ padding: '8px 20px', background: 'var(--accent-color)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', width: '100%' }}
              >
                Done
              </button>
            </div>
          </div>
        )}
        {showWorkflowStartModal && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 10002, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 'min(640px, 92vw)', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '20px', boxShadow: 'var(--shadow-strong)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                <h3 style={{ margin: 0, fontSize: '18px', color: 'var(--text-primary)' }}>Start New Workflow</h3>
                <button onClick={() => setShowWorkflowStartModal(false)} style={{ border: 'none', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '20px', lineHeight: 1 }}>×</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <button onClick={() => { setShowWorkflowStartModal(false); setShowTemplatesModal(true); }} style={{ textAlign: 'left', border: '1px solid var(--accent-color)', background: 'rgba(99,102,241,0.12)', color: 'var(--text-primary)', borderRadius: '10px', padding: '14px', cursor: 'pointer' }}>
                  <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>Use Template</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Choose a pre-built workflow and customize it quickly.</div>
                </button>
                <button onClick={startWithAiGenerator} style={{ textAlign: 'left', border: '1px solid var(--border-color)', background: 'var(--bg-primary)', color: 'var(--text-primary)', borderRadius: '10px', padding: '14px', cursor: 'pointer' }}>
                  <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>Generate with AI</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Describe what you need and let AI draft the workflow graph.</div>
                </button>
                <button onClick={createBlankWorkflow} style={{ textAlign: 'left', border: '1px solid var(--border-color)', background: 'var(--bg-primary)', color: 'var(--text-primary)', borderRadius: '10px', padding: '14px', cursor: 'pointer' }}>
                  <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>Start Blank</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Open an empty canvas and build from scratch.</div>
                </button>
                <button onClick={importWorkflowFromStart} style={{ textAlign: 'left', border: '1px solid var(--border-color)', background: 'var(--bg-primary)', color: 'var(--text-primary)', borderRadius: '10px', padding: '14px', cursor: 'pointer' }}>
                  <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>Import Bundle</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Upload an existing .xavvy file and continue editing.</div>
                </button>
                {/* §4.79 Multi-Agent Orchestration Studio */}
                <button onClick={createAgentGraphWorkflow} style={{ textAlign: 'left', border: '1px solid #8B5CF6', background: 'rgba(139,92,246,0.12)', color: 'var(--text-primary)', borderRadius: '10px', padding: '14px', cursor: 'pointer', gridColumn: '1 / -1' }}>
                  <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>🤖 Multi-Agent Graph <span style={{ fontSize: '10px', color: '#8B5CF6', marginLeft: '6px' }}>NEW</span></div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Build an agent graph: nodes are AI agents that delegate to one another and share memory. Streaming reasoning panel included.</div>
                </button>
              </div>
              <div style={{ marginTop: '12px', fontSize: '11px', color: 'var(--text-tertiary)' }}>
                Tip: in the editor press <strong>/</strong>, <strong>Space</strong>, or <strong>Ctrl/Cmd + K</strong> to quick-add nodes.
              </div>
            </div>
          </div>
        )}
        {showTemplatesModal && (
            <WorkflowTemplatesModal
                onClose={() => setShowTemplatesModal(false)}
                onSelectTemplate={loadTemplate}
            />
        )}
        {showZapierImport && (
            <ZapierImportModal
                onClose={() => setShowZapierImport(false)}
                onImported={handleZapierImported}
                workspaceId={currentWorkspace?.id || null}
            />
        )}
        </>
        )}

        {/* ── Shared modals (both home & flow) ── */}
        {showGitSyncModal && (
          <GitSyncModal
            workspace={currentWorkspace || { name: 'Workspace' }}
            syncBaseUrl={currentWorkspace ? `${API_BASE_URL}/workspaces/${currentWorkspace.id}/git-sync` : `${API_BASE_URL}/users/me/git-sync`}
            config={gitSyncConfig}
            onClose={() => setShowGitSyncModal(false)}
            onSaved={fetchGitSyncConfig}
          />
        )}
        {showLicenseManager && (
          <LicenseManager
            onClose={() => setShowLicenseManager(false)}
            token={token}
            isAdmin={isAdmin}
            productMode={branding?.product_mode || 'flow'}
          />
        )}
        {showEnvModal && (
          <EnvironmentManagerModal
            workspace={currentWorkspace}
            environments={workspaceEnvironments}
            credentials={[]}
            onClose={() => setShowEnvModal(false)}
            onSaved={fetchWorkspaceEnvironments}
          />
        )}
        <TutorialModal
          isOpen={showTutorial}
          steps={tutorialSteps}
          stepIndex={tutorialStepIndex}
          stepTargetSelector={tutorialTargetSelector}
          canAdvance={isTutorialStepComplete}
          disableAutoShow={disableTutorialAutoShow}
          setDisableAutoShow={setDisableTutorialAutoShow}
          forceAutoShow={isDemoUser}
          onPrevious={() => setTutorialStepIndex(prev => Math.max(0, prev - 1))}
          onNext={() => setTutorialStepIndex(prev => Math.min(tutorialSteps.length - 1, prev + 1))}
          onClose={() => closeTutorial(false)}
          onFinish={() => closeTutorial(true)}
        />

        {/* ── Global Command Palette (rendered via portal in commandPalettePortal) ── */}
        {commandPalettePortal}
      </div>
    );
  }

  return (
    <div style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary)', color: 'var(--text-primary)', overflow: 'hidden' }}>
      {/* Top Toolbar */}
      <div style={{
          height: '52px',
          padding: '0 12px',
          borderBottom: '1px solid var(--border-color)',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          background: 'var(--bg-secondary)',
          flexShrink: 0,
          position: 'relative'
      }}>
        {/* Left: Back + logo + name + unsaved dot */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            <button
                onClick={handleBackToHome}
                data-tutorial-id="tutorial-toolbar-back"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '6px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}
                onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                title={editorReturnView === 'home' ? 'Back to Home' : editorReturnView === 'mobile_monitor' ? 'Back to Run Monitor' : 'Back to Flows'}
            >
                <span style={{ fontSize: '16px', lineHeight: 1 }}>←</span>
            </button>
            <div style={{ height: '20px', width: '1px', background: 'var(--border-color)', flexShrink: 0 }} />
            {branding.logo_url
              ? <img src={branding.logo_url} alt={branding.app_name || 'xavvy'} style={{ height: '24px', maxWidth: '80px', objectFit: 'contain', flexShrink: 0 }} />
              : <img src={logo} alt={branding.app_name || 'xavvy'} style={{ height: '24px', width: '24px', flexShrink: 0 }} />
            }
            <input
              type="text"
              data-tutorial-id="tutorial-editor-workflow-name"
              value={workflowName}
              onChange={(e) => {
                setWorkflowName(e.target.value);
                if (showTutorial && activeTutorialStep?.id === 'editor-rename-workflow') {
                  setWorkflowNameTouched(true);
                }
              }}
              placeholder="Workflow Name"
              style={{ background: 'transparent', color: 'var(--text-primary)', border: '1px solid transparent', padding: '4px 8px', borderRadius: '6px', fontSize: '14px', fontWeight: 600, width: '150px', minWidth: '80px', transition: 'border-color 0.2s' }}
              onFocus={(e) => { e.target.style.border = '1px solid var(--accent-color)'; e.target.style.background = 'var(--bg-input)'; }}
              onBlur={(e) => { e.target.style.border = '1px solid transparent'; e.target.style.background = 'transparent'; }}
            />
            {hasUnsavedChanges && (
              <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: 'var(--warning-color)', flexShrink: 0, display: 'inline-block' }} title="Unsaved changes" />
            )}
        </div>

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Center: AI dropdown + Run + Save */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            {/* AI dropdown */}
            <div ref={aiMenuRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setShowAiMenu(v => !v)}
                data-tutorial-id="tutorial-toolbar-generate"
                disabled={currentWorkspace?.my_role === 'viewer'}
                style={{ ...btnStyle(showAiMenu ? 'var(--primary-color)' : 'transparent', currentWorkspace?.my_role === 'viewer'), border: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 10px', marginLeft: 0, fontSize: '13px' }}
                title="AI Actions"
              >
                ✨ AI <span style={{ fontSize: '9px', opacity: 0.7 }}>▾</span>
              </button>
              {showAiMenu && (
                <div ref={aiMenuDropdownRef} style={{ position: 'absolute', top: '38px', left: 0, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '6px', minWidth: '210px', boxShadow: '0 6px 24px rgba(0,0,0,0.2)', zIndex: 10000, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <button onClick={() => { setShowWorkflowGeneratorModal(true); setShowAiMenu(false); }} disabled={currentWorkspace?.my_role === 'viewer'} style={menuItemSt(currentWorkspace?.my_role === 'viewer')} onMouseOver={(e) => { if (currentWorkspace?.my_role !== 'viewer') e.currentTarget.style.background = 'var(--bg-primary)'; }} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>✨ Generate Workflow</button>
                  <button onClick={() => { setCopilotState({ isOpen: true, mode: 'explain', runId: null }); setShowAiMenu(false); }} data-tutorial-id="tutorial-toolbar-explain" disabled={!workflowId || currentWorkspace?.my_role === 'viewer'} style={menuItemSt(!workflowId || currentWorkspace?.my_role === 'viewer')} onMouseOver={(e) => { if (workflowId && currentWorkspace?.my_role !== 'viewer') e.currentTarget.style.background = 'var(--bg-primary)'; }} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>💡 Explain Workflow</button>
                  <button onClick={() => { setCopilotState({ isOpen: true, mode: 'inputs', runId: null }); setShowAiMenu(false); }} data-tutorial-id="tutorial-toolbar-test-inputs" disabled={!workflowId || currentWorkspace?.my_role === 'viewer'} style={menuItemSt(!workflowId || currentWorkspace?.my_role === 'viewer')} onMouseOver={(e) => { if (workflowId && currentWorkspace?.my_role !== 'viewer') e.currentTarget.style.background = 'var(--bg-primary)'; }} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>🧪 Test Inputs</button>
                  <div style={{ borderTop: '1px solid var(--border-color)', margin: '4px 2px' }} />
                  <button onClick={() => { setShowShortcutsModal(true); setShowAiMenu(false); }} style={menuItemSt()} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>⌨ Keyboard Shortcuts</button>
                  <div style={{ borderTop: '1px solid var(--border-color)', margin: '4px 2px' }} />
                  <button onClick={() => { setShowCopilotChat(v => !v); setShowAiMenu(false); }} disabled={!workflowId || currentWorkspace?.my_role === 'viewer'} style={{ ...menuItemSt(!workflowId || currentWorkspace?.my_role === 'viewer'), color: showCopilotChat ? 'var(--accent-color)' : 'var(--text-primary)', fontWeight: showCopilotChat ? 600 : 400 }} onMouseOver={(e) => { if (workflowId && currentWorkspace?.my_role !== 'viewer') e.currentTarget.style.background = 'var(--bg-primary)'; }} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>✨ Chat Copilot{showCopilotChat ? ' ✓' : ''}</button>
                  {workflowId && formTriggerUrl && (
                    <>
                      <div style={{ borderTop: '1px solid var(--border-color)', margin: '4px 2px' }} />
                      <div style={{ padding: '4px 8px', fontSize: '11px', color: 'var(--text-secondary)' }}>📋 Form URL</div>
                      <div style={{ display: 'flex', gap: '4px', padding: '4px 8px', background: 'var(--bg-primary)', borderRadius: '6px', margin: '2px 0' }}>
                        <input type="text" value={formTriggerUrl} readOnly style={{ flex: 1, background: 'transparent', color: 'var(--text-secondary)', border: 'none', outline: 'none', fontSize: '11px' }} onFocus={(e) => e.target.select()} />
                        <button onClick={() => navigator.clipboard?.writeText(formTriggerUrl)} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '11px', padding: '0 4px' }}>Copy</button>
                        <button onClick={() => window.open(formTriggerUrl, '_blank')} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '11px', padding: '0 4px' }}>↗</button>
                      </div>
                    </>
                  )}
                  {/* §4.79 Multi-Agent Orchestration Studio — toggles only in agent_graph workflows */}
                  {workflowType === 'agent_graph' && (
                    <>
                      <div style={{ borderTop: '1px solid var(--border-color)', margin: '4px 2px' }} />
                      <button onClick={() => { setShowReasoningPanel(!showReasoningPanel); setShowAiMenu(false); }} style={{ ...menuItemSt(), color: showReasoningPanel ? '#8B5CF6' : 'var(--text-primary)', fontWeight: showReasoningPanel ? 600 : 400 }} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>🧠 Reasoning Stream{showReasoningPanel ? ' ✓' : ''}</button>
                      {workflowId && (
                        <button onClick={() => { setShowMemoryPanel(!showMemoryPanel); setShowAiMenu(false); }} style={{ ...menuItemSt(), color: showMemoryPanel ? '#8B5CF6' : 'var(--text-primary)', fontWeight: showMemoryPanel ? 600 : 400 }} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>💾 Graph Memory{showMemoryPanel ? ' ✓' : ''}</button>
                      )}
                    </>
                  )}
                  {workflowId && nodes.some(n => n.type === 'chat_trigger' || n.data?.type === 'chat_trigger') && (<>
                    <div style={{ borderTop: '1px solid var(--border-color)', margin: '4px 2px' }} />
                    <button onClick={() => { if (!showAgentChat) setAgentChatStarted(true); setShowAgentChat(!showAgentChat); setShowAiMenu(false); }} style={{ ...menuItemSt(), color: showAgentChat ? 'var(--primary-color)' : 'var(--text-primary)', fontWeight: showAgentChat ? 600 : 400 }} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>🤖 Agent Mode{showAgentChat ? ' ✓' : ''}</button>
                    <button onClick={() => { togglePublicChat(); }} disabled={publicChatLoading || currentWorkspace?.my_role === 'viewer'} style={{ ...menuItemSt(publicChatLoading || currentWorkspace?.my_role === 'viewer'), color: publicChatEnabled ? 'var(--info-color)' : 'var(--text-primary)', fontWeight: publicChatEnabled ? 600 : 400 }} onMouseOver={(e) => { if (!(publicChatLoading || currentWorkspace?.my_role === 'viewer')) e.currentTarget.style.background = 'var(--bg-primary)'; }} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>🔗 {publicChatEnabled ? 'Public Link On' : 'Public Link'}</button>
                    {publicChatEnabled && publicChatLink && (
                      <div style={{ display: 'flex', gap: '4px', padding: '4px 8px', background: 'var(--bg-primary)', borderRadius: '6px', margin: '2px 0' }}>
                        <input type="text" value={publicChatLink} readOnly style={{ flex: 1, background: 'transparent', color: 'var(--text-secondary)', border: 'none', outline: 'none', fontSize: '11px' }} onFocus={(e) => e.target.select()} />
                        <button onClick={copyPublicChatLink} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '11px', padding: '0 4px' }}>Copy</button>
                        <button onClick={() => window.open(publicChatLink, '_blank')} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '11px', padding: '0 4px' }}>↗</button>
                      </div>
                    )}
                  </>)}
                </div>
              )}
            </div>

            {/* Publish dropdown — collapsed into one button */}
            {workflowId && currentWorkspace?.my_role !== 'viewer' && (
              <div ref={publishMenuRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowPublishMenu(v => !v)}
                  style={{
                    ...btnStyle(
                      workflowPublishStatus === 'published' ? 'var(--success-color)'
                      : workflowPublishStatus === 'review' ? 'var(--warning-color)'
                      : 'transparent',
                      false
                    ),
                    border: '1px solid var(--border-color)',
                    display: 'flex', alignItems: 'center', gap: '5px',
                    padding: '5px 10px', marginLeft: 0, fontSize: '12px'
                  }}
                  title="Publish lifecycle"
                >
                  {workflowPublishStatus === 'published' ? '✓ Published' : workflowPublishStatus === 'review' ? '⏳ In Review' : workflowPublishStatus === 'draft_pending' ? '⚠ Publish' : 'Publish'}
                  <span style={{ fontSize: '9px', opacity: 0.7 }}>▾</span>
                </button>
                {showPublishMenu && (
                  <div ref={publishMenuDropdownRef} style={{ position: 'absolute', top: '38px', right: 0, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '6px', minWidth: '200px', boxShadow: '0 6px 24px rgba(0,0,0,0.2)', zIndex: 10000, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <div style={{ padding: '4px 8px 6px', fontSize: '11px', color: 'var(--text-tertiary)', borderBottom: '1px solid var(--border-color)', marginBottom: '4px' }}>
                      Status: <strong style={{ color: 'var(--text-primary)' }}>{workflowPublishStatus}</strong>
                    </div>
                    {(workflowPublishStatus === 'draft' || workflowPublishStatus === 'draft_pending') && !currentWorkspace && (
                      <button onClick={async () => { try { const res = await axios.post(`${API_BASE_URL}/workflows/${workflowId}/publish`); setWorkflowPublishStatus(res.data.publish_status); setShowPublishMenu(false); } catch (e) { alert(e.response?.data?.detail || 'Failed to publish'); } }} style={menuItemSt()} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>
                        ✅ {workflowPublishStatus === 'draft_pending' ? 'Publish Changes' : 'Publish'}
                      </button>
                    )}
                    {(workflowPublishStatus === 'draft' || workflowPublishStatus === 'draft_pending') && currentWorkspace && (
                      <button onClick={async () => { try { const res = await axios.post(`${API_BASE_URL}/workflows/${workflowId}/submit-review`); setWorkflowPublishStatus(res.data.publish_status); setShowPublishMenu(false); } catch (e) { alert(e.response?.data?.detail || 'Failed to submit'); } }} style={menuItemSt()} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>
                        📋 Submit for Review
                      </button>
                    )}
                    {workflowPublishStatus === 'review' && (currentWorkspace?.my_role === 'admin' || currentUser?.is_admin) && (<>
                      <button onClick={async () => { try { const res = await axios.post(`${API_BASE_URL}/workflows/${workflowId}/publish`); setWorkflowPublishStatus(res.data.publish_status); setShowPublishMenu(false); } catch (e) { alert(e.response?.data?.detail || 'Failed to publish'); } }} style={menuItemSt()} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>
                        ✅ Approve & Publish
                      </button>
                      <button onClick={async () => { try { const res = await axios.post(`${API_BASE_URL}/workflows/${workflowId}/reject-review`); setWorkflowPublishStatus(res.data.publish_status); setShowPublishMenu(false); } catch (e) { alert(e.response?.data?.detail || 'Failed to reject'); } }} style={{ ...menuItemSt(), color: 'var(--error-color)' }} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>
                        ✕ Reject Review
                      </button>
                    </>)}
                    {workflowPublishStatus === 'published' && (!currentWorkspace || currentWorkspace?.my_role === 'admin' || currentUser?.is_admin) && (
                      <button onClick={async () => { if (!(await window.confirm('Unpublish this workflow? It will no longer run on schedule or webhook.'))) return; try { const res = await axios.post(`${API_BASE_URL}/workflows/${workflowId}/unpublish`); setWorkflowPublishStatus(res.data.publish_status); setShowPublishMenu(false); } catch (e) { alert(e.response?.data?.detail || 'Failed to unpublish'); } }} style={{ ...menuItemSt(), color: 'var(--text-secondary)' }} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>
                        ↩ Unpublish
                      </button>
                    )}
                    <div style={{ borderTop: '1px solid var(--border-color)', margin: '4px 2px', padding: '4px 8px 0', fontSize: '11px', color: 'var(--text-tertiary)', lineHeight: 1.4 }}>
                      Draft → Review → Published
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Run — with trigger picker when multiple trigger root nodes exist */}
            {(() => {
              const triggerNodes = nodes.filter((node) => {
                const nodeType = node.data?.type || node.type;
                if (nodeType === 'sticky_note' || nodeType === 'group') {
                  return false;
                }
                return !edges.some((edge) => edge.target === node.id) && triggerNodeTypes.has(String(nodeType || ''));
              });
              const disabled = !workflowId || isRunning || currentWorkspace?.my_role === 'viewer' || triggerNodes.length === 0;
              const hasMultipleTriggers = triggerNodes.length > 1;
              return (
                <div style={{ position: 'relative' }} data-tutorial-id="tutorial-editor-run-workflow">
                  <div style={{ display: 'flex', alignItems: 'stretch', borderRadius: '6px', overflow: 'hidden', opacity: disabled ? 0.5 : 1 }}>
                    <button
                      onClick={() => {
                        if (disabled) {
                          return;
                        }
                        if (hasMultipleTriggers) {
                          setShowTriggerPicker((prev) => !prev);
                          return;
                        }
                        runWorkflow([triggerNodes[0].id]);
                      }}
                      disabled={disabled}
                      style={{ ...btnStyle('var(--success-color)', disabled), borderRadius: hasMultipleTriggers ? '6px 0 0 6px' : '6px', padding: '6px 14px', fontWeight: 600, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 0, borderRight: hasMultipleTriggers ? '1px solid rgba(255,255,255,0.2)' : 'none' }}
                      title={triggerNodes.length === 0 ? 'Add a trigger node to run this workflow' : hasMultipleTriggers ? 'Click to select trigger' : 'Run from trigger'}
                    >
                      ▶ {isRunning ? 'Running…' : 'Run'}
                    </button>
                    {hasMultipleTriggers && (
                      <button
                        onClick={() => { if (!disabled) setShowTriggerPicker(p => !p); }}
                        disabled={disabled}
                        style={{ ...btnStyle('var(--success-color)', disabled), borderRadius: '0 6px 6px 0', padding: '6px 8px', fontSize: '10px', marginLeft: 0 }}
                        title="Select trigger"
                      >▾</button>
                    )}
                  </div>
                  {showTriggerPicker && hasMultipleTriggers && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.4)', zIndex: 9999, minWidth: '200px', overflow: 'hidden' }}
                      onMouseLeave={() => setShowTriggerPicker(false)}
                    >
                      <div style={{ padding: '8px 12px', fontSize: '11px', color: 'var(--text-tertiary)', borderBottom: '1px solid var(--border-color)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Select Trigger</div>
                      {triggerNodes.map(n => {
                        const nodeType = n.data?.type || n.type;
                        const icon = triggerNodeIconMap[nodeType] || iconByType[nodeType] || '▶';
                        const label = n.data?.label || n.data?.type || 'Node';
                        return (
                          <button key={n.id} onClick={() => runWorkflow([n.id])}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '9px 14px', background: 'transparent', border: 'none', color: 'var(--text-primary)', fontSize: '13px', cursor: 'pointer', textAlign: 'left' }}
                            onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-node)'}
                            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                          >
                            <span style={{ fontSize: '16px' }}>{icon}</span>
                            <span>{label}</span>
                          </button>
                        );
                      })}
                      <div style={{ borderTop: '1px solid var(--border-color)' }}>
                        <button onClick={() => runWorkflow(triggerNodes.map((node) => node.id))}
                          style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '9px 14px', background: 'transparent', border: 'none', color: 'var(--text-secondary)', fontSize: '12px', cursor: 'pointer', textAlign: 'left' }}
                          onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-node)'}
                          onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <span>▶▶</span><span>Run All Triggers</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Save */}
            <button onClick={saveWorkflow} data-tutorial-id="tutorial-editor-save-workflow" disabled={isSaving || currentWorkspace?.my_role === 'viewer'} style={{ ...btnStyle('var(--accent-color)', isSaving || currentWorkspace?.my_role === 'viewer'), display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', marginLeft: 0, fontSize: '13px' }} title="Save Workflow">
              💾 {isSaving ? 'Saving…' : 'Save'}
            </button>
        </div>

        <div style={{ width: '1px', height: '20px', background: 'var(--border-color)', flexShrink: 0, margin: '0 2px' }} />

        {/* Undo / Redo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
          <button
            onClick={handleUndo}
            title="Undo (Ctrl+Z)"
            style={{ background: 'transparent', border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer', padding: '5px 7px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}
            onMouseOver={(e) => { e.currentTarget.style.background = 'var(--bg-node)'; }}
            onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 7v6h6"/><path d="M3 13C5.2 7.4 10.3 4 16 4a9 9 0 0 1 0 18c-4 0-7.4-2-9.3-5"/>
            </svg>
          </button>
          <button
            onClick={handleRedo}
            title="Redo (Ctrl+Y)"
            style={{ background: 'transparent', border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer', padding: '5px 7px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)' }}
            onMouseOver={(e) => { e.currentTarget.style.background = 'var(--bg-node)'; }}
            onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 7v6h-6"/><path d="M21 13c-2.2-5.6-7.3-9-13-9a9 9 0 0 0 0 18c4 0 7.4-2 9.3-5"/>
            </svg>
          </button>
        </div>

        <div style={{ width: '1px', height: '20px', background: 'var(--border-color)', flexShrink: 0, margin: '0 2px' }} />

        {/* Right icon strip */}
        <div data-tutorial-id="tutorial-toolbar-icon-strip" style={{ display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
            {/* Primary panel toggles */}
            <button onClick={() => setShowScheduleModal(true)} disabled={!workflowId || currentWorkspace?.my_role === 'viewer'} style={iconBtnSt(!!cronSchedule, !workflowId || currentWorkspace?.my_role === 'viewer')} title={cronSchedule ? `Scheduled: ${cronSchedule}` : 'Schedule'} onMouseOver={(e) => { if (!(!workflowId || currentWorkspace?.my_role === 'viewer') && !cronSchedule) e.currentTarget.style.background = 'var(--bg-node)'; }} onMouseOut={(e) => { if (!cronSchedule) e.currentTarget.style.background = 'transparent'; }}>⏰</button>
            <button onClick={() => setShowHistory(v => !v)} disabled={!workflowId} style={iconBtnSt(showHistory, !workflowId)} title="Run History" onMouseOver={(e) => { if (workflowId && !showHistory) e.currentTarget.style.background = 'var(--bg-node)'; }} onMouseOut={(e) => { if (!showHistory) e.currentTarget.style.background = 'transparent'; }}>📜</button>
            <button onClick={() => setSelectionMode(v => !v)} style={iconBtnSt(selectionMode)} title={selectionMode ? 'Selection Mode: ON (drag to select)' : 'Selection Mode: OFF (drag to pan)'} onMouseOver={(e) => { if (!selectionMode) e.currentTarget.style.background = 'var(--bg-node)'; }} onMouseOut={(e) => { if (!selectionMode) e.currentTarget.style.background = 'transparent'; }}>⛶</button>

            {/* Context-sensitive: group / ungroup (always inline — contextual, brief) */}
            {selectedNodes.filter(n => n.type !== 'group' && !n.parentId).length >= 2 && (
              <button onClick={groupSelectedNodes} style={iconBtnSt()} title="Group selected nodes" onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-node)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>⬜</button>
            )}
            {selectedNodes.length === 1 && selectedNodes[0].type === 'group' && (
              <button onClick={() => ungroupNode(selectedNodes[0].id)} style={iconBtnSt()} title="Ungroup" onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-node)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>⬛</button>
            )}

            {/* Overflow menu ··· */}
            <div ref={moreMenuRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setShowMoreMenu(v => !v)}
                style={iconBtnSt(showMoreMenu)}
                title="More tools"
                onMouseOver={(e) => { if (!showMoreMenu) e.currentTarget.style.background = 'var(--bg-node)'; }}
                onMouseOut={(e) => { if (!showMoreMenu) e.currentTarget.style.background = 'transparent'; }}
              >···</button>
              {showMoreMenu && (
                <div ref={moreMenuDropdownRef} style={{ position: 'absolute', top: '38px', right: 0, background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '6px', minWidth: '190px', boxShadow: '0 6px 24px rgba(0,0,0,0.2)', zIndex: 10000, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  {workflowId && workspaceEnvironments.length > 0 && (
                    <>
                      <div style={{ padding: '2px 8px 4px', fontSize: '11px', color: 'var(--text-tertiary)' }}>🌐 Environment</div>
                      <select
                        value={currentEnvironmentId || ''}
                        onChange={(e) => setCurrentEnvironmentId(e.target.value || null)}
                        title="Select environment for this run"
                        style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '5px 8px', fontSize: '12px', cursor: 'pointer', margin: '0 4px 4px', width: 'calc(100% - 8px)' }}
                      >
                        <option value="">No environment</option>
                        {workspaceEnvironments.map(env => (
                          <option key={env.id} value={env.id}>{env.name}</option>
                        ))}
                      </select>
                      <div style={{ borderTop: '1px solid var(--border-color)', margin: '4px 2px' }} />
                    </>
                  )}
                  <button onClick={() => { setViewMode(m => m === 'canvas' ? 'steps' : 'canvas'); setShowMoreMenu(false); }} style={{ ...menuItemSt(), color: viewMode === 'steps' ? 'var(--accent-color)' : 'var(--text-primary)', fontWeight: viewMode === 'steps' ? 600 : 400 }} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>≡ {viewMode === 'canvas' ? 'Steps View' : 'Canvas View'}</button>
                  <button onClick={() => { setShowVariableInspector(v => !v); setShowMoreMenu(false); }} style={{ ...menuItemSt(), color: showVariableInspector ? 'var(--accent-color)' : 'var(--text-primary)', fontWeight: showVariableInspector ? 600 : 400 }} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>🔍 Variable Inspector{showVariableInspector ? ' ✓' : ''}</button>
                  <div style={{ borderTop: '1px solid var(--border-color)', margin: '4px 2px' }} />
                  <button onClick={() => { setShowMetrics(v => !v); setShowMoreMenu(false); }} disabled={!workflowId} style={{ ...menuItemSt(!workflowId), color: showMetrics ? 'var(--accent-color)' : 'var(--text-primary)', fontWeight: showMetrics ? 600 : 400 }} onMouseOver={(e) => { if (workflowId) e.currentTarget.style.background = 'var(--bg-primary)'; }} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>📊 Metrics & Alerting{showMetrics ? ' ✓' : ''}</button>
                  <button onClick={() => { setShowProcessIntel(v => !v); setShowMoreMenu(false); }} disabled={!workflowId} style={{ ...menuItemSt(!workflowId), color: showProcessIntel ? 'var(--accent-color)' : 'var(--text-primary)', fontWeight: showProcessIntel ? 600 : 400 }} onMouseOver={(e) => { if (workflowId) e.currentTarget.style.background = 'var(--bg-primary)'; }} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>🧭 Process Intelligence{showProcessIntel ? ' ✓' : ''}</button>
                  <button onClick={() => { setShowVersions(v => !v); setShowMoreMenu(false); }} disabled={!workflowId} style={{ ...menuItemSt(!workflowId), color: showVersions ? 'var(--accent-color)' : 'var(--text-primary)', fontWeight: showVersions ? 600 : 400 }} onMouseOver={(e) => { if (workflowId) e.currentTarget.style.background = 'var(--bg-primary)'; }} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>🕘 Versions{showVersions ? ' ✓' : ''}</button>
                  <button onClick={() => { setShowWorkflowEnvVars(v => !v); setShowMoreMenu(false); }} disabled={!workflowId} style={{ ...menuItemSt(!workflowId), color: showWorkflowEnvVars ? 'var(--accent-color)' : 'var(--text-primary)', fontWeight: showWorkflowEnvVars ? 600 : 400 }} onMouseOver={(e) => { if (workflowId) e.currentTarget.style.background = 'var(--bg-primary)'; }} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>⚙ Workflow Settings{showWorkflowEnvVars ? ' ✓' : ''}</button>
                  <div style={{ borderTop: '1px solid var(--border-color)', margin: '4px 2px' }} />
                  <button onClick={() => { duplicateWorkflow(workflowId); setShowMoreMenu(false); }} disabled={!workflowId || currentWorkspace?.my_role === 'viewer'} style={menuItemSt(!workflowId || currentWorkspace?.my_role === 'viewer')} onMouseOver={(e) => { if (!(!workflowId || currentWorkspace?.my_role === 'viewer')) e.currentTarget.style.background = 'var(--bg-primary)'; }} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>📑 Duplicate Workflow</button>
                </div>
              )}
            </div>
        </div>

        <div style={{ width: '1px', height: '20px', background: 'var(--border-color)', flexShrink: 0, margin: '0 2px' }} />

        {/* Collaborator presence avatars */}
        {collabUsers.length > 0 && workflowId && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '3px', flexShrink: 0 }}>
            {collabUsers.slice(0, 5).map(u => (
              <div key={u.user_id} title={`${u.username} is editing`} style={{ width: '26px', height: '26px', borderRadius: '50%', background: u.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', color: '#fff', fontWeight: 700, border: '2px solid var(--bg-secondary)', cursor: 'default', flexShrink: 0 }}>
                {(u.username || '?')[0].toUpperCase()}
              </div>
            ))}
            {collabUsers.length > 5 && (
              <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginLeft: '2px' }}>+{collabUsers.length - 5}</span>
            )}
            <div style={{ width: '1px', height: '20px', background: 'var(--border-color)', flexShrink: 0, margin: '0 4px' }} />
          </div>
        )}

        {/* Utilities: theme + notifications + user menu */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
            <button onClick={cycleTheme} data-tutorial-id="tutorial-toolbar-theme" style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '6px', color: 'var(--text-secondary)', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-node)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'} title={`Theme: ${theme}`}>{getThemeIcon()}</button>
            <NotificationCenter token={token} onNavigate={(params) => {
              if (params.workflow_id) { loadWorkflow(params.workflow_id); }
              else if (params.type === 'case' || params.type === 'work_item') {
                setCaseNavTarget({ case_id: params.case_id, work_item_id: params.work_item_id });
                setView('casematrix');
              }
            }} />
            {/* User menu */}
            <div ref={userMenuRef} style={{ position: 'relative' }}>
              <button onClick={() => setShowUserMenu(v => !v)} style={{ background: showUserMenu ? 'var(--bg-primary)' : 'transparent', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '3px 8px 3px 4px', color: 'var(--text-primary)', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }} title="Account & Settings">
                <span style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'var(--accent-color)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#fff', fontWeight: 700, flexShrink: 0 }}>{currentUser?.username?.[0]?.toUpperCase() || '?'}</span>
                <span style={{ fontSize: '9px', opacity: 0.5 }}>▾</span>
              </button>
              {showUserMenu && (
                <div ref={userMenuDropdownRef} style={{ position: 'absolute', right: 0, top: '38px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '10px', padding: '6px', minWidth: '200px', boxShadow: '0 6px 24px rgba(0,0,0,0.2)', zIndex: 10000, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border-color)', marginBottom: '4px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>{currentUser?.username}</div>
                    {currentUser?.email && <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '1px' }}>{currentUser.email}</div>}
                  </div>
                  <button onClick={() => { setReturnView('editor'); setView('profile'); setShowUserMenu(false); }} style={menuItemSt()} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>👤 Profile</button>
                  <button onClick={() => { setReturnView('editor'); setInitialCredentialServiceType(null); setView('credentials'); setShowUserMenu(false); }} style={menuItemSt()} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>🔑 Credentials</button>
                  <button onClick={() => { setReturnView('editor'); setView('variables'); setShowUserMenu(false); }} style={menuItemSt()} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>🌐 Global Variables</button>
                  <button onClick={() => { setReturnView('editor'); setView('datastore'); setShowUserMenu(false); }} style={menuItemSt()} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>🗄️ Data Store</button>
                  <button onClick={() => { setReturnView('editor'); setView('tables'); setShowUserMenu(false); }} style={menuItemSt()} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>📋 Tables</button>
                  <button onClick={() => { setReturnView('editor'); setView('scheduler_calendar'); setShowUserMenu(false); }} style={menuItemSt()} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>📅 Schedule Calendar</button>
                  <button onClick={() => { setReturnView('editor'); setView('webhooks'); setShowUserMenu(false); }} style={menuItemSt()} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>🔗 Webhooks</button>
                  {isAdmin && <button onClick={() => { setReturnView('editor'); setView('customRoutes'); setShowUserMenu(false); }} style={menuItemSt()} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>🛣️ Custom API Routes</button>}
                  <button onClick={() => { setReturnView('editor'); setView('marketplace'); setShowUserMenu(false); }} style={menuItemSt()} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>🛒 Marketplace</button>
                  <button onClick={() => { setReturnView('editor'); setView('audit_logs'); setShowUserMenu(false); }} style={menuItemSt()} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>📋 Audit Logs</button>
                  <button onClick={() => { setReturnView('editor'); setView('mobile_monitor'); setShowUserMenu(false); }} style={menuItemSt()} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>📱 Run Monitor</button>
                  <button onClick={() => { setReturnView('editor'); setView('xavvy_forms'); setShowUserMenu(false); }} style={menuItemSt()} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>📝 Xavvy Forms</button>
                  {isAdmin && <>
                    <button onClick={() => { setReturnView('editor'); setView('admin'); setShowUserMenu(false); }} style={menuItemSt()} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>⚙ Admin Panel</button>
                    <button onClick={() => { setReturnView('editor'); setView('monitoring'); setShowUserMenu(false); }} style={menuItemSt()} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>📈 Monitoring</button>
                    <button onClick={() => { setReturnView('editor'); setView('plugins'); setShowUserMenu(false); }} style={menuItemSt()} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>🧩 Plugin Governance</button>
                    <button onClick={() => { setShowLicenseManager(true); setShowUserMenu(false); }} style={menuItemSt()} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>🔑 License</button>
                  </>}
                  <button onClick={() => { openTutorial(0); setShowUserMenu(false); }} style={menuItemSt()} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>🎓 Tutorial</button>
                  <div style={{ borderTop: '1px solid var(--border-color)', margin: '4px 0' }} />
                  <button onClick={handleLogout} style={{ ...menuItemSt(), color: 'var(--error-color)' }} onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-primary)'} onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}>↪ Logout</button>
                </div>
              )}
            </div>
        </div>
      </div>

      {/* Pinned Nodes Bar — shows all nodes with pinned test data */}
      {(() => {
        const pinnedNodes = nodes.filter(n => n.data?.pinnedData && n.type !== 'sticky_note' && n.type !== 'group');
        if (pinnedNodes.length === 0) return null;
        return (
          <div style={{
            padding: '6px 24px',
            background: 'var(--bg-node)',
            borderBottom: '1px solid var(--border-color)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '13px',
            flexWrap: 'wrap'
          }}>
            <span style={{ color: 'var(--warning-color)', fontWeight: 600, whiteSpace: 'nowrap' }}>📌 Pinned:</span>
            {pinnedNodes.map(n => (
              <span key={n.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', padding: '1px 6px', borderRadius: '4px', fontSize: '12px' }}>
                <span style={{ color: 'var(--text-primary)' }}>{n.data?.label || n.data?.type || n.id}</span>
                <button onClick={() => clearNodePin(n.id)} style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: '13px', padding: '0 1px', lineHeight: 1 }} title="Clear this pin">×</button>
              </span>
            ))}
            <span style={{ color: 'var(--text-tertiary)', fontSize: '11px' }}>— skipped during execution; stored output injected</span>
            {pinnedTriggerMeta?.id && (
              <span style={{ color: 'var(--text-tertiary)', fontSize: '11px' }}>· run {pinnedTriggerMeta.id.substring(0, 8)}…</span>
            )}
            <button onClick={clearPinnedTriggerData} style={{ marginLeft: 'auto', background: 'transparent', color: 'var(--error-color)', border: '1px solid var(--error-color)', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', padding: '2px 8px', whiteSpace: 'nowrap' }}>Clear All</button>
          </div>
        );
      })()}
      
      <div style={{ flex: 1, display: 'flex', position: 'relative', overflow: 'hidden' }}>
        <div data-tutorial-id="tutorial-editor-sidebar">
          <Sidebar workflowType={workflowType} />
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', height: '100%', overflow: 'hidden' }}>
            <div style={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column' }} ref={reactFlowWrapper} onMouseMove={handleCanvasMouseMove}>
            {viewMode === 'steps' ? (
              <StepView
                nodes={nodes}
                edges={edges}
                onNodeClick={(node) => onNodeClick(null, node)}
                selectedNodeId={selectedNode?.id}
              />
            ) : (<>
            {/* Pre-run validation warning banner (§4.57) */}
            {validationWarnings.length > 0 && (
                <div style={{
                    position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)',
                    zIndex: 20, background: 'var(--warning-soft)', border: '1px solid var(--warning)',
                    borderRadius: '6px', padding: '8px 14px', display: 'flex',
                    alignItems: 'flex-start', gap: '10px', maxWidth: '600px', minWidth: '300px',
                    boxShadow: 'var(--elev-2)',
                }}>
                    <span style={{ fontSize: '14px', flexShrink: 0 }}>⚠️</span>
                    <div style={{ flex: 1, fontSize: '12px', color: 'var(--warning)', lineHeight: 1.5 }}>
                        {validationWarnings.map((w, i) => (
                            <div key={i}><strong>{w.node_label}:</strong> {w.message}</div>
                        ))}
                    </div>
                    <button onClick={() => setValidationWarnings([])} style={{
                        background: 'transparent', border: 'none', color: 'var(--warning)',
                        cursor: 'pointer', fontSize: '14px', flexShrink: 0, padding: 0,
                    }}>✕</button>
                </div>
            )}
            <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                proOptions={{ hideAttribution: true }}
                snapToGrid={true}
                onNodesChange={safeOnNodesChange}
                onEdgesChange={safeOnEdgesChange}
                onConnect={onConnect}
                onEdgeClick={onEdgeClick}
                onInit={setReactFlowInstance}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onNodeClick={onNodeClick}
                onPaneClick={onPaneClick}
                onNodeContextMenu={onNodeContextMenu}
                onSelectionChange={onSelectionChange}
                onNodeDragStart={handleNodeDragStart}
                onNodeDragStop={handleNodeDragStop}
                onMove={(_, vp) => setViewport(vp)}
                fitView
                panOnScroll={true}
                zoomOnScroll={false}
                style={{ background: 'var(--canvas-bg)' }}
                panOnDrag={!selectionMode}
                selectionOnDrag={selectionMode}
            >
                <Controls />
                <MiniMap style={{ background: 'var(--panel)' }} nodeColor="#8b8b94" maskColor="rgba(0,0,0,0.12)" />
                <Background variant="dots" gap={16} size={1} color="var(--canvas-dots)" />
            </ReactFlow>
            {nodes.length === 1 && nodes[0]?.data?.type === 'debug' && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                <div style={{ pointerEvents: 'auto', width: 'min(560px, 86vw)', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '12px', padding: '16px', boxShadow: 'var(--shadow-strong)' }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '6px' }}>Build faster with quick actions</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '12px' }}>Add your first production node, generate with AI, or load a template. You can also press Space to open quick-add.</div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button onClick={() => setShowQuickAdd(true)} style={{ padding: '6px 10px', border: '1px solid var(--border-color)', background: 'var(--bg-primary)', color: 'var(--text-primary)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>+ Add Node</button>
                    <button onClick={() => setShowWorkflowGeneratorModal(true)} disabled={currentWorkspace?.my_role === 'viewer'} style={{ padding: '6px 10px', border: '1px solid var(--border-color)', background: 'var(--bg-primary)', color: currentWorkspace?.my_role === 'viewer' ? 'var(--text-tertiary)' : 'var(--text-primary)', borderRadius: '6px', cursor: currentWorkspace?.my_role === 'viewer' ? 'not-allowed' : 'pointer', fontSize: '12px' }}>✨ Generate with AI</button>
                    <button onClick={() => setShowTemplatesModal(true)} style={{ padding: '6px 10px', border: '1px solid var(--border-color)', background: 'var(--bg-primary)', color: 'var(--text-primary)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>📋 Browse Templates</button>
                  </div>
                </div>
              </div>
            )}
            {/* Lock badges — nodes currently open in another user's config panel */}
            {Object.entries(remoteEditingNodes).filter(([, e]) => e.nodeId).map(([userId, entry]) => {
              const node = nodes.find(n => n.id === entry.nodeId);
              if (!node) return null;
              const sx = node.position.x * viewport.zoom + viewport.x;
              const sy = node.position.y * viewport.zoom + viewport.y;
              return (
                <div key={`lock-${userId}`} style={{ position: 'absolute', left: sx, top: sy, zIndex: 9997, pointerEvents: 'none', transform: 'translate(0, -100%)', marginBottom: '2px' }}>
                  <div style={{ background: entry.color, color: '#fff', fontSize: '10px', fontWeight: 600, padding: '2px 6px', borderRadius: '4px', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '3px' }}>
                    🔒 {entry.username}
                  </div>
                </div>
              );
            })}
            {/* Remote collaborator cursors */}
            {Object.entries(remoteCursors).map(([userId, cursor]) => {
              const sx = cursor.x * viewport.zoom + viewport.x;
              const sy = cursor.y * viewport.zoom + viewport.y;
              return (
                <div key={userId} style={{ position: 'absolute', left: sx, top: sy, zIndex: 9998, pointerEvents: 'none', transform: 'translate(-3px, -3px)' }}>
                  <svg width="14" height="18" viewBox="0 0 14 18" style={{ display: 'block' }}>
                    <path d="M0 0 L0 14 L4 10 L7 18 L9 17 L6 9 L12 9 Z" fill={cursor.color} stroke="#fff" strokeWidth="1.2" />
                  </svg>
                  <div style={{ background: cursor.color, color: '#fff', fontSize: '10px', fontWeight: 600, padding: '2px 6px', borderRadius: '4px', whiteSpace: 'nowrap', marginTop: '1px', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {cursor.username}
                  </div>
                </div>
              );
            })}
            {/* Collaboration conflict toast */}
            {conflictToast && (
              <div style={{ position: 'absolute', bottom: '16px', right: '16px', zIndex: 9999, background: 'var(--bg-secondary)', border: `1px solid ${conflictToast.color}`, borderRadius: '8px', padding: '10px 12px', display: 'flex', alignItems: 'center', gap: '10px', boxShadow: '0 4px 16px rgba(0,0,0,0.35)', maxWidth: '280px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: conflictToast.color, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)' }}>Workflow saved</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{conflictToast.username} saved the workflow</div>
                </div>
                <button onClick={() => setConflictToast(null)} style={{ background: 'transparent', border: 'none', color: 'var(--text-tertiary)', fontSize: '14px', cursor: 'pointer', padding: '2px', lineHeight: 1, flexShrink: 0 }}>✕</button>
              </div>
            )}
            {/* Tool connect modal — configure node-as-tool when connecting AI Agent tools handle */}
            {toolConnectModal && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={() => setToolConnectModal(null)}>
                <div style={{ background: 'var(--bg-primary)', border: '1px solid rgba(168,85,247,0.5)', borderRadius: '10px', padding: '20px 22px', width: '380px', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }} onMouseDown={e => e.stopPropagation()}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#a855f7', marginBottom: '3px' }}>⚙ Connect as AI Tool</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '16px' }}>
                    <b style={{ color: 'var(--text-secondary)' }}>{toolConnectModal.targetNodeLabel}</b> will be callable by the AI Agent during its reasoning loop.
                  </div>

                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>Tool Name <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>(no spaces)</span></label>
                  <input
                    value={toolConnectName}
                    onChange={e => setToolConnectName(e.target.value.replace(/\s/g, '_').replace(/[^a-z0-9_]/gi, ''))}
                    placeholder="e.g. send_email"
                    autoFocus
                    onKeyDown={e => e.key === 'Enter' && confirmToolConnect()}
                    style={{ width: '100%', marginBottom: '12px', padding: '7px 10px', fontSize: '13px', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-primary)', boxSizing: 'border-box', outline: 'none' }}
                  />

                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' }}>Description <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>— tells the AI when and how to use this tool</span></label>
                  <textarea
                    value={toolConnectDesc}
                    onChange={e => setToolConnectDesc(e.target.value)}
                    placeholder={`e.g. Send an email to a recipient. Use 'to' for the email address, 'subject' for the subject line, 'body' for the message.`}
                    rows={4}
                    style={{ width: '100%', marginBottom: '18px', padding: '7px 10px', fontSize: '12px', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-primary)', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit', outline: 'none' }}
                  />

                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <button onClick={() => setToolConnectModal(null)} style={{ padding: '7px 16px', borderRadius: '4px', border: '1px solid var(--border-color)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
                    <button onClick={confirmToolConnect} disabled={!toolConnectName.trim()} style={{ padding: '7px 16px', borderRadius: '4px', border: 'none', background: toolConnectName.trim() ? '#a855f7' : 'var(--bg-tertiary)', color: toolConnectName.trim() ? 'white' : 'var(--text-tertiary)', cursor: toolConnectName.trim() ? 'pointer' : 'not-allowed', fontSize: '12px', fontWeight: 600 }}>
                      Add Tool
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Locked-node deletion blocked toast */}
            {lockedNodeToast && (
              <div style={{ position: 'absolute', bottom: conflictToast ? '76px' : '16px', right: '16px', zIndex: 9999, background: 'var(--bg-secondary)', border: `1px solid ${lockedNodeToast.color}`, borderRadius: '8px', padding: '10px 12px', display: 'flex', alignItems: 'center', gap: '10px', boxShadow: '0 4px 16px rgba(0,0,0,0.35)', maxWidth: '280px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: lockedNodeToast.color, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)' }}>Deletion blocked</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '1px' }}>{lockedNodeToast.username} is currently editing this node</div>
                </div>
                <button onClick={() => setLockedNodeToast(null)} style={{ background: 'transparent', border: 'none', color: 'var(--text-tertiary)', fontSize: '14px', cursor: 'pointer', padding: '2px', lineHeight: 1, flexShrink: 0 }}>✕</button>
              </div>
            )}
            </>)}
            </div>
            {executionResult && <ExecutionPanel result={executionResult} onClose={() => setExecutionResult(null)} workflowId={workflowId} />}
        </div>
        {selectedNode && (
          <div data-tutorial-id="tutorial-editor-config-panel">
            <NodeConfigPanel
                selectedNode={selectedNode}
                nodes={nodes}
                edges={edges}
                setNodes={configPanelSetNodes}
                setEdges={setEdges}
                setSelectedNode={setSelectedNode}
                nodeStatus={nodeStatus}
                runNode={runNode}
                isRunning={isRunning}
                currentWorkspaceId={currentWorkspace?.id || null}
                workflowType={workflowType}
                nodeCatalog={nodeCatalog}
                onCreateCredential={(serviceType) => {
                  setReturnView('editor');
                  setInitialCredentialServiceType(serviceType);
                  setView('credentials');
                }}
            />
          </div>
        )}
        {showVariableInspector && (
          <VariableInspector
            nodes={nodes}
            nodeStatus={nodeStatus}
            onClose={() => setShowVariableInspector(false)}
          />
        )}
        {showWorkflowEnvVars && workflowId && (
          <WorkflowEnvVars
            workflowId={workflowId}
            onClose={() => setShowWorkflowEnvVars(false)}
          />
        )}
        {showEnvModal && (
          <EnvironmentManagerModal
            workspace={currentWorkspace}
            environments={workspaceEnvironments}
            credentials={[]}
            onClose={() => setShowEnvModal(false)}
            onSaved={fetchWorkspaceEnvironments}
          />
        )}
        {showGitSyncModal && (
          <GitSyncModal
            workspace={currentWorkspace || { name: 'Workspace' }}
            syncBaseUrl={currentWorkspace ? `${API_BASE_URL}/workspaces/${currentWorkspace.id}/git-sync` : `${API_BASE_URL}/users/me/git-sync`}
            config={gitSyncConfig}
            onClose={() => setShowGitSyncModal(false)}
            onSaved={fetchGitSyncConfig}
          />
        )}
        {showHistory && workflowId && (
            <WorkflowHistory
                workflowId={workflowId}
                nodes={nodes}
                edges={edges}
                onClose={() => setShowHistory(false)}
                onPinTriggerData={handlePinTriggerData}
                onLoadRunOutputs={(outputs) => {
                    // Execution Data Inspector (§4.58): populate canvas node status from a historical run
                    const statusMap = {};
                    Object.entries(outputs.node_outputs || {}).forEach(([nodeId, entry]) => {
                        statusMap[nodeId] = {
                            status: entry.error ? 'failed' : 'completed',
                            output: entry.data,
                        };
                    });
                    setNodeStatus(statusMap);
                    setShowHistory(false);
                }}
            />
        )}
        {showMetrics && workflowId && (
            <WorkflowMetrics
                workflowId={workflowId}
                workflowName={workflowName}
                getAuthHeaders={() => {
                    const t = localStorage.getItem('token');
                    return t ? { Authorization: `Bearer ${t}` } : {};
                }}
                onClose={() => setShowMetrics(false)}
            />
        )}
        {showProcessIntel && workflowId && (
            <ProcessIntel
                sourceType="workflow_run"
                sourceId={workflowId}
                sourceName={workflowName}
                onClose={() => setShowProcessIntel(false)}
            />
        )}
        {showVersions && workflowId && (
            <WorkflowVersions
                workflowId={workflowId}
                onClose={() => setShowVersions(false)}
                onRestored={handleVersionRestored}
                canRestore={currentWorkspace?.my_role !== 'viewer'}
            />
        )}
        {(showAgentChat || agentChatStarted) && workflowId && (
            <div style={{ display: showAgentChat ? 'block' : 'none' }}>
                <AgentChat
                    workflowId={workflowId}
                    onClose={() => {
                        setShowAgentChat(false);
                        setAgentChatStarted(false);
                    }}
                    onMinimize={() => setShowAgentChat(false)}
                    onChatStart={() => setNodeStatus({})}
                    token={token}
                />
            </div>
        )}
        {/* §4.79 Multi-Agent Orchestration Studio — reasoning + memory panels */}
        {workflowType === 'agent_graph' && showReasoningPanel && (
            <ReasoningStreamPanel
                events={agentReasoningEvents}
                nodes={nodes}
                onClose={() => setShowReasoningPanel(false)}
                onClear={() => setAgentReasoningEvents([])}
            />
        )}
        {workflowType === 'agent_graph' && showMemoryPanel && workflowId && (
            <AgentGraphMemoryPanel
                workflowId={workflowId}
                onClose={() => setShowMemoryPanel(false)}
            />
        )}
        {showScheduleModal && (
            <ScheduleModal
                cronSchedule={cronSchedule}
                setCronSchedule={setCronSchedule}
                isPaused={isPaused}
                setIsPaused={setIsPaused}
                maxConcurrentRuns={workflowMaxConcurrentRuns}
                setMaxConcurrentRuns={setWorkflowMaxConcurrentRuns}
                schedulerTimezoneMeta={schedulerTimezoneMeta}
                onClose={() => setShowScheduleModal(false)}
                advancedScheduler={advancedScheduler}
                setAdvancedScheduler={setAdvancedScheduler}
                slaMaxSeconds={slaMaxSeconds}
                setSlaMaxSeconds={setSlaMaxSeconds}
                runPriority={runPriority}
                setRunPriority={setRunPriority}
            />
        )}
        {showWorkflowGeneratorModal && (
            <WorkflowGeneratorModal
                currentWorkspace={currentWorkspace}
                onClose={() => setShowWorkflowGeneratorModal(false)}
                onApply={applyGeneratedWorkflowToCanvas}
            />
        )}
        {showTemplatesModal && (
            <WorkflowTemplatesModal
                onClose={() => setShowTemplatesModal(false)}
                onSelectTemplate={loadTemplate}
            />
        )}
        <CopilotModal
            isOpen={copilotState.isOpen}
            mode={copilotState.mode}
            workflowId={workflowId}
            runId={copilotState.runId}
            onClose={() => setCopilotState(prev => ({ ...prev, isOpen: false }))}
        />
        {showCopilotChat && workflowId && (
            <FlowCopilotPanel
                workflowId={workflowId}
                lastRunId={executionResult?.run_id || null}
                onClose={() => setShowCopilotChat(false)}
                onApplyAddNode={copilotAddNode}
                onApplyModifyNode={copilotModifyNode}
                onApplyRemoveNode={copilotRemoveNode}
            />
        )}
        <TutorialModal
          isOpen={showTutorial}
          steps={tutorialSteps}
          stepIndex={tutorialStepIndex}
          stepTargetSelector={tutorialTargetSelector}
          canAdvance={isTutorialStepComplete}
          disableAutoShow={disableTutorialAutoShow}
          setDisableAutoShow={setDisableTutorialAutoShow}
          onPrevious={() => setTutorialStepIndex(prev => Math.max(0, prev - 1))}
          onNext={() => setTutorialStepIndex(prev => Math.min(tutorialSteps.length - 1, prev + 1))}
          onClose={() => closeTutorial(false)}
          onFinish={() => closeTutorial(true)}
        />
        {showLicenseManager && (
          <LicenseManager
            onClose={() => setShowLicenseManager(false)}
            token={token}
            isAdmin={isAdmin}
            productMode={branding?.product_mode || 'flow'}
          />
        )}
        {showShortcutsModal && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 10021, background: 'rgba(0,0,0,0.5)' }} onMouseDown={() => setShowShortcutsModal(false)}>
            <div style={{ width: 'min(560px, 92vw)', margin: '12vh auto 0', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '12px', boxShadow: 'var(--shadow-strong)', padding: '16px' }} onMouseDown={(e) => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)' }}>Keyboard Shortcuts</div>
                <button onClick={() => setShowShortcutsModal(false)} style={{ border: 'none', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '20px', lineHeight: 1 }}>×</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
                <div style={{ padding: '8px', border: '1px solid var(--border-color)', borderRadius: '8px' }}><strong>Space / /</strong><div style={{ color: 'var(--text-secondary)', marginTop: '2px' }}>Open quick-add</div></div>
                <div style={{ padding: '8px', border: '1px solid var(--border-color)', borderRadius: '8px' }}><strong>Ctrl/Cmd + K</strong><div style={{ color: 'var(--text-secondary)', marginTop: '2px' }}>Open quick-add</div></div>
                <div style={{ padding: '8px', border: '1px solid var(--border-color)', borderRadius: '8px' }}><strong>↑ ↓ Enter Esc</strong><div style={{ color: 'var(--text-secondary)', marginTop: '2px' }}>Navigate quick-add</div></div>
                <div style={{ padding: '8px', border: '1px solid var(--border-color)', borderRadius: '8px' }}><strong>Ctrl/Cmd + C / V</strong><div style={{ color: 'var(--text-secondary)', marginTop: '2px' }}>Copy/paste selected nodes</div></div>
                <div style={{ padding: '8px', border: '1px solid var(--border-color)', borderRadius: '8px' }}><strong>Ctrl/Cmd + Z</strong><div style={{ color: 'var(--text-secondary)', marginTop: '2px' }}>Undo</div></div>
                <div style={{ padding: '8px', border: '1px solid var(--border-color)', borderRadius: '8px' }}><strong>Ctrl/Cmd + Y / Shift+Z</strong><div style={{ color: 'var(--text-secondary)', marginTop: '2px' }}>Redo</div></div>
                <div style={{ padding: '8px', border: '1px solid var(--border-color)', borderRadius: '8px' }}><strong>?</strong><div style={{ color: 'var(--text-secondary)', marginTop: '2px' }}>Open this shortcuts panel</div></div>
              </div>
            </div>
          </div>
        )}
        {showQuickAdd && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 10020, background: 'rgba(0,0,0,0.35)' }} onMouseDown={() => { setShowQuickAdd(false); setQuickAddQuery(''); }}>
            <div style={{ width: 'min(620px, 92vw)', margin: '10vh auto 0', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '12px', boxShadow: 'var(--shadow-strong)' }} onMouseDown={(e) => e.stopPropagation()}>
              <input
                autoFocus
                value={quickAddQuery}
                onChange={(e) => setQuickAddQuery(e.target.value)}
                placeholder="Search nodes..."
                style={{ width: '100%', border: 'none', outline: 'none', background: 'transparent', color: 'var(--text-primary)', fontSize: '14px', padding: '14px 16px', borderBottom: '1px solid var(--border-color)' }}
              />
              <div ref={quickAddListRef} style={{ maxHeight: '340px', overflowY: 'auto' }}>
                {quickAddNodes.length === 0 ? (
                  <div style={{ padding: '14px 16px', color: 'var(--text-secondary)', fontSize: '13px' }}>No matching nodes</div>
                ) : (
                  quickAddNodes.map((node, idx) => (
                    <button
                      key={`${node.type}-${idx}`}
                      ref={(element) => { quickAddItemRefs.current[idx] = element; }}
                      onMouseEnter={() => setQuickAddIndex(idx)}
                      onClick={() => insertNodeFromQuickAdd(node)}
                      style={{ width: '100%', textAlign: 'left', border: 'none', borderBottom: '1px solid var(--border-color)', background: idx === quickAddIndex ? 'var(--bg-primary)' : 'transparent', color: 'var(--text-primary)', padding: '10px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px' }}
                    >
                      <span style={{ width: '20px', textAlign: 'center', fontSize: '15px' }}>{node.icon || '⚙️'}</span>
                      <span style={{ fontSize: '13px', fontWeight: 600 }}>{node.label || node.type}</span>
                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)', marginLeft: 'auto' }}>{node.category || 'Node'}</span>
                    </button>
                  ))
                )}
              </div>
              <div style={{ padding: '8px 12px', borderTop: '1px solid var(--border-color)', color: 'var(--text-tertiary)', fontSize: '11px', display: 'flex', justifyContent: 'space-between' }}>
                <span>↑ ↓ navigate · Enter add · Esc close</span>
                <button onClick={() => setShowShortcutsModal(true)} style={{ border: 'none', background: 'transparent', color: 'var(--accent-color)', cursor: 'pointer', fontSize: '11px', padding: 0 }}>All shortcuts</button>
              </div>
            </div>
          </div>
        )}

        {/* Node context menu (right-click) */}
        {nodeContextMenu && (
          <div
            style={{
              position: 'fixed',
              left: nodeContextMenu.x,
              top: nodeContextMenu.y,
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              boxShadow: 'var(--shadow-strong)',
              zIndex: 9999,
              minWidth: '190px',
              overflow: 'hidden'
            }}
            onMouseLeave={() => setNodeContextMenu(null)}
          >
            <div style={{ padding: '6px 10px', fontSize: '11px', color: 'var(--text-tertiary)', borderBottom: '1px solid var(--border-color)', fontWeight: 600 }}>
              {nodeContextMenu.nodeLabel}
            </div>
            <button
              onClick={() => openPinModal(nodeContextMenu.nodeId)}
              style={{ display: 'block', width: '100%', padding: '8px 12px', background: 'none', border: 'none', color: 'var(--text-primary)', cursor: 'pointer', textAlign: 'left', fontSize: '13px' }}
              onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-hover)'}
              onMouseOut={(e) => e.currentTarget.style.background = 'none'}
            >
              📌 {nodeContextMenu.isPinned ? 'Edit pinned data' : 'Pin test data'}
            </button>
            {nodeContextMenu.isPinned && (
              <button
                onClick={() => { clearNodePin(nodeContextMenu.nodeId); setNodeContextMenu(null); }}
                style={{ display: 'block', width: '100%', padding: '8px 12px', background: 'none', border: 'none', color: 'var(--error-color)', cursor: 'pointer', textAlign: 'left', fontSize: '13px' }}
                onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-hover)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'none'}
              >
                🗑 Clear pin
              </button>
            )}
          </div>
        )}

        {/* Edge click panel */}
        {edgePanel && (() => {
          const ep = edgePanel;
          const c = getEdgeConditionColor(ep.edge.data, ep.edge.label);
          const isTool = ep.edge.data?.edge_type === 'tool';
          const srcType = ep.sourceNode?.data?.type;
          const availableConds = getAvailableConditions(srcType, ep.sourceNode?.data);
          const currentCond = ep.edge.data?.condition || ep.edge.label;
          return (
            <div
              style={{
                position: 'fixed',
                left: ep.x + 10,
                top: ep.y - 8,
                background: 'var(--panel)',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                boxShadow: 'var(--shadow-strong)',
                zIndex: 9999,
                minWidth: '180px',
                maxWidth: '240px',
                overflow: 'hidden',
              }}
              onMouseLeave={() => setEdgePanel(null)}
            >
              <div style={{ padding: '7px 12px', borderBottom: '1px solid var(--border)', fontSize: '10px', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {isTool ? 'Tool connection' : 'Branch'}
              </div>

              {availableConds.length > 0 && !isTool && (
                <div style={{ padding: '8px 10px', display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                  {availableConds.map(cond => {
                    const active = currentCond === cond;
                    const cc = EDGE_CONDITION_COLORS[cond] || EDGE_CONDITION_COLORS._default;
                    return (
                      <button
                        key={cond}
                        onClick={() => {
                          pushHistory();
                          setEdges(eds => eds.map(e => e.id === ep.edge.id
                            ? { ...e, label: cond, data: { ...e.data, condition: cond }, markerEnd: { ...(e.markerEnd || {}), color: cc.stroke } }
                            : e
                          ));
                          setEdgePanel(prev => prev ? { ...prev, edge: { ...prev.edge, label: cond, data: { ...prev.edge.data, condition: cond } } } : null);
                        }}
                        style={{
                          padding: '3px 10px',
                          background: active ? cc.bg : 'transparent',
                          border: `1px solid ${active ? cc.border : 'var(--border)'}`,
                          borderRadius: '999px',
                          color: active ? cc.text : 'var(--muted)',
                          fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {cond}
                      </button>
                    );
                  })}
                </div>
              )}

              {isTool && (
                <div style={{ padding: '8px 12px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#d8b4fe', marginBottom: ep.edge.data.description ? 4 : 0 }}>
                    {ep.edge.data.tool_name}
                  </div>
                  {ep.edge.data.description && (
                    <div style={{ fontSize: '11px', color: 'var(--dim)', lineHeight: 1.4 }}>{ep.edge.data.description}</div>
                  )}
                </div>
              )}

              <div style={{ borderTop: '1px solid var(--border)', padding: '6px' }}>
                <button
                  onClick={() => {
                    pushHistory();
                    const edge = ep.edge;
                    setEdges(eds => eds.filter(e => e.id !== edge.id));
                    if (edge.data?.edge_type === 'tool') {
                      setNodes(nds => nds.map(n => {
                        if (n.id !== edge.source) return n;
                        const updated = (n.data?.params?.tool_connections || []).filter(tc => tc.node_id !== edge.target || tc.tool_name !== edge.data?.tool_name);
                        return { ...n, data: { ...n.data, params: { ...n.data?.params, tool_connections: updated } } };
                      }));
                    }
                    setEdgePanel(null);
                  }}
                  style={{
                    width: '100%', padding: '5px 8px',
                    background: 'rgba(239,68,68,0.08)',
                    border: '1px solid rgba(239,68,68,0.25)',
                    borderRadius: '6px',
                    color: '#fca5a5', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Delete edge
                </button>
              </div>
            </div>
          );
        })()}

        {/* Pin test data modal */}
        {showPinModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setShowPinModal(false)}>
            <div style={{ background: 'var(--bg-secondary)', borderRadius: '10px', border: '1px solid var(--border-color)', padding: '24px', width: '520px', maxWidth: '95vw', boxShadow: 'var(--shadow-strong)' }}
              onClick={(e) => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 8px', fontSize: '16px', color: 'var(--text-primary)' }}>📌 Pin Test Data</h3>
              <p style={{ margin: '0 0 14px', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                Paste a JSON object or array. When the workflow runs, this node will be skipped and its pinned output will be injected directly — enabling isolated testing of downstream nodes.
              </p>
              <textarea
                value={pinModalText}
                onChange={(e) => { setPinModalText(e.target.value); setPinModalError(''); }}
                placeholder='{ "result": "your test output here" }'
                rows={10}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '10px', fontFamily: 'monospace', fontSize: '12px',
                  background: 'var(--bg-primary)', color: 'var(--text-primary)', border: `1px solid ${pinModalError ? 'var(--error-color)' : 'var(--border-color)'}`,
                  borderRadius: '4px', resize: 'vertical', outline: 'none'
                }}
              />
              {pinModalError && <p style={{ color: 'var(--error-color)', fontSize: '12px', margin: '6px 0 0' }}>{pinModalError}</p>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px' }}>
                <button onClick={() => setShowPinModal(false)} style={{ padding: '7px 18px', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '5px', cursor: 'pointer', fontSize: '13px' }}>Cancel</button>
                <button onClick={savePinData} style={{ padding: '7px 18px', background: 'var(--accent-color)', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>Save Pin</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Icon-only button style (used in editor toolbar icon strip)
const iconBtnSt = (active = false, disabled = false) => ({
  background: disabled ? 'transparent' : (active ? 'var(--accent-color)' : 'transparent'),
  border: '1px solid var(--border-color)',
  borderRadius: '6px',
  padding: 0,
  cursor: disabled ? 'not-allowed' : 'pointer',
  color: disabled ? 'var(--text-tertiary)' : (active ? 'white' : 'var(--text-secondary)'),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '15px',
  width: '30px',
  height: '30px',
  opacity: disabled ? 0.45 : 1,
  flexShrink: 0,
  transition: 'background 0.15s',
});

// Dropdown menu item style
const menuItemSt = (disabled = false) => ({
  background: 'transparent',
  border: 'none',
  borderRadius: '6px',
  padding: '7px 12px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  color: disabled ? 'var(--text-tertiary)' : 'var(--text-primary)',
  fontSize: '13px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: '100%',
  textAlign: 'left',
  opacity: disabled ? 0.5 : 1,
});

const tabPillStyle = (active) => ({
  background: active ? 'var(--accent-soft)' : 'transparent',
  border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
  borderRadius: '999px',
  padding: '4px 12px',
  color: active ? 'var(--accent)' : 'var(--muted)',
  fontSize: '12px',
  fontWeight: active ? 600 : 500,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  transition: 'background 120ms ease, border-color 120ms ease, color 120ms ease',
});

const btnStyle = (bg, disabled = false) => {
  const isFlat = bg === 'transparent' || bg === 'var(--button-bg)';
  return {
    padding: '6px 12px',
    background: disabled ? 'var(--panel-2)' : (bg.startsWith('#') ? bg : (bg.startsWith('var') ? bg : 'transparent')),
    color: disabled ? 'var(--dim)' : (isFlat ? 'var(--text)' : '#fff'),
    border: isFlat ? '1px solid var(--border)' : '1px solid transparent',
    borderRadius: '6px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: 1,
    transition: 'background 120ms ease, border-color 120ms ease',
    opacity: disabled ? 0.6 : 1,
  };
};

export default function App() {
  return (
    <ReactFlowProvider>
      <Flow />
    </ReactFlowProvider>
  );
}
